/**
 * Typed API client — M1 skeleton + broker auth attach (Story ADMIN-W4).
 * All server data flows through TanStack Query hooks that call this client.
 * No manual fetch in components, no server data in useState.
 *
 * Auth (closes the story-1.3 TODO): every request attaches
 * `Authorization: Bearer <access token>` WHEN a broker session exists
 * (ensureFreshToken proactively refreshes near expiry).
 * A 401 WITH a session → exactly ONE forced refresh + retry; still failing →
 * clear the session + redirect to /login (intended path preserved).
 * PUBLIC endpoints (the /einladung activation calls) pass `auth: false`
 * (the portal-api precedent): NEVER a bearer, NEVER the refresh/redirect path —
 * their uniform 401 means "invalid token", not "session expired", and must not
 * log out an existing session or bounce the user off the page.
 * The portal client (src/portal/**) is untouched — it has its own token.
 *
 * Story 2.4: added tasks namespace (list / getSplitView / action mutations / bulk).
 * Story P1b: added agent namespace + agentChatStream() helper.
 */

import { runtimeConfig } from '@runtime-config';
import type {
  CustomerRetentionDto,
  AccessRequestResponse,
  AdminInvitationDto,
  AdminMembersResponse,
  AgentAttachmentResponseDto,
  AgentMessageDto,
  AgentTranscribeResponseDto,
  AssignControlBacklogItemInput,
  AttachmentView,
  AuditListResponse,
  BeziehungsnetzDto,
  BrokerBrandingDto,
  CarrierListResponseDto,
  VersichererDetailDto,
  CreateVersichererBody,
  UpdateVersichererBody,
  EnrichVersichererResponse,
  VersichererListResponse,
  VersichererClaimRowDto,
  VersichererSubmissionRowDto,
  VersichererKommRowDto,
  ChannelConfigDto,
  ChannelKey,
  ChannelMatrixEntryDto,
  ClaimPickerItemDto,
  CommReferenceDto,
  CommunicationContentDto,
  ControlBacklogItemDto,
  ControlBacklogListResponse,
  CreateCommReferenceInput,
  CreateCustomerResponseDto,
  CreateEmailBlacklistRuleInput,
  CustomerKundentyp,
  CustomerListResponseDto,
  CustomerMailTeamDto,
  CustomerStatusValue,
  DeliveryDocType,
  DocExtractionKind,
  DocumentDetailDto,
  DocumentListQuery,
  DocumentListResponse,
  DocumentVersionDto,
  DuplicateInfoDto,
  EffectiveFieldPermissionsResponse,
  EffectivePermissionsResponse,
  EffectiveVisibilityResponse,
  EmailBlacklistListResponse,
  EmailBlacklistRuleDto,
  EmailSyncCapResponse,
  EmailSyncLevel,
  EmailSyncSettingsResponse,
  FieldPermissionDto,
  FieldPermissionKeyDefinition,
  FirmNameSuggestResponse,
  HealthStatus,
  IntakeReleaseRequest,
  IntakeReleaseResponse,
  InvoiceCheckDetailDto,
  InvoiceCheckStatus,
  InvoiceListResponseDto,
  MailboxSendMessageBody,
  MailboxSendResult,
  MeProfileResponse,
  NotificationsResponse,
  PolicyDto,
  PolicyListItemDto,
  PolicyVersionDto,
  PortalConfigDto,
  PortfolioStatisticsDto,
  ProcessReminderConfigResponseDto,
  ProcessReminderTemplateDto,
  ProcessReminderTypeConfigDto,
  RenewalBoardResponseDto,
  RenewalBucket,
  RenewalHeuteDto,
  RoleFieldPermissionsResponse,
  RoleVisibilityDeniesResponse,
  SearchResponseDto,
  SetEmailSyncLevelInput,
  SetProcessReminderTemplateInput,
  SetProcessReminderTypeConfigInput,
  SplitViewResponse,
  TaskAttachmentPatch,
  TaskAttachmentView,
  TaskBulkResult,
  TaskDraftPut,
  TaskDraftView,
  TaskSummary,
  TenantSelfResponse,
  UpdatePolicyDto,
  UpdateProfileInput,
  VisibilityKeyDefinition,
  // P2 Placement Slice 1 — Kunde-Ebene Risikoprofil wire types
  RisikoprofilResponse,
  RisikoprofilPatchBody,
  BasisspartenResponse,
  BasisspartenItem,
  BasisspartePutBody,
  KundeSchadenverlaufResponse,
  KundeSchadenSparteItem,
  KundeSchadenSpartePutBody,
  // P2 Placement Slice 2 — Police-Ebene Risiko & Deckung + carrier placement
  PoliceRisikoResponse,
  PoliceRisikoPatchBody,
  DeckungResponse,
  DeckungItem,
  DeckungPutBody,
  CarrierPlacementSettingsBody,
  CarrierPlacementContactsDto,
  // P4-P6 Foundation Slice B / F1 — carrier notification addresses
  InsurerNotificationAddressesDto,
  // P2 Placement Slice 6 — Schadenverlauf wire types
  PolicySchadenverlaufResponse,
  SchadenverlaufEintrag,
  SchadenverlaufRohdatenResponse,
  // P2 Placement Slice 3 — Regelwerk admin wire types
  RegelwerkListResponse,
  RegelwerkSollDeckungItem,
  RegelwerkAttributSchemaItem,
  SollDeckungRuleCreateBody,
  SollDeckungRulePatchBody,
  AttributSchemaPatchBody,
  // P2 Placement Slice 4 — Ausschreibungs-/Placement wire types
  PlacementListResponse,
  PlacementDetail,
  PlacementCreateBody,
  PlacementStatus,
  PlacementClosureStatus,
  PlacementListView,
  // P2 Placement Slice 5 — Ausschreibungs-Screen Tab 1 / Tab 2 wire types
  PlacementRisikoprofilResponse,
  PlacementSollDeckungResponse,
  PlacementBranchIstPatchBody,
  Step2AnswerPutBody,
  // P2 Placement Slice 7 — Ausschreibungs-Screen Tab 3a Versicherer-Ranking & Auswahl wire types
  VersichererRankingResponse,
  VersichererAuswahlResponse,
  VersichererAuswahlPutBody,
  // P2 Placement Slice 8 — Ausschreibungs-Screen Tab 3b Submission + Versand + Erinnerung wire types
  SubmissionListResponse,
  SubmissionErstellenResponse,
  SubmissionVersendenResponse,
  SubmissionVersendenBody,
  SubmissionErinnernBody,
  SubmissionNichtOfferiertBody,
  SubmissionInaktivBody,
  SubmissionFristBody,
  SubmissionUnterlagenResponse,
  // P2 Placement Slice 9 — Ausschreibungs-Screen Tab 3c Rückfragen / Q&A wire types
  QnaListResponse,
  QnaGelesenResponse,
  QnaAntwortenBody,
  QnaErledigtBody,
  QnaFristBody,
  // P2 Placement Slice 10 — Ausschreibungs-Screen Tab 4a Offert-Posteingang wire types
  OfferListResponse,
  OfferUploadResponse,
  OfferZuordnenBody,
  OfferNormalisiertBody,
  OfferVerwerfenBody,
  // P2 Placement Slice 11 — Ausschreibungs-Screen Tab 4b KI-Offertvergleich wire types
  OfferComparisonResponse,
  OfferComparisonRunBody,
  OfferComparisonPatchBody,
  OfferScoreGewichteResponse,
  OfferScoreGewichtePutBody,
  // P2 Placement Slice 12 — Ausschreibungs-Screen Tab 4c Kundenfreigabe + Entscheid wire types
  KundenfreigabeResponse,
  KundenfreigabeZustellenBody,
  KundenfreigabeKanalBody,
  KundenentscheidErfassenBody,
  // P2 Placement Slice 13 — Ausschreibungs-Screen Tab 5a Antrag wire types
  AntragResponse,
  AntragVorbereitenBody,
  AntragAktualisierenBody,
  AntragSignaturBody,
  AntragFristBody,
  AntragVerwerfenBody,
  // P2 Placement Slice 14 — Underwriting / Versicherer-Rueckmeldung wire types
  UnderwritingResponse,
  UwEntscheidBody,
  UwBedingungenBody,
  UwRundeEntscheidBody,
  UwAlternativeBody,
  UwFallbackBody,
  UwDeckungBody,
  UwPoliceErhaltenBody,
  PlacementHistorieResponse,
  PlacementAbschlussResponse,
  PkPoliceVerknuepfenBody,
  PkAbweichungGeklaertBody,
  PkRueckmeldungVersichererBody,
  MahnwesenProcessConfigDto,
  PutMahnwesenProcessConfigBody,
  TextTemplateDto,
  UpsertTextTemplateBody,
  PruefberichtDto,
  PruefberichtEntscheidBody,
  VersichererBenachrichtigungDialogDto,
  VersichererBenachrichtigungMandateDto,
  SendVersichererBenachrichtigungBody,
  SendVersichererBenachrichtigungResultDto,
  MandateInsurerNotificationDto,
  SchliessenVersichererBenachrichtigungBody,
  InsurerNotificationFristConfigDto,
  SetInsurerNotificationFristConfigBody,
  CarrierDocumentsResponse,
  MandateProcessStateDto,
  CloseMandateProcessBody,
  RegistrationDto,
  RunRegistrationsResultDto,
  JetztMeldenRegistrationBody,
  ErneutMeldenRegistrationBody,
  SchliessenRegistrationBody,
  BrokerRegistrationSettingsDto,
  SetBrokerRegistrationSettingsBody,
  CarrierRegistrationSettingsBody,
  CarrierRegistrationSettingsDto,
  BestaetigenRegistrationBody,
  AblehnenRegistrationBody,
  RueckfrageRegistrationBody,
  RueckfrageBeantwortetRegistrationBody,
  KorrekturRegistrationBody,
  PolicyRegistrationAusnahmeBody,
  PolicyRegistrationDto,
  RegistrationAltbestandDto,
  RegistrationProposalDto,
  ApplyRegistrationProposalBody,
  RegistrationZuordnenDto,
  RegistrationBelegOptionsDto,
  ZuordnenRegistrationBody,
  PolicyRegistrationPreviewDto,
  VererbungRetryResultDto,
  MandateRegistrationsViewDto,
  RegistrationViewFilters,
  RegistrationDossierExportDto,
  ExportRegistrationDossierBody,
  CarrierRegistrationsDto,
} from '@brokly/shared';

import type {
  AvbDocumentResponse,
  AvbRegisterFields,
  CatalogResponseDto,
  ConfidencePolicyEntryDto,
  FunktionsgruppeDetailDto,
  FunktionsgruppeDto,
  FunktionsgruppenListResponse,
  GroupRoutingEntryDto,
  KundenteamCustomerDto,
  KundenteamDetailDto,
  KundenteamDto,
  KundenteamListResponse,
  PortalGrantAdminItem,
  RoleDto,
  SetPortalConfigBody,
  SetToleranceConfigBody,
  SuppressionEntryDto,
  TenantConfigDto,
  TestmodusBody,
  TestmodusResult,
  ToleranceConfigEntryDto,
  UserRolesDto,
} from '../features/administration/types';

import type {
  MailboxConnectionsResponse,
  MailboxConnectResponse,
  MailboxProvider,
  ManualSyncResult,
} from '../features/mailbox/types';

// P2 Placement — the firm-wide Ausschreibungen list envelope (GET /placements without a subject).
import type { PlacementFirmListResponse } from '../features/placements/types';

import type {
  KontrolleFristenDto,
  KontrolleRueckstandDetailDto,
  KontrolleRueckstandItemDto,
  KontrolleUebersichtDto,
  SetKontrolleFristenInput,
  ZuweisenInput,
} from '../features/control/types';

import { ensureFreshToken, refreshAccessToken } from '../features/auth/useBrokerAuth';
import { clearBrokerSession } from './auth/auth-session';

export type { DocumentListQuery };

export const API_BASE_URL = runtimeConfig('VITE_API_BASE_URL') ?? '/api';

/** Hard redirect to /login preserving the intended in-app path (ADMIN-W4). */
function redirectToLogin(): void {
  const intended = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/login?redirect=${encodeURIComponent(intended)}`);
}

/** Merge the bearer header into a RequestInit (no-op when token is null). */
function withAuth(init: RequestInit, token: string | null): RequestInit {
  if (!token) return init;
  return { ...init, headers: { ...init.headers, Authorization: `Bearer ${token}` } };
}

/**
 * Typed api error thrown by apiFetch on a non-2xx response. A superclass of Error, so every
 * existing `catch (e) { e.message }` / `err instanceof Error` call site is unaffected; callers
 * that need the HTTP status or the api's typed `code` (e.g. the Firmenstammdaten enrich-refresh
 * button's 429/422 handling — apps/web/src/features/entity-detail/components/FirmenStammdaten.tsx)
 * narrow with `err instanceof ApiError`.
 *
 * Mirrors the ApiErrorEnvelope shape from @brokly/shared (code, message, optional
 * retry_after_seconds — apps/api/src/common/filters/all-exceptions.filter.ts).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  /** Seconds until the client may retry, when the api sent one (429s). */
  readonly retryAfterSeconds: number | undefined;
  /** The error's own payload, when it carries one (the release 409s list what blocked them). */
  readonly data: unknown;
  /** The 409 `mandate_process_open`: the released or sent mandate that blocks a new prepare. */
  readonly openMandateId: string | undefined;
  /** The Mandatsauflösung an error names (P6: the live one on `termination_open`, the one decided on). */
  readonly terminationId: string | undefined;
  /** A stable hint code next to the error (P6: `abbruch_statt_aufloesung`). */
  readonly hint: string | undefined;

  constructor(
    message: string,
    status: number,
    code?: string,
    retryAfterSeconds?: number,
    data?: unknown,
    openMandateId?: string,
    terminationId?: string,
    hint?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
    this.data = data;
    this.openMandateId = openMandateId;
    this.terminationId = terminationId;
    this.hint = hint;
  }
}

/**
 * fetch with the broker bearer attached (when a session exists) + the ONE
 * refresh-and-retry on 401. Without a session (public routes like the
 * /einladung activation calls) this is a plain fetch — no header, no redirect.
 */
async function authedFetch(url: string, init: RequestInit): Promise<Response> {
  const token = await ensureFreshToken();
  const response = await fetch(url, withAuth(init, token));
  if (response.status !== 401 || !token) {
    return response;
  }
  // One forced refresh (single-flight) + one retry — then give up to /login.
  const retryToken = await refreshAccessToken();
  if (retryToken) {
    const retried = await fetch(url, withAuth(init, retryToken));
    if (retried.status !== 401) {
      return retried;
    }
  }
  clearBrokerSession();
  redirectToLogin();
  return response;
}

/** RequestInit + `auth: false` → plain fetch (no bearer, no 401 refresh/redirect). */
export interface ApiFetchInit extends RequestInit {
  auth?: boolean;
}

export async function apiFetch<T>(path: string, init?: ApiFetchInit): Promise<T> {
  const { auth = true, ...requestInit } = init ?? {};
  const url = `${API_BASE_URL}${path}`;
  const finalInit: RequestInit = {
    ...requestInit,
    headers: {
      'Content-Type': 'application/json',
      ...requestInit.headers,
    },
  };
  const response = auth ? await authedFetch(url, finalInit) : await fetch(url, finalInit);

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      code?: string;
      message?: string;
      retry_after_seconds?: number;
      data?: unknown;
      openMandateId?: unknown;
      terminationId?: unknown;
      hint?: unknown;
    };
    throw new ApiError(
      body.message ?? `HTTP ${response.status}`,
      response.status,
      body.code,
      body.retry_after_seconds,
      body.data,
      typeof body.openMandateId === 'string' && body.openMandateId !== '' ? body.openMandateId : undefined,
      typeof body.terminationId === 'string' && body.terminationId !== '' ? body.terminationId : undefined,
      typeof body.hint === 'string' && body.hint !== '' ? body.hint : undefined,
    );
  }

  // 204 No Content (e.g. DELETE) — no body to parse.
  if (response.status === 204) return undefined as T;
  // Nest answers a `null` return with an empty 200 (Content-Length 0), which json() cannot parse.
  if (response.headers?.get('content-length') === '0') return null as T;

  return response.json() as Promise<T>;
}

/**
 * Like apiFetch, but returns the raw response bytes as a Blob — for streamed documents
 * (e.g. a generated PDF) rather than JSON. Carries the broker bearer through authedFetch,
 * which is why the caller cannot just point an <a>/window.open at the URL: the document
 * route is auth-gated and a new tab would not send the token. On error the body is still
 * parsed as JSON so the {code,message} contract surfaces the same as apiFetch.
 */
export async function apiFetchBlob(path: string, init?: ApiFetchInit): Promise<Blob> {
  const { auth = true, ...requestInit } = init ?? {};
  const url = `${API_BASE_URL}${path}`;
  const response = auth ? await authedFetch(url, requestInit) : await fetch(url, requestInit);

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { code?: string; message?: string };
    throw new Error(body.message ?? `HTTP ${response.status}`);
  }

  return response.blob();
}

/** Paginated list response shape from GET /tasks. */
export interface TaskListResponse {
  data: TaskSummary[];
  page: {
    next_cursor: string | null;
    limit: number;
    /**
     * Story GAP-B4: total matching rows across all pages (ignoring the cursor),
     * present ONLY when the request passed `include_total=true`. Powers the
     * Aufgaben "x von y" count.
     */
    total_count?: number;
  };
}

/** Params for list/filter. */
export interface TaskListParams {
  status?: string;
  routing?: string;
  process?: string;
  assigned_to?: string;
  /**
   * Issue 27: restrict the list to the caller own work, resolved SERVER-side to
   * "assigned_to = me OR assigned_group_id in one of my active Funktionsgruppen".
   * Mutually exclusive with `assigned_to` (the api answers 400 mine_and_assigned_to).
   */
  mine?: boolean;
  comm_id?: string;
  q?: string;
  cursor?: string;
  limit?: number;
  /** Story GAP-B4: opt-in `page.total_count` (one extra COUNT(*) server-side). */
  include_total?: boolean;
}

/**
 * Body for POST /tasks — the manual "Neue Aufgabe" create. Mirrors the api
 * CreateTaskDto: `title` is required; `process`/`routing` default server-side;
 * `subject_type`+`subject_id` are coupled; the rest are optional. tenant + actor
 * are NEVER on the wire (derived from the token server-side — rule #1).
 */
export interface CreateTaskInput {
  title: string;
  process?: string;
  routing?: string;
  subject_type?: 'person' | 'firma' | 'invoice' | 'commission_line';
  subject_id?: string;
  due_at?: string;
  assigned_to?: string;
  assigned_group_id?: string;
  meta?: Record<string, unknown>;
}

/** Params for GET /customers (Story V10-J; cursor added in V10-J2). */
export interface CustomerListParams {
  status?: CustomerStatusValue;
  subject_type?: 'person' | 'firma';
  q?: string;
  limit?: number;
  cursor?: string;
  /** Restrict the list to the open unconfirmed records (the api's open rule, not the column). */
  unconfirmed_only?: boolean;
  /** Drop every record that never was a customer, open or declined. */
  customers_only?: boolean;
  /** Opt-in `page.total_count` (one extra COUNT(*) server-side). */
  include_total?: boolean;
}

/**
 * Body for POST /customers — the manual create-customer wizard
 * (natürliche vs juristische Person). Mirrors the api CreateCustomerDto:
 * `kundentyp` discriminates; person fields (first_name/last_name/…) vs firma
 * fields (name/uid/domain/…); email + phone are shared optionals.
 */
export interface CreateCustomerBody {
  kundentyp: CustomerKundentyp;
  // natürliche Person
  salutation?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  correspondence_language?: 'de' | 'fr' | 'it' | 'en';
  // juristische Person
  name?: string;
  uid?: string;
  domain?: string;
  address?: string;
  // shared optionals
  email?: string;
  phone?: string;
}

/** Params for GET /invoices (Story V10-J2). */
export interface InvoiceListParams {
  status?: InvoiceCheckStatus;
  subject_type?: 'person' | 'firma';
  subject_id?: string;
  limit?: number;
}

/**
 * Wire mirror of the api InvoiceCheckHistoryResult (GET /admin/invoice-check/:id/history,
 * Story 10.7). camelCase result type (NOT a snake_case DTO). Read-only client
 * shape (CommOverviewItem precedent) — the api module owns the source type.
 */
export interface InvoiceHistoryEntry {
  id: string;
  invoiceId: string;
  step: string;
  actor: string;
  result: string | null;
  appliedVersionId: string | null;
  appliedThreshold: unknown | null;
  createdAt: string;
}
export interface InvoiceHistoryResult {
  invoiceId: string;
  activityLog: InvoiceHistoryEntry[];
  latestCategory: string | null;
  appliedThreshold: unknown | null;
  isReproducible: boolean;
}

/** Result of POST /admin/invoice-check/:id/override (Story 10.6). */
export interface InvoiceOverrideResult {
  invoiceCheckId: string;
  newStatus: InvoiceCheckStatus;
  paymentRelease: boolean;
}

/** The K-override request body (reason ≥10 chars is enforced server-side). */
export interface InvoiceOverrideBody {
  newCategory: 'K0' | 'K1' | 'K2' | 'K3';
  reason: string;
}

/** Params for GET /policies (Story V10-J2 — subject / carrier scoping). */
export interface PolicyListParams {
  subject_id?: string;
  line_of_business?: string;
  carrier_uid?: string;
}

/** Result of POST /documents/upload (202 Accepted) — DMS M7/M11. */
export interface DocumentUploadResult {
  document_id: string;
  job_id: string;
  status: string;
  /**
   * DMS Part 2 (P2-11) — the plain-Ablage Tier-1 dedup verdict for THIS upload. `'duplicate'`
   * when the byte-identical latch dropped it (kept for audit, excluded from the productive
   * stock); `'unique'` otherwise. The Ablage upload flow surfaces `'duplicate'` as a red
   * bottom-right "Duplikat erkannt" toast. Optional so a pre-dedup api build still typechecks.
   */
  dedup_status?: 'unique' | 'duplicate';
}

/** Result of PATCH /documents/:id (manual reclassify) — DMS docs-fix. */
export interface DocumentUpdateResult {
  id: string;
  kind: string | null;
  linked_type: string;
  linked_id: string | null;
}

/** Body for PATCH /documents/:id — send only the keys that change (snake_case wire). */
export interface DocumentUpdateBody {
  /** document_kind enum value (EDITABLE_DOCUMENT_KINDS), or `null` to clear. */
  kind?: string | null;
  /** doc_link_type: 'firma' | 'person' | 'policy' | 'other'. */
  linked_type?: string;
  /** UUID of the linked object, or `null` (required unless linked_type='other'). */
  linked_id?: string | null;
}

/** Result of POST /policies/upload (202 Accepted) — Story 9.5 manual policy intake. */
export interface PolicyUploadResult {
  document_id: string;
  job_id: string;
  status: string;
}

/** Result of PATCH /documents/:id/state and one leg of the batch/state body — DMS M7/M11. */
export interface DocumentStateResult {
  id: string;
  state: 'active' | 'inactive';
}

/** Result of POST /documents/batch/delete — admin-only, skips still-active ids — DMS M7/M11. */
export interface DocumentBatchDeleteResult {
  deleted: string[];
  skipped_active: string[];
  /** P6: rows of an inaktiv (write-protected) customer, not deleted. */
  refused: DocumentBatchRefusedRow[];
}

/** P6: a batch row the write protection of an inaktiv customer refused. */
export interface DocumentBatchRefusedRow {
  id: string;
  code: 'customer_read_only';
}

/** Result of POST /documents/batch/state — DMS M7/M11. */
export interface DocumentBatchStateResult {
  updated: string[];
  skipped: string[];
  /** P6: rows of an inaktiv (write-protected) customer, not changed. */
  refused: DocumentBatchRefusedRow[];
}

/**
 * Wire mirror of the api CommOverviewItemDto (GET /communications, Story 7.2).
 * Defined here (TaskListResponse precedent) — the api module owns the DTO class;
 * this is the read-only client-side shape.
 */
export interface CommOverviewItem {
  id: string;
  channel: string;
  direction: string;
  subject: string | null;
  from: string | null;
  to: string | null;
  cc: string | null;
  received_at: string;
  /**
   * Bereich 1 sync level captured at ingest (1/2/3), or null for pre-V3.0 rows.
   * At 3 the row carries NO subject → the list renders an anonymized
   * "E-Mail zwischen … am …" entry (composed client-side from participants + date).
   */
  sync_level_captured: EmailSyncLevel | null;
  task_status: string | null;
  task_routing: string | null;
  vorgang_status: string | null;
  /** Representative linked client (comm_reference → person/firma) + display name; null when unlinked. */
  subject_type: string | null;
  subject_id: string | null;
  kunde_name: string | null;
  your_effective_tier: 'full' | 'title' | 'metadata';
  /** Berechtigungskonzept V3.0 §2.2 (Bereich 1) — is this comm currently marked private? */
  is_private: boolean;
  /** Bereich 1 — may the CALLING viewer mark/unmark this comm private? */
  can_mark_private: boolean;
  /**
   * MV (mail-only team visibility): the NAME of the team this mail is restricted to,
   * or several names joined with ', ' when it is linked to more than one restricted
   * client. null = no restriction. Set on every row the viewer may see, the mailbox
   * owner included, so the marker is honest for everyone.
   */
  restricted_to_team: string | null;
}

/** The three GET /communications buckets (Story 7.2 wire values). */
export type CommOverviewBucket = 'eingang' | 'ausgang_entwuerfe' | 'gesendet';

/**
 * Wire mirror of the api VorgangBoardRow (GET /vorgaenge/board, Story 6.4).
 * Read-only client shape — the api owns the DTO. Dates arrive as ISO strings.
 */
export interface VorgangBoardRowWire {
  vorgangId: string;
  docKind: string | null;
  subjectType: 'person' | 'firma';
  subjectId: string;
  mandateId: string | null;
  /** versandt | erinnert | ueberfaellig | eingegangen | abgeschlossen | abgebrochen. */
  status: string;
  sentAt: string;
  deliveryChannel: string | null;
  mahnStage: number;
  nextDeadlineAt: string | null;
  reminderCount: number;
  closedAt: string | null;
  inVerarbeitung: boolean;
  customerStatus: string | null;
  /** API-computed: base status + " (in Verarbeitung)" add-on. */
  displayStatus: string;
  /** API-computed: "Frist: <date>" / "Stufe X · Frist: …" / "überfällig" / "—". */
  mahnstandLabel: string;
  readAt: string | null;
  confirmedAt: string | null;
  signedDocumentId: string | null;
  policyNo: string | null;
  claimNo: string | null;
  /** P4-S2: the Mandatskachel source + API-computed state/label (Kap. 10.5). */
  mandateKind: 'analyse' | 'broker' | null;
  dispatchedByName: string | null;
  tileState: 'nicht_erteilt' | 'in_verarbeitung' | 'unterzeichnet' | 'entfaellt';
  tileLabel: string;
}

/** Paginated envelope of GET /vorgaenge/board. */
export interface VorgangBoardListResponse {
  items: VorgangBoardRowWire[];
  total: number;
}

/** Paginated envelope of GET /communications. */
export interface CommOverviewListResponse {
  data: CommOverviewItem[];
  page: {
    next_cursor: string | null;
    limit: number;
  };
}

/**
 * Wire mirror of the api CommunicationDismissDto (migration 0102).
 * Response of POST /communications/:commId/dismiss and /undismiss.
 */
export interface CommunicationDismissResult {
  id: string;
  dismissed: boolean;
  dismissed_at: string | null;
}

/** Body for POST /communications/:commId/dismiss — audit context only, no schema impact. */
export interface DismissCommunicationBody {
  reason?: 'ablehnen' | 'archivieren';
}

/**
 * Wire mirror of the api CommunicationPrivateDto (Berechtigungskonzept V3.0 §2.2).
 * Response of POST /communications/:commId/mark-private and /unmark-private.
 */
export interface CommunicationPrivateResult {
  id: string;
  is_private: boolean;
  marked_private_by: string | null;
  marked_private_at: string | null;
  unmarked_by: string | null;
  unmarked_at: string | null;
}

/**
 * Wire mirror of the api CommThreadPrivateDto (Berechtigungskonzept V3.0 P4).
 * Response of POST /communications/:commId/mark-thread-private and /unmark-thread-private.
 */
export interface CommThreadPrivateResult {
  thread_id: string;
  is_private: boolean;
  affected_count: number;
  marked_by: string | null;
  marked_at: string | null;
  unmarked_by: string | null;
  unmarked_at: string | null;
}

/**
 * Body for POST /communications/vn-messages — a broker→VN free-text message
 * (Wave W5 broker→VN messaging). Mirrors the api SendBrokerMessageDto: the
 * message is delivered via the VN's RESOLVED channel (portal thread + timely
 * notification, OR a broker-branded email for an email-only VN).
 */
export interface SendVnMessageBody {
  subjectType: 'person' | 'firma';
  subjectId: string;
  /** Optional Betreff — omitted/null renders a default subject downstream. */
  subject?: string | null;
  body: string;
  /** Per-message "Vertraulich" flag → the notification email is subject-only (no teaser). */
  confidential?: boolean;
}

/** 201 response of POST /communications/vn-messages — the created outbound message. */
export interface SendVnMessageResult {
  communication_id: string;
  /** 'portal' → landed in the portal thread + notified; 'email' → sent as a branded email. */
  delivery: 'portal' | 'email';
}

/** Params for GET /communications (Story V10-J2 wiring). */
export interface CommOverviewParams {
  bucket: CommOverviewBucket;
  subject_type?: 'person' | 'firma';
  subject_id?: string;
  cursor?: string;
  limit?: number;
}

// ── Freigabe / access-workflow wire shapes (Story 7.3 + PART C) ──────────────
// Read-only client mirrors of the api AccessRequestDto / ManagedAccessGrantDto
// (CommOverviewItem precedent — the api module owns the DTO classes). tenant_id is
// NEVER on the wire; review_note is intentionally absent.

/** One comm_release_request (access request + decision result). */
export interface AccessRequestItem {
  id: string;
  comm_id: string;
  requested_by: string;
  reviewed_by: string | null;
  /** release_status: 'pending' | 'approved' | 'rejected'. */
  status: string;
  reviewed_at: string | null;
  created_at: string;
  /**
   * Decider context (issue 33). Populated ONLY by the decider list
   * (GET /communications/access-requests?role=decider), where the caller is a full-tier
   * participant of the comm by construction; null on every requester-facing surface.
   */
  subject?: string | null;
  requester_name?: string | null;
  /** The Pflichtfeld Begründung the requester gave. Decider list only. */
  reason?: string | null;
  /** 'sender' | 'recipient', which internal party the request targets. */
  target_role?: string | null;
  /** The comm's sync level when it was captured (1 | 2 | 3). */
  sync_level_at_request?: number | null;
  /** Who decided (name, else e-mail); participant view only. */
  reviewer_name?: string | null;
}

/**
 * One standing comm_access_grant (Managed-Access).
 *
 * Email sharing v2 (2026-08-10): exactly ONE target is set — either `user_id`
 * (a colleague) OR `funktionsgruppe_id` (a group; every member inherits the grant,
 * with `funktionsgruppe_name` + `funktionsgruppe_member_count` for display). A
 * domain-scoped grant carries `domain` (all mail from that sender domain).
 */
export interface ManagedAccessGrantItem {
  id: string;
  user_id: string | null;
  funktionsgruppe_id: string | null;
  funktionsgruppe_name: string | null;
  funktionsgruppe_member_count: number | null;
  subject_type: string | null;
  subject_id: string | null;
  comm_id: string | null;
  domain: string | null;
  visibility_tier: 'full' | 'title' | 'metadata';
  scope: string;
  granted_at: string;
  expires_at: string | null;
}

/** Body for POST /communications/:commId/access-requests. */
export interface IssueAccessRequestBody {
  reason?: string;
  /** Bereich 1 (3.3): the internal party the request is addressed to (picker if both exist). */
  target_role?: 'sender' | 'recipient';
}

/** Body for POST /communications/access-requests/:requestId/decision. */
export interface DecideAccessRequestBody {
  decision: 'grant' | 'deny';
  /** Optional on grant; MANDATORY (≥20 chars) on a decline — enforced server-side. */
  review_note?: string;
  /** Bereich 1 (3.3): the grant duration on a GRANT decision (default 'permanent'). */
  duration?: 'permanent' | '1_day' | '7_days' | '30_days' | 'until_date';
  /** Required when duration = 'until_date' (ISO 8601). */
  until_date?: string;
}

/**
 * Body for POST /communications/access-grants (Managed-Access).
 *
 * Email sharing v2: TARGET = exactly one of { user_id | funktionsgruppe_id };
 * SCOPE = exactly one of { comm_id | subject_type+subject_id | domain | (none →
 * workspace) }. The XOR + the sharer's per-tier cap are enforced server-side.
 */
export interface GrantManagedAccessBody {
  user_id?: string;
  funktionsgruppe_id?: string;
  subject_type?: 'person' | 'firma';
  subject_id?: string;
  comm_id?: string;
  domain?: string;
  visibility_tier?: 'full' | 'title' | 'metadata';
  expires_at?: string;
}

/** Params for GET /communications/access-requests (decider inbox). */
export interface ListAccessRequestsParams {
  status?: 'pending' | 'approved' | 'rejected';
  role?: 'decider';
}

/** Params for GET /communications/access-grants (standing grants for a subject + own). */
export interface ListAccessGrantsParams {
  subject_type?: 'person' | 'firma';
  subject_id?: string;
  user_id?: string;
  /** Email sharing v2: list domain-scoped grants for this sender domain. */
  domain?: string;
}

/**
 * Wire mirror of the api RelationshipFeedItem (GET /persons|firms/:id/relationships,
 * Epic 5 feed). Read-only client-side shape (CommOverviewItem precedent).
 */
export interface RelationshipFeedItem {
  id: string;
  relType: 'person_firma' | 'person_person' | 'firma_firma';
  role: string | null;
  description: string | null;
  source: string | null;
  counterpart: {
    type: 'person' | 'firma';
    id: string;
    displayName: string | null;
  };
}

/**
 * Wire mirrors of the api AVB link/auto-match DTOs (GET /policies/:id/avb-links,
 * POST …/auto-match — Wave global-avb). Read-only client shapes (the api module owns
 * the source types; the CommOverviewItem precedent). NEVER carries storage secrets.
 * [Source: apps/api/src/modules/avb/avb.constants.ts]
 */
export interface AvbMatchReason {
  carrier_uid: string;
  line_of_business: string | null;
  effective_date: string;
  avb_valid_from: string;
  avb_valid_to: string | null;
}
export interface AvbLinkItem {
  id: string;
  policy_id: string;
  policy_version_id: string;
  avb_document_id: string;
  linked_at: string;
  unlinked_at: string | null;
  title: string;
  version_label: string;
  carrier_name: string | null;
  link_source: string;
  match_reason: AvbMatchReason | null;
}
export interface AvbMatchCandidate {
  avb_document_id: string;
  title: string;
  version_label: string;
  carrier_name: string | null;
  valid_from: string;
  valid_to: string | null;
  reason: AvbMatchReason;
  outcome: 'created' | 'already_linked';
  link_id: string | null;
}
export interface AvbAutoMatchResult {
  policy_id: string;
  policy_version_id: string;
  dry_run: boolean;
  created_count: number;
  matches: AvbMatchCandidate[];
}

/**
 * PUT /customer-status/:subjectType/:subjectId response (Story 6.1 AC3, wired
 * for E1). camelCase on purpose: it mirrors the api's CustomerStatusResponseDto
 * verbatim, which is NOT the snake_case shape the Kunden grid wire uses.
 */
export interface CustomerStatusResponse {
  status: CustomerStatusValue;
  inVerarbeitung: boolean;
  targetStatus: CustomerStatusValue | null;
  endDate: string | null;
  customerNumber: string; // F5: always present (assigned at creation)
  displayStatus: string;
  /** The moment the nightly run made the customer inaktiv (ISO); null otherwise or on a legacy row. */
  inactivatedAt?: string | null;
  /** An inaktiv customer is write-protected: the screen offers no write control. */
  readOnly?: boolean;
  /** P6-S7: the retention, the Legal Hold and the Löschvorschlag (the header chips). */
  retention?: CustomerRetentionDto;
}

/**
 * GET /firms/:id (H2). Every field is optional because the RESTRICTED shape carries only
 * `id`, `name` and `restricted: true` - the api refuses to send the rest for a blacklisted
 * firm to a caller without the capability.
 */
/**
 * B3: the caller's own e-mail signature as the api returns it.
 *
 * `rendered` is the org-wide template already applied. The client inserts THAT string and
 * never re-implements the template, which is what stops two employees' signatures drifting.
 */
/** F1: one Zeichnungsberechtigte Person. The image itself is never inlined in a list. */
export interface BrokerSignatoryWire {
  id: string;
  firstName: string;
  lastName: string;
  roleLabel: string | null;
  /** True once a PNG has been uploaded. */
  hasSignature: boolean;
  source: string;
  isActive: boolean;
}

export interface EmailSignatureWire {
  displayName: string;
  jobTitle: string | null;
  phone: string | null;
  bodyText: string | null;
  /** The sanitised rich body as stored (null for a plain-text-only signature). */
  bodyHtml: string | null;
  /** The org-wide template applied, PLAIN text (text/plain fallback + legacy insert). */
  rendered: string;
  /** The org-wide template applied, HTML — what the composer injects into the mail body. */
  renderedHtml: string;
}

export interface FirmDetailWire {
  id: string;
  name: string;
  legal_form?: string | null;
  uid?: string | null;
  zefix_status?: string;
  address?: string | null;
  postcode?: string | null;
  city?: string | null;
  canton?: string | null;
  phone?: string | null;
  // ── P2-8 (spec-part2-stefan-delta.md §3.2) customer Stammdaten expansion ─────────────────
  // Mirrors FirmDetailDto (packages/shared/src/firms.ts) — kept as a local optional mirror
  // (not imported from @brokly/shared) for the same reason the block above is: mocks in
  // tests only ever populate the subset a given test cares about. A/M markers per field
  // match the shared DTO's doc comments 1:1.
  /** A — promoted RegisterEnrichmentResult.noga. `null` when the register returned none. */
  noga_code?: string | null;
  /** A — resolveBranche(noga_code). `null` when noga_code is null/unresolvable. */
  branche?: string | null;
  /** M — broker-entered Jahresumsatz. Money string (repo-wide convention). */
  annual_revenue?: string | null;
  /** A — EXTRACT(YEAR FROM founding_date). `null` when unknown. */
  founding_year?: number | null;
  /** M (defaulted from customer_state) — Kunde seit, ISO 8601 date string. */
  customer_since?: string | null;
  /** A — promoted company_master.purpose (Zweck). */
  business_activity?: string | null;
  /** M — Referenzwährung, ISO 4217. NOT NULL DEFAULT 'CHF' at the DB — always present. */
  reference_currency?: string;
  /** A — derived "{uid} MWST" when mwst_pflichtig. */
  vat_number?: string | null;
  /** A — from mwstValidated && mwstStatus==='registered'. `null` when undetermined. */
  mwst_pflichtig?: boolean | null;
  /** M — Vorsteuerabzugsberechtigt, independently broker-set. */
  input_tax_deductible?: boolean | null;
  /** M — Adresszusatz (c/o, Stockwerk, etc.). */
  address_supplement?: string | null;
  restricted?: boolean;
}

/**
 * Response body for POST /firms/:id/enrich (Story 4.3 AC3; the Firmenstammdaten refresh
 * button). Local mirror of EnrichFirmResponse (packages/shared/src/firms.ts) — same
 * mirror-not-import reason as FirmDetailWire above. Always `enrichment_status: 'pending'` at
 * enqueue time — the caller polls firms.get() for the real terminal status.
 */
export interface FirmEnrichResponseWire {
  task: null;
  job_id: string;
  enrichment_status: 'pending';
}

/**
 * Body for PUT /firms/:id (D1-14 manual edit). Every field optional: send only what the broker
 * touched. Snake_case to match FirmDetailWire, so a read row can be edited and sent back.
 */
export interface FirmMasterDataPatch {
  name?: string;
  legal_form?: string | null;
  uid?: string | null;
  address?: string | null;
  postcode?: string | null;
  city?: string | null;
  canton?: string | null;
  phone?: string | null;
  // P2-8 additions — mirrors UpdateFirmDto (apps/api/src/modules/firms/dto/update-firm.dto.ts).
  noga_code?: string | null;
  branche?: string | null;
  founding_year?: number | null;
  business_activity?: string | null;
  vat_number?: string | null;
  mwst_pflichtig?: boolean | null;
  annual_revenue?: string | null;
  customer_since?: string | null;
  reference_currency?: string;
  input_tax_deductible?: boolean | null;
  address_supplement?: string | null;
}

/**
 * One row of GET /firms/:id/domains (D2 multi-domain).
 *
 * Local mirror of the api-side FirmDomainItem, same reason as CustomerStatusResponse
 * above: the type is module-local in apps/api and not shipped through @brokly/shared.
 * `status` is the email_domain enum: 'unknown' | 'verified' | 'rejected'. A 'rejected'
 * row is soft-removed, so it routes no mail but still holds the domain.
 */
export interface FirmDomainItem {
  id: string;
  domain: string;
  status: string;
  createdAt: string;
}

/** GET /firms/:id/domains response. */
export interface FirmDomainsListResponse {
  firmaId: string;
  domains: FirmDomainItem[];
}

/** POST / DELETE /firms/:id/domains response. */
export interface FirmDomainMutationResponse {
  firmaId: string;
  domain: string;
  status: string;
  outcome: string;
}

// ── G: Hauptansprechperson + company employees (wave 4) ──────────────────────

/** One employee of a client company. */
export interface CompanyEmployee {
  personId: string;
  displayName: string;
  email: string | null;
  role: string | null;
  isMainContact: boolean;
  /**
   * P2-8 (spec §3.2/§3.4): the generated Briefanrede (person.briefanrede) — "for a firma
   * customer, the Briefanrede uses the Hauptansprechperson". `null` until the first auto-fill.
   */
  briefanrede: string | null;
}

/** GET /firms/:id/main-contact */
export interface MainContactResponse {
  firmaId: string;
  employees: CompanyEmployee[];
  mainContactPersonId: string | null;
  /**
   * HOW the contact was decided, so the UI can be honest instead of implying a choice:
   * 'explicit' a human chose, 'default_first' nobody has chosen so it is the first person
   * added, 'none' the company has no people.
   */
  source: 'explicit' | 'default_first' | 'none';
}

// ── Zeichnungsberechtigte (customer-firm register signatories) ───────────────
//
// GET /firms/:id/signatories. Local optional mirror of FirmSignatoriesDto (packages/shared),
// not imported — same mirror-not-import reason as FirmDetailWire (test mocks populate only the
// subset a given test cares about). DISTINCT from the BROKER signatories namespace below
// (apiClient.signatories → /admin/signatories): do NOT reuse that name.

export interface FirmSignatoryWire {
  last_name?: string;
  first_name?: string;
  display_name?: string;
  role?: string | null;
  signature?: string;
  added_in?: string | null;
}

export interface FirmSignatorySetSummaryWire {
  source?: 'zefix_replay' | 'excerpt_pdf';
  count?: number;
  fetched_at?: string | null;
  document_id?: string | null;
  incomplete_history?: boolean;
}

export interface FirmSignatoriesWire {
  firma_id?: string;
  uid?: string | null;
  zefix_status?: string;
  effective_source?: 'zefix_replay' | 'excerpt_pdf' | null;
  signatories?: FirmSignatoryWire[];
  incomplete_history?: boolean;
  fetched_at?: string | null;
  document_id?: string | null;
  sets?: FirmSignatorySetSummaryWire[];
  discrepancy?: {
    differs?: boolean;
    only_in_excerpt?: FirmSignatoryWire[];
    only_in_replay?: FirmSignatoryWire[];
  } | null;
}

// ── E6 mandate wizard (wave 2) ───────────────────────────────────────────────

import type {
  MandateSignerWire,
  MandateSignerProposalWire,
  MandateReleaseInput,
  MandateReleaseResultWire,
  MandateBadgeWire,
  MandateBadgesWire,
  MandatePendingDeliveryWire,
  MandateDeliveryPreviewWire,
  RegisterCheckDto,
  RegisterCheckStateDto,
  RecordRegisterCheckBody,
  ClearRegisterBefundBody,
  CustomerRegisterBefundDto,
  MandateScopeItemDto,
  ScopeProposalDto,
  ScopeDto,
  SetScopeBody,
  PortalStatusDto,
  PortalInvitationPlanDto,
  TerminationDto,
  UebergabeChecklistDto,
  TerminationImpactDto,
  TerminationLetterPreviewDto,
  TerminationLetterListItemDto,
  MandateTerminationConfigDto,
  TerminationArt,
  TerminationReasonCode,
  EndDateSource,
  TerminationProposalDto,
  TerminationOverrideReasonCode,
} from '@brokly/shared';

/** POST /tasks/:taskId/termination-proposal/apply body: the staff confirms or corrects the proposal. */
export interface ApplyTerminationProposalBody {
  mandateKind?: 'analyse' | 'broker';
  art: TerminationArt;
  requestedEndDate?: string | null;
}

/** POST /mandates/terminations/:id/freigeben body (and the `approve` of the one-request open). */
export interface ApproveTerminationBody {
  endDate: string;
  endDateSource: Exclude<EndDateSource, 'rueckdatiert'>;
  backdate?: boolean;
}

/** POST /mandates/terminations body: the manual wizard «Mandat auflösen» (open, with `approve` in one request). */
export interface OpenTerminationBody {
  subjectType: 'person' | 'firma';
  subjectId: string;
  mandateKind?: 'analyse' | 'broker';
  art: TerminationArt;
  requestedEndDate?: string;
  /** The Eingangsdatum (default today, never in the future): the reference of the Fristregel and the Rückdatierung. */
  receivedOn?: string;
  reasonCode: TerminationReasonCode;
  remark: string;
  evidenceDocumentId?: string;
  approve?: ApproveTerminationBody;
}

/** PUT /admin/einstellungen/mandatsaufloesung body. */
export type SetMandateTerminationConfigBody = Partial<Omit<MandateTerminationConfigDto, 'istDefault'>>;

export type {
  MandateSignerWire,
  MandateSignerProposalWire,
  MandateReleaseInput,
  MandateReleaseResultWire,
  MandateBadgeWire,
  MandateBadgesWire,
  MandatePendingDeliveryWire,
  MandateDeliveryPreviewWire,
};

/** POST /mandates/prepare body. `anrede` picks the Sie- or Du-form template (E7). */
export interface MandatePrepareRequest {
  subjectType: 'person' | 'firma';
  subjectId: string;
  targetStatus: string;
  anrede?: 'sie' | 'du';
  /** P4-S1 step 3: the broker-confirmed signers. Omitted -> the server default. */
  signers?: MandateSignerWire[];
}

/** The reviewable draft: a real document and a composed email, nothing committed. */
export interface MandatePrepareResponse {
  mandateId: string;
  mandateKind: string;
  mandateStatus: string;
  /** null while a Brokermandat waits for its render (register check and scope come first). */
  documentId: string | null;
  filename: string;
  mimeType: string;
  barcodeToken: string;
  deliveryChannel: string;
  targetStatus: string;
  /** Brokermandat: true until POST /mandates/:id/render produced the document. */
  renderPending?: boolean;
  registerCheck?: RegisterCheckDto | null;
  scope?: MandateScopeItemDto[];
  escalationTarget?: string | null;
  /** what the first contact does for the customer portal (Brokermandat; null otherwise). */
  invitation?: PortalInvitationPlanDto | null;
  // P4-S1 Mandatsvorgang review fields:
  processNumber: string;
  statusBefore: string;
  customerNumber: string | null;
  parameters: {
    segment: 'KMU' | 'PRIV';
    anrede: 'sie' | 'du';
    lang: 'de';
    channel: string;
    channelSource: string;
  };
  signers: MandateSignerWire[];
  release: {
    routing: 'automated' | 'freigabe' | 'manual';
    confidence: number;
  };
  email: {
    to: string[];
    subject: string;
    body: string;
    templateKey: string;
    templateRef: string;
    handlungsbausteinRef: string | null;
    /**
     * Where the address came from: 'main_contact' the Hauptansprechperson, 'primary_email' the
     * firm's own stored address, 'none' nothing on file (the UI must say so, not invent one).
     */
    recipientSource: 'main_contact' | 'primary_email' | 'none';
  };
}

/** POST /mandates/:id/send body: the reviewed, possibly inline-edited email. */
export interface MandateSendRequest {
  to: string[];
  subject: string;
  body: string;
  includeAttachment: boolean;
  attachmentDocumentId?: string;
}

/** What the customer looks like once the mandate is out. */
export interface MandateDispatchResult {
  mandateId: string;
  mandateKind: string;
  mandateStatus: string;
  documentId: string;
  vorgangId: string;
  deliveryChannel: string;
  customerStatus: string;
  inVerarbeitung: boolean;
  targetStatus: string;
  displayStatus: string;
  barcodeToken: string;
}

/**
 * A customer's PRIMARY portal holder (GET /admin/customer-portal/access/:type/:id).
 * The 1-click invite invites this holder; portal grants (PortalGrantAdminItem) are the
 * additional people the customer invited themselves.
 */
export interface PortalAccessAdminItem {
  id: string;
  subject_type: string;
  subject_id: string;
  /** 'not_invited' | 'invited' | 'active'. */
  status: string;
  /** The ONE broker-entered invitation address; null when none was stored. */
  invite_email: string | null;
  invited_at: string | null;
  activated_at: string | null;
}

/** `access` is null when the customer has no portal_access row yet. */
export interface PortalAccessResponse {
  access: PortalAccessAdminItem | null;
}

export const apiClient = {
  health: {
    get: (): Promise<HealthStatus> => apiFetch<HealthStatus>('/health'),
  },

  /**
   * Firm email domains (D2). A firm matches inbound mail on ALL of its domains, and
   * before D2 a broker could neither see nor remove one.
   */
  /**
   * H2: the company's master data for the Kunde Übersicht.
   *
   * The api answers a blacklisted firm with `{ id, name, restricted: true }` and nothing
   * else (D-NAME-ONLY-200-NOT-403), so the caller must handle `restricted` rather than
   * assume the fields are simply empty.
   */
  firms: {
    /** GET /firms/:id - full detail, or the name-only projection when restricted. */
    get: (firmaId: string): Promise<FirmDetailWire> =>
      apiFetch<FirmDetailWire>(`/firms/${firmaId}`),

    /**
     * PUT /firms/:id - manual Firmenstammdaten edit (D1-14; crm.write).
     *
     * Send any subset of the editable fields. A field whose value differs from what is stored
     * is marked as broker-overridden on the server, so the next Zefix enrichment will NOT
     * overwrite it. Returns the refreshed full detail. 409 if the UID is already used by
     * another firm, 403 if the firm is blacklisted and the caller may only see the name.
     */
    update: (firmaId: string, patch: FirmMasterDataPatch): Promise<FirmDetailWire> =>
      apiFetch<FirmDetailWire>(`/firms/${firmaId}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      }),

    /**
     * POST /firms/:id/enrich - manual register re-fetch trigger (the Firmenstammdaten card's
     * refresh/sync button). Async: 202 just enqueues the job; the caller polls firms.get() for
     * the zefix_status transition to 'done' / 'failed'. Rate-limited server-side
     * (apps/api/src/modules/firms/firm-enrich.rate-limiter.ts) — a 429 (per-firm 30s cooldown,
     * per-user 20/min cap, or "already running/pending") and a 422 (blacklisted firm) both
     * surface as ApiError; read err.status / err.code / err.retryAfterSeconds.
     */
    enrich: (firmaId: string): Promise<FirmEnrichResponseWire> =>
      apiFetch<FirmEnrichResponseWire>(`/firms/${firmaId}/enrich`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'manual' }),
      }),
  },

  /**
   * Register lookups that are not tied to a stored firm.
   */
  register: {
    /**
     * GET /register/firm-names?stem= — Firmenname autocomplete. `stem` is the first 3+ chars
     * typed; the browser ranks the rest locally (useFirmNameSuggestions). Pass TanStack's
     * signal so a superseded stem aborts. 503 when the register is unreachable.
     */
    suggestFirmNames: (stem: string, signal?: AbortSignal): Promise<FirmNameSuggestResponse> =>
      apiFetch<FirmNameSuggestResponse>(`/register/firm-names?stem=${encodeURIComponent(stem)}`, { signal }),
  },

  /**
   * F1: Zeichnungsberechtigte Personen (wave 3).
   *
   * A ZB person is NOT a user and may sign without any Brokly account - that is the point of
   * the feature, so there is no app_user link anywhere in this surface.
   *
   * Reads use `signing.read` (an ORIGINAL baseline key, so every seeded role holds it and the
   * selection popup cannot ship dead); writes use `tenant.manage`. There is no delete: a
   * signature already placed into an issued contract has to stay explicable, so removal is
   * `update({ isActive: false })`.
   */
  signatories: {
    /** GET /admin/signatories - inactive rows are returned too, flagged. */
    list: (includeInactive = false): Promise<BrokerSignatoryWire[]> =>
      apiFetch<BrokerSignatoryWire[]>(
        `/admin/signatories${includeInactive ? '?includeInactive=true' : ''}`,
      ),

    create: (input: {
      firstName: string;
      lastName: string;
      roleLabel?: string;
    }): Promise<BrokerSignatoryWire> =>
      apiFetch<BrokerSignatoryWire>(`/admin/signatories`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    update: (
      id: string,
      patch: {
        firstName?: string;
        lastName?: string;
        roleLabel?: string | null;
        isActive?: boolean;
      },
    ): Promise<BrokerSignatoryWire> =>
      apiFetch<BrokerSignatoryWire>(`/admin/signatories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    /** POST /admin/signatories/:id/signature - multipart PNG upload. */
    uploadSignature: async (
      id: string,
      file: File | Blob,
      filename?: string,
    ): Promise<BrokerSignatoryWire> => {
      const form = new FormData();
      form.append('file', file, filename ?? 'signature.png');
      // NOTE: no Content-Type header - the browser sets the multipart boundary.
      const res = await authedFetch(`${API_BASE_URL}/admin/signatories/${id}/signature`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `HTTP ${res.status}`);
      }
      return res.json() as Promise<BrokerSignatoryWire>;
    },

    /**
     * The URL the img tag points at (wave 13). Not fetched here: the browser does the GET with
     * the session, and the api answers 404 for a missing signature, an unknown id, a foreign
     * tenant, or a checksum mismatch - all indistinguishable, so nothing about existence leaks.
     */
    signatureUrl: (id: string): string => `${API_BASE_URL}/admin/signatories/${id}/signature`,
  },

  /**
   * B3: the caller's OWN e-mail signature.
   *
   * Not permission-gated on the api, deliberately: there is no id on the wire and the row is
   * resolved from the token, so the only signature a caller can reach is their own. `rendered`
   * is the exact string the composer inserts, so the client never re-implements the org-wide
   * template and two employees' signatures cannot drift apart.
   */
  emailSignature: {
    /** GET /me/email-signature - `{ signature: null }` when none has been defined yet. */
    get: (): Promise<{ signature: EmailSignatureWire | null }> =>
      apiFetch<{ signature: EmailSignatureWire | null }>(`/me/email-signature`),

    /** PUT /me/email-signature - upsert; returns the rendered forms to insert. */
    put: (input: {
      displayName: string;
      jobTitle?: string | null;
      phone?: string | null;
      bodyText?: string | null;
      /** Rich body (bold/italic/links/inline logo). Sanitised server-side before storage. */
      bodyHtml?: string | null;
    }): Promise<EmailSignatureWire> =>
      apiFetch<EmailSignatureWire>(`/me/email-signature`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
  },

  /**
   * Parts C + E: the caller's OWN settings profile + the request-higher-access action.
   * The row is the caller's (resolved from the token); the response carries the caller's
   * own app_user `id` so any authenticated user can resolve "me" (e.g. the Aufgaben
   * "Meine" filter) without the admin-gated GET /admin/members list.
   */
  me: {
    /** GET /me/profile — id + name/email/job_title/avatar + role names + is_read_only. */
    profile: (): Promise<MeProfileResponse> => apiFetch<MeProfileResponse>('/me/profile'),
    /** PATCH /me/profile — partial update of the caller's display name / job title. */
    updateProfile: (input: UpdateProfileInput): Promise<MeProfileResponse> =>
      apiFetch<MeProfileResponse>('/me/profile', {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    /**
     * POST /me/avatar — multipart image upload (field 'file'), replacing the caller's
     * avatar. Mirrors signatories.uploadSignature: FormData + authedFetch directly
     * (no Content-Type header — the browser sets the multipart boundary). The caller
     * is expected to have already downscaled the image client-side (see
     * features/settings/avatar-image.ts#resizeAvatarToSquareJpeg).
     */
    uploadAvatar: async (file: Blob, filename = 'avatar.jpg'): Promise<MeProfileResponse> => {
      const form = new FormData();
      form.append('file', file, filename);
      const res = await authedFetch(`${API_BASE_URL}/me/avatar`, { method: 'POST', body: form });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
        throw new ApiError(body.message ?? `HTTP ${res.status}`, res.status, body.code);
      }
      return res.json() as Promise<MeProfileResponse>;
    },
    /** POST /me/access-request — a read-only user requests higher access (notifies admins). */
    requestAccess: (): Promise<AccessRequestResponse> =>
      apiFetch<AccessRequestResponse>('/me/access-request', { method: 'POST' }),
  },

  /**
   * Berechtigungskonzept V3.0 · Bereich 1 — the caller's OWN per-mailbox e-mail sync
   * levels (personal; JWT-guarded). The tenant-admin cap lives under `admin` below.
   */
  emailSync: {
    /** GET /email-sync/settings — per-mailbox levels + the tenant cap. */
    settings: (): Promise<EmailSyncSettingsResponse> =>
      apiFetch<EmailSyncSettingsResponse>('/email-sync/settings'),
    /** PUT /email-sync/settings — set a per-account (or default) level (capped → 400). */
    setLevel: (body: SetEmailSyncLevelInput): Promise<EmailSyncSettingsResponse> =>
      apiFetch<EmailSyncSettingsResponse>('/email-sync/settings', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
  },

  /**
   * Berechtigungskonzept V3.0 · Bereich 1 (3.5) — the 3-tier blocklist. Platform rules are
   * system-managed (read-only); tenant rules are admin-managed; user rules are personal.
   */
  emailBlocklist: {
    /** GET /email-blocklist — platform + tenant + own user rules + can_manage_tenant. */
    list: (): Promise<EmailBlacklistListResponse> =>
      apiFetch<EmailBlacklistListResponse>('/email-blocklist'),
    /** POST /email-blocklist — create a tenant (admin) or user (self) rule (tighten-only). */
    create: (body: CreateEmailBlacklistRuleInput): Promise<EmailBlacklistRuleDto> =>
      apiFetch<EmailBlacklistRuleDto>('/email-blocklist', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    /** DELETE /email-blocklist/:id — remove an own user rule, or a tenant rule (admin).
     *  P2 hardening 04: reveal_past=true also reveals a tenant rule's firm-wide-hidden past mail. */
    remove: (id: string, opts: { reveal_past?: boolean } = {}): Promise<{ deleted: true }> => {
      const query = opts.reveal_past ? '?reveal_past=true' : '';
      return apiFetch<{ deleted: true }>(`/email-blocklist/${id}${query}`, { method: 'DELETE' });
    },
    /** GET /email-blocklist/preview?pattern= — "bis zu N bisherige E-Mails" (admin). */
    preview: (pattern: string): Promise<import('@brokly/shared').EmailBlocklistPreviewResponse> =>
      apiFetch<import('@brokly/shared').EmailBlocklistPreviewResponse>(
        `/email-blocklist/preview?pattern=${encodeURIComponent(pattern)}`,
      ),
  },

  /**
   * Part D: the in-app notification bell. Per-user + tenant-scoped on the api; the
   * client just lists + marks read (unread_count drives the bell badge).
   */
  notifications: {
    /** GET /notifications — newest-first list + unread count. */
    list: (): Promise<NotificationsResponse> =>
      apiFetch<NotificationsResponse>('/notifications'),
    /** POST /notifications/:id/read — mark one read. */
    markRead: (id: string): Promise<{ read: boolean }> =>
      apiFetch<{ read: boolean }>(`/notifications/${id}/read`, { method: 'POST' }),
    /** POST /notifications/read-all — mark all read. */
    markAllRead: (): Promise<{ marked: number }> =>
      apiFetch<{ marked: number }>('/notifications/read-all', { method: 'POST' }),
  },

  firmDomains: {
    /** GET /firms/:id/domains - includes soft-removed rows so a removal is undoable. */
    list: (firmaId: string): Promise<FirmDomainsListResponse> =>
      apiFetch<FirmDomainsListResponse>(`/firms/${firmaId}/domains`),

    /** POST /firms/:id/domains - 409 when the domain is active on another firm. */
    add: (firmaId: string, domain: string): Promise<FirmDomainMutationResponse> =>
      apiFetch<FirmDomainMutationResponse>(`/firms/${firmaId}/domains`, {
        method: 'POST',
        body: JSON.stringify({ domain }),
      }),

    /** DELETE /firms/:id/domains/:domain - HARD remove (the row is deleted, not deactivated). */
    remove: (firmaId: string, domain: string): Promise<FirmDomainMutationResponse> =>
      apiFetch<FirmDomainMutationResponse>(
        `/firms/${firmaId}/domains/${encodeURIComponent(domain)}`,
        { method: 'DELETE' },
      ),
  },

  /**
   * The E6 mandate wizard (wave 2).
   *
   * Three calls, in the order the popup uses them. `prepare` changes NOTHING about the
   * customer: it renders the document and composes the covering email so the broker can
   * review and edit them. The status only moves on `send`, which is why `cancel` has
   * nothing to roll back (E2) and can never clear an "in Verarbeitung" overlay.
   */
  /**
   * G2..G5: the Hauptansprechperson, the main contact on the CLIENT side.
   *
   * Read is `crm.read`; setting it is `crm.write`, i.e. "any user who can edit the customer"
   * exactly as G3 asks, with no special role.
   */
  mainContact: {
    /** GET /firms/:id/main-contact - every employee, and which one is the contact. */
    get: (firmaId: string): Promise<MainContactResponse> =>
      apiFetch<MainContactResponse>(`/firms/${firmaId}/main-contact`),

    /** PUT /firms/:id/main-contact - 404 if the person is not linked to this company. */
    set: (firmaId: string, personId: string): Promise<MainContactResponse> =>
      apiFetch<MainContactResponse>(`/firms/${firmaId}/main-contact`, {
        method: 'PUT',
        body: JSON.stringify({ personId }),
      }),

    /**
     * POST /firms/:id/contacts - create a NEW contact person and link them to the firm.
     * The manual counterpart to inbound-email genesis (the G-3 gap): the card could
     * re-designate an existing person but never add one. `email` is optional. Returns the
     * refreshed Overview data, with the new person shown as Hauptansprechperson iff it was the
     * firm's first contact (decided server-side). 409 if the email already belongs to someone.
     */
    addContact: (
      firmaId: string,
      input: { lastName: string; firstName?: string; email?: string },
    ): Promise<MainContactResponse> =>
      apiFetch<MainContactResponse>(`/firms/${firmaId}/contacts`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  },

  /**
   * Zeichnungsberechtigte — the CUSTOMER firm's register signatories (firma_signatory_set).
   * Distinct from `apiClient.signatories` (the BROKER's own mandate signers, /admin/signatories).
   */
  firmSignatories: {
    /** GET /firms/:id/signatories — the effective list + source metadata + discrepancy. */
    get: (firmaId: string): Promise<FirmSignatoriesWire> =>
      apiFetch<FirmSignatoriesWire>(`/firms/${firmaId}/signatories`),

    /**
     * POST /firms/:id/excerpt — upload a Handelsregisterauszug (PDF). The doc-extraction worker
     * parses it into the excerpt_pdf set (which wins over the replay). Multipart, field `file`;
     * 202 { document_id, job_id, status }. FormData + authedFetch, like signatories.uploadSignature.
     */
    uploadExcerpt: async (
      firmaId: string,
      file: File,
    ): Promise<{ document_id: string; job_id: string; status: string }> => {
      const form = new FormData();
      form.append('file', file, file.name);
      // No Content-Type header — the browser sets the multipart boundary.
      const res = await authedFetch(`${API_BASE_URL}/firms/${firmaId}/excerpt`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        // NIT #11: throw ApiError (not a plain Error) so callers can read .status/.code — the card
        // needs the 403 vs 4xx distinction, matching apiFetch's and uploadSignature's error path.
        const body = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
        throw new ApiError(body.message ?? `HTTP ${res.status}`, res.status, body.code);
      }
      return res.json() as Promise<{ document_id: string; job_id: string; status: string }>;
    },
  },

  mandateWizard: {
    /** POST /mandates/prepare - render the document + compose the mail. No status change. */
    prepare: (body: MandatePrepareRequest): Promise<MandatePrepareResponse> =>
      apiFetch<MandatePrepareResponse>('/mandates/prepare', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** GET /mandates/signer-proposal - P4-S1 step 3 proposal (read-only). */
    signerProposal: (subjectType: 'person' | 'firma', subjectId: string): Promise<MandateSignerProposalWire> =>
      apiFetch<MandateSignerProposalWire>(
        `/mandates/signer-proposal?subjectType=${encodeURIComponent(subjectType)}&subjectId=${encodeURIComponent(subjectId)}`,
      ),

    /** POST /mandates/:id/send - the send IS the confirmation (E2). Real mail goes out. */
    send: (mandateId: string, body: MandateSendRequest): Promise<MandateDispatchResult> =>
      apiFetch<MandateDispatchResult>(`/mandates/${mandateId}/send`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /**
     * POST /mandates/:id/release - P4-S1 step 6 Einzelfreigabe (draft -> released). A portal
     * mandate passes the edited notification, which the api stores for the later delivery.
     */
    release: (mandateId: string, body?: MandateReleaseInput): Promise<MandateReleaseResultWire> =>
      apiFetch<MandateReleaseResultWire>(`/mandates/${mandateId}/release`, {
        method: 'POST',
        ...(body ? { body: JSON.stringify(body) } : {}),
      }),

    /** GET /customers/:t/:id/portal-status (10.5) the header Portalstatus chip. */
    portalStatus: (subjectType: 'person' | 'firma', subjectId: string): Promise<PortalStatusDto> =>
      apiFetch<PortalStatusDto>(
        `/customers/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}/portal-status`,
      ),

    /** GET /customers/:t/:id/mandate-badges - P4-S1 (7.6) the header Mandatskacheln. */
    badges: (subjectType: 'person' | 'firma', subjectId: string): Promise<MandateBadgesWire> =>
      apiFetch<MandateBadgesWire>(
        `/customers/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}/mandate-badges`,
      ),

    /**
     * POST /mandates/:id/mark-posted - «gedruckt und versendet» for a released post mandate: the
     * api commits it exactly like a send (Vorgang, Mahnwesen), with no mail.
     */
    markPosted: (mandateId: string): Promise<MandateDispatchResult> =>
      apiFetch<MandateDispatchResult>(`/mandates/${mandateId}/mark-posted`, { method: 'POST' }),

    /** GET /mandates/:id/delivery - what Zustellen of a released post/portal mandate sends. */
    deliveryPreview: (mandateId: string): Promise<MandateDeliveryPreviewWire> =>
      apiFetch<MandateDeliveryPreviewWire>(`/mandates/${mandateId}/delivery`),

    /**
     * POST /mandates/:id/cancel - drop the draft and its PDF. Idempotent. On a released mandate
     * this is the «Freigabe zurückziehen».
     */
    cancel: (mandateId: string, body?: { reason: 'compliance' }): Promise<{ cancelled: boolean }> =>
      apiFetch<{ cancelled: boolean }>(`/mandates/${mandateId}/cancel`, {
        method: 'POST',
        ...(body ? { body: JSON.stringify(body) } : {}),
      }),

    /**
     * GET /mandates/:id/document/:documentId - the generated mandate PDF bytes (6c).
     *
     * Returns a Blob the caller opens in a new tab via an object URL. Fetched with the bearer
     * (the route is auth-gated), so a plain window.open on the URL would 401 - the blob detour
     * is deliberate.
     */
    documentBlob: (mandateId: string, documentId: string): Promise<Blob> =>
      apiFetchBlob(`/mandates/${mandateId}/document/${documentId}`),

    /**
     * GET /mandates/:id/portal-brief - the portal Begleitbrief of a released post Brokermandat (the
     * personal activation link and code), printed and posted with the mandate. mandate.dispatch.
     */
    portalBriefBlob: (mandateId: string): Promise<Blob> => apiFetchBlob(`/mandates/${mandateId}/portal-brief`),

  },

  // Brokermandat steps 3, 5 and 6: the register check, the Mandatsumfang, the render, and the
  // customer's Register-Befund flag with its clear (tenant.manage).
  mandateBroker: {
    /** GET /customers/:t/:id/register-befund - the red flag on the customer page. */
    customerRegisterBefund: (subjectType: 'person' | 'firma', subjectId: string): Promise<CustomerRegisterBefundDto> =>
      apiFetch<CustomerRegisterBefundDto>(
        `/customers/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}/register-befund`,
      ),
    /** POST /customers/:t/:id/register-befund/:checkId/freigeben - the lock's exit, with a written reason. */
    clearRegisterBefund: (
      subjectType: 'person' | 'firma',
      subjectId: string,
      checkId: string,
      body: ClearRegisterBefundBody,
    ): Promise<RegisterCheckDto> =>
      apiFetch<RegisterCheckDto>(
        `/customers/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}/register-befund/${encodeURIComponent(checkId)}/freigeben`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    /** GET /mandates/:id/register-check - the draft's register state. */
    registerCheck: (mandateId: string): Promise<RegisterCheckStateDto> =>
      apiFetch<RegisterCheckStateDto>(`/mandates/${mandateId}/register-check`),
    /** POST /mandates/:id/register-check - the broker's attestation. */
    recordRegisterCheck: (mandateId: string, body: RecordRegisterCheckBody): Promise<RegisterCheckDto> =>
      apiFetch<RegisterCheckDto>(`/mandates/${mandateId}/register-check`, { method: 'POST', body: JSON.stringify(body) }),
    /** GET /mandates/:id/scope-proposal - Bestand + declared insurances grouped per carrier. */
    scopeProposal: (mandateId: string): Promise<ScopeProposalDto> =>
      apiFetch<ScopeProposalDto>(`/mandates/${mandateId}/scope-proposal`),
    /** PUT /mandates/:id/scope - store the Mandatsumfang of the draft. */
    setScope: (mandateId: string, body: SetScopeBody): Promise<ScopeDto> =>
      apiFetch<ScopeDto>(`/mandates/${mandateId}/scope`, { method: 'PUT', body: JSON.stringify(body) }),
    /** POST /mandates/:id/render - the document with the Policenliste annex. */
    render: (mandateId: string, body?: { anrede: 'sie' | 'du' }): Promise<MandatePrepareResponse> =>
      apiFetch<MandatePrepareResponse>(`/mandates/${mandateId}/render`, {
        method: 'POST',
        ...(body ? { body: JSON.stringify(body) } : {}),
      }),
  },

  // P4-S7: the «Versicherer benachrichtigen» dialog + send + Fristueberwachung list + manual close.
  versichererBenachrichtigung: {
    mandate: (subjectType: 'person' | 'firma', subjectId: string): Promise<VersichererBenachrichtigungMandateDto> =>
      apiFetch<VersichererBenachrichtigungMandateDto>(
        `/customers/${subjectType}/${subjectId}/versicherer-benachrichtigung/mandate`,
      ),
    dialog: (
      subjectType: 'person' | 'firma',
      subjectId: string,
    ): Promise<VersichererBenachrichtigungDialogDto> =>
      apiFetch<VersichererBenachrichtigungDialogDto>(
        `/customers/${subjectType}/${subjectId}/versicherer-benachrichtigung`,
      ),
    send: (
      mandateId: string,
      body: SendVersichererBenachrichtigungBody,
    ): Promise<SendVersichererBenachrichtigungResultDto> =>
      apiFetch<SendVersichererBenachrichtigungResultDto>(`/mandates/${mandateId}/versicherer-benachrichtigung`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    list: (mandateId: string): Promise<MandateInsurerNotificationDto[]> =>
      apiFetch<MandateInsurerNotificationDto[]>(`/mandates/${mandateId}/versicherer-benachrichtigungen`),
    schliessen: (
      mandateId: string,
      notificationId: string,
      body: SchliessenVersichererBenachrichtigungBody,
    ): Promise<MandateInsurerNotificationDto> =>
      apiFetch<MandateInsurerNotificationDto>(
        `/mandates/${mandateId}/versicherer-benachrichtigungen/${notificationId}/schliessen`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
  },

  // The Brokermandat registration with the insurers (Meldung je Versicherungsbeziehung).
  registrations: {
    /**
     * GET /customers/:t/:id/mandate-registrations: the area «Mandate & Eintragungen» (crm.read); with
     * tiles_only the header tile «Eintragung bei Versicherern» without rows.
     */
    view: (
      subjectType: string,
      subjectId: string,
      params: RegistrationViewFilters & { tiles_only?: boolean } = {},
    ): Promise<MandateRegistrationsViewDto> => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.mandate_kind) qs.set('mandate_kind', params.mandate_kind);
      if (params.handlungsbedarf !== undefined) qs.set('handlungsbedarf', String(params.handlungsbedarf));
      if (params.sort) qs.set('sort', params.sort);
      if (params.tiles_only !== undefined) qs.set('tiles_only', String(params.tiles_only));
      const q = qs.toString();
      return apiFetch<MandateRegistrationsViewDto>(
        `/customers/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}/mandate-registrations${q ? `?${q}` : ''}`,
      );
    },
    /** POST /customers/:t/:id/mandate-registrations/dossier: the Nachweis-Dossier, filed (document.download). */
    exportDossier: (subjectType: string, subjectId: string, body: ExportRegistrationDossierBody): Promise<RegistrationDossierExportDto> =>
      apiFetch<RegistrationDossierExportDto>(
        `/customers/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}/mandate-registrations/dossier`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    list: (mandateId: string): Promise<RegistrationDto[]> => apiFetch<RegistrationDto[]>(`/mandates/${mandateId}/registrations`),
    run: (mandateId: string): Promise<RunRegistrationsResultDto> =>
      apiFetch<RunRegistrationsResultDto>(`/mandates/${mandateId}/registrations/run`, { method: 'POST' }),
    freigeben: (mandateId: string, registrationId: string): Promise<RegistrationDto> =>
      apiFetch<RegistrationDto>(`/mandates/${mandateId}/registrations/${registrationId}/freigeben`, { method: 'POST' }),
    jetztMelden: (mandateId: string, registrationId: string, body: JetztMeldenRegistrationBody): Promise<RegistrationDto> =>
      apiFetch<RegistrationDto>(`/mandates/${mandateId}/registrations/${registrationId}/jetzt-melden`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    schliessen: (mandateId: string, registrationId: string, body: SchliessenRegistrationBody): Promise<RegistrationDto> =>
      apiFetch<RegistrationDto>(`/mandates/${mandateId}/registrations/${registrationId}/schliessen`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    erneutMelden: (mandateId: string, body: ErneutMeldenRegistrationBody): Promise<RegistrationDto> =>
      apiFetch<RegistrationDto>(`/mandates/${mandateId}/registrations/erneut-melden`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    // The reminder loop: the one reminder now (communication.send + mandate.eingangskontrolle), the
    // Klärung task now (mandate.eingangskontrolle), the registration behind a task (crm.read).
    erinnern: (mandateId: string, registrationId: string): Promise<RegistrationDto> =>
      apiFetch<RegistrationDto>(`/mandates/${mandateId}/registrations/${registrationId}/erinnern`, { method: 'POST' }),
    eskalieren: (mandateId: string, registrationId: string): Promise<RegistrationDto> =>
      apiFetch<RegistrationDto>(`/mandates/${mandateId}/registrations/${registrationId}/eskalieren`, { method: 'POST' }),
    taskRegistration: (taskId: string): Promise<RegistrationDto> => apiFetch<RegistrationDto>(`/tasks/${taskId}/registration`),
    // A «Versand unklar» reminder resolved by a broker; the rows behind «Altbestand prüfen».
    versandBestaetigt: (mandateId: string, registrationId: string): Promise<RegistrationDto> =>
      apiFetch<RegistrationDto>(`/mandates/${mandateId}/registrations/${registrationId}/erinnerung-versand-bestaetigt`, { method: 'POST' }),
    nichtVersendet: (mandateId: string, registrationId: string): Promise<RegistrationDto> =>
      apiFetch<RegistrationDto>(`/mandates/${mandateId}/registrations/${registrationId}/erinnerung-nicht-versendet`, { method: 'POST' }),
    altbestand: (taskId: string): Promise<RegistrationAltbestandDto> => apiFetch<RegistrationAltbestandDto>(`/tasks/${taskId}/altbestand`),
    // The insurer's answer, recorded by a broker (mandate.eingangskontrolle).
    bestaetigen: (mandateId: string, registrationId: string, body: BestaetigenRegistrationBody): Promise<RegistrationDto> =>
      apiFetch<RegistrationDto>(`/mandates/${mandateId}/registrations/${registrationId}/bestaetigen`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    ablehnen: (mandateId: string, registrationId: string, body: AblehnenRegistrationBody): Promise<RegistrationDto> =>
      apiFetch<RegistrationDto>(`/mandates/${mandateId}/registrations/${registrationId}/ablehnen`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    rueckfrage: (mandateId: string, registrationId: string, body: RueckfrageRegistrationBody): Promise<RegistrationDto> =>
      apiFetch<RegistrationDto>(`/mandates/${mandateId}/registrations/${registrationId}/rueckfrage`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    rueckfrageBeantwortet: (
      mandateId: string,
      registrationId: string,
      body: RueckfrageBeantwortetRegistrationBody,
    ): Promise<RegistrationDto> =>
      apiFetch<RegistrationDto>(`/mandates/${mandateId}/registrations/${registrationId}/rueckfrage-beantwortet`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    korrektur: (mandateId: string, registrationId: string, body: KorrekturRegistrationBody): Promise<RegistrationDto> =>
      apiFetch<RegistrationDto>(`/mandates/${mandateId}/registrations/${registrationId}/korrektur`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    /** GET /tasks/:taskId/registration-proposal: the parsed insurer reply behind a review task (crm.read). */
    proposal: (taskId: string): Promise<RegistrationProposalDto> =>
      apiFetch<RegistrationProposalDto>(`/tasks/${taskId}/registration-proposal`),
    /** POST /tasks/:taskId/registration-proposal/apply: Übernehmen / Korrigieren (accept) or Verwerfen. */
    applyProposal: (taskId: string, body: ApplyRegistrationProposalBody): Promise<RegistrationProposalDto> =>
      apiFetch<RegistrationProposalDto>(`/tasks/${taskId}/registration-proposal/apply`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    /** GET /tasks/:taskId/registration-zuordnen: the candidate registrations of a Zuordnen task (crm.read; 404 for another task). */
    zuordnen: (taskId: string): Promise<RegistrationZuordnenDto> =>
      apiFetch<RegistrationZuordnenDto>(`/tasks/${taskId}/registration-zuordnen`),
    /** POST /tasks/:taskId/registration-zuordnen (mandate.eingangskontrolle): match the mail to one registration; a review task opens. */
    zuordnenApply: (taskId: string, body: ZuordnenRegistrationBody): Promise<{ review_task_id: string }> =>
      apiFetch<{ review_task_id: string }>(`/tasks/${taskId}/registration-zuordnen`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    /**
     * GET /registrations/:regId/beleg-options (crm.read): the mails a broker may pick as Beleg (the
     * customer's and those exchanged with the insurer, never a draft) with their stored attachments,
     * newest first, paged by next_cursor.
     */
    belegOptions: (registrationId: string, params: { cursor?: string; limit?: number } = {}): Promise<RegistrationBelegOptionsDto> => {
      const qs = new URLSearchParams();
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      const q = qs.toString();
      return apiFetch<RegistrationBelegOptionsDto>(`/registrations/${registrationId}/beleg-options${q ? `?${q}` : ''}`);
    },
  },

  // P4-S8: the Analysemandat-Prozess state read + step-27 Abschluss / reopen.
  // P6 Mandatsauflösung: the dialog «Mandat auflösen», its Folgenanalyse, the decisions on a live one.
  mandateTermination: {
    /** POST /mandates/terminations - open; with `approve` the Freigabe in the same request (one tx). */
    open: (body: OpenTerminationBody): Promise<TerminationDto> =>
      apiFetch<TerminationDto>('/mandates/terminations', { method: 'POST', body: JSON.stringify(body) }),
    /** GET /mandates/terminations/folgenanalyse - the checklist before anything is opened (+ the Enddatum rule). */
    preview: (subjectType: 'person' | 'firma', subjectId: string, endDate?: string | null): Promise<TerminationImpactDto> =>
      apiFetch<TerminationImpactDto>(
        `/mandates/terminations/folgenanalyse?subjectType=${encodeURIComponent(subjectType)}&subjectId=${encodeURIComponent(subjectId)}${
          endDate ? `&endDate=${encodeURIComponent(endDate)}` : ''
        }`,
      ),
    /** GET /customers/:t/:id/termination - the live one (the dialog resumes it), or null. */
    live: (subjectType: 'person' | 'firma', subjectId: string): Promise<TerminationDto | null> =>
      apiFetch<TerminationDto | null>(
        `/customers/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}/termination`,
      ),
    approve: (id: string, body: ApproveTerminationBody): Promise<TerminationDto> =>
      apiFetch<TerminationDto>(`/mandates/terminations/${encodeURIComponent(id)}/freigeben`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    reject: (id: string, reason: string): Promise<TerminationDto> =>
      apiFetch<TerminationDto>(`/mandates/terminations/${encodeURIComponent(id)}/zurueckweisen`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    /** POST /mandates/terminations/:id/widerrufen - the customer goes back to the status before. */
    revoke: (id: string, reason: string, opts: { letterAlreadySent?: boolean } = {}): Promise<TerminationDto> =>
      apiFetch<TerminationDto>(`/mandates/terminations/${encodeURIComponent(id)}/widerrufen`, {
        method: 'POST',
        body: JSON.stringify(opts.letterAlreadySent === undefined ? { reason } : { reason, letterAlreadySent: opts.letterAlreadySent }),
      }),
    /** P6-S3 GET /mandates/terminations/brief-vorschau - the Dankesbrief as it would read (read-only, crm.read). */
    letterPreview: (q: {
      subjectType: 'person' | 'firma';
      subjectId: string;
      endDate: string;
      receivedOn?: string | null;
      mandateKind?: 'analyse' | 'broker' | null;
      trigger?: 'kunde_eingang' | 'kunde_portal' | 'manuell' | null;
    }): Promise<TerminationLetterPreviewDto> => {
      const p = new URLSearchParams({ subjectType: q.subjectType, subjectId: q.subjectId, endDate: q.endDate });
      if (q.receivedOn) p.set('receivedOn', q.receivedOn);
      if (q.mandateKind) p.set('mandateKind', q.mandateKind);
      if (q.trigger) p.set('trigger', q.trigger);
      return apiFetch<TerminationLetterPreviewDto>(`/mandates/terminations/brief-vorschau?${p.toString()}`);
    },
    /** P6-S3 GET /mandates/terminations?letterStatus=a,b - the board's Dankesbrief section (crm.read). */
    listByLetterStatus: (statuses: string[]): Promise<TerminationLetterListItemDto[]> =>
      apiFetch<TerminationLetterListItemDto[]>(`/mandates/terminations?letterStatus=${encodeURIComponent(statuses.join(','))}`),
    /** GET /mandates/terminations/:id - one termination with its Dankesbrief state (crm.read). */
    get: (id: string): Promise<TerminationDto> => apiFetch<TerminationDto>(`/mandates/terminations/${encodeURIComponent(id)}`),
    /** P6-S3 GET /mandates/terminations/:id/brief - the print copy (document.download). */
    letterBlob: (id: string): Promise<Blob> => apiFetchBlob(`/mandates/terminations/${encodeURIComponent(id)}/brief`),
    /** P6-S3 POST /mandates/terminations/:id/brief-gedruckt - «gedruckt und versendet». */
    letterPrinted: (id: string): Promise<TerminationDto> =>
      apiFetch<TerminationDto>(`/mandates/terminations/${encodeURIComponent(id)}/brief-gedruckt`, { method: 'POST' }),
    /** P6-S3 POST .../brief-als-post - a failed letter goes to print («Als Brief versenden»). */
    letterToPost: (id: string): Promise<TerminationDto> =>
      apiFetch<TerminationDto>(`/mandates/terminations/${encodeURIComponent(id)}/brief-als-post`, { method: 'POST' }),
    /** P6-S3 POST .../brief-als-versendet - a failed letter the broker sent himself, with a remark. */
    letterMarkSent: (id: string, remark: string, channel: 'post' | 'email' | 'portal' | 'anderer'): Promise<TerminationDto> =>
      apiFetch<TerminationDto>(`/mandates/terminations/${encodeURIComponent(id)}/brief-als-versendet`, {
        method: 'POST',
        body: JSON.stringify({ remark, channel }),
      }),
    /** P6-S3 POST /mandates/terminations/:id/brief-erneut-senden - «Erneut senden». */
    letterResend: (id: string, opts?: { confirmUnclear?: boolean }): Promise<TerminationDto> =>
      apiFetch<TerminationDto>(`/mandates/terminations/${encodeURIComponent(id)}/brief-erneut-senden`, {
        method: 'POST',
        // #103 review: a versand_unklar letter goes again only with the explicit confirm
        ...(opts?.confirmUnclear ? { body: JSON.stringify({ confirmUnclear: true }) } : {}),
      }),
    /** GET /tasks/:taskId/termination-proposal - the Kündigung the intake read from a mail (crm.read). */
    proposal: (taskId: string): Promise<TerminationProposalDto> =>
      apiFetch<TerminationProposalDto>(`/tasks/${encodeURIComponent(taskId)}/termination-proposal`),
    /** POST /tasks/:taskId/termination-proposal/apply - opens it 'eroeffnet' (never approves), the Prüfung runs. */
    applyProposal: (taskId: string, body: ApplyTerminationProposalBody): Promise<{ terminationId: string; status: string; taskId: string }> =>
      apiFetch<{ terminationId: string; status: string; taskId: string }>(`/tasks/${encodeURIComponent(taskId)}/termination-proposal/apply`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    /** POST /tasks/:taskId/termination-proposal/verwerfen - no Kündigung, the task done. */
    discardProposal: (taskId: string): Promise<{ decision: 'verworfen' }> =>
      apiFetch<{ decision: 'verworfen' }>(`/tasks/${encodeURIComponent(taskId)}/termination-proposal/verwerfen`, { method: 'POST' }),
    /** POST /mandates/terminations/:id/berechtigung-bestaetigen - «Berechtigung bestätigt» (in_pruefung -> eroeffnet). */
    confirmAuthorization: (id: string, reasonCode: TerminationOverrideReasonCode): Promise<{ terminationId: string; freigabeTaskId: string }> =>
      apiFetch<{ terminationId: string; freigabeTaskId: string }>(`/mandates/terminations/${encodeURIComponent(id)}/berechtigung-bestaetigen`, {
        method: 'POST',
        body: JSON.stringify({ reasonCode }),
      }),
    /** GET /admin/einstellungen/mandatsaufloesung (tenant.read). */
    config: (): Promise<MandateTerminationConfigDto> =>
      apiFetch<MandateTerminationConfigDto>('/admin/einstellungen/mandatsaufloesung'),
    /** PUT /admin/einstellungen/mandatsaufloesung (tenant.manage). */
    setConfig: (body: SetMandateTerminationConfigBody): Promise<MandateTerminationConfigDto> =>
      apiFetch<MandateTerminationConfigDto>('/admin/einstellungen/mandatsaufloesung', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
  },

  mandateProcess: {
    state: (mandateId: string): Promise<MandateProcessStateDto> =>
      apiFetch<MandateProcessStateDto>(`/mandates/${mandateId}/process-state`),
    close: (mandateId: string, body: CloseMandateProcessBody = {}): Promise<MandateProcessStateDto> =>
      apiFetch<MandateProcessStateDto>(`/mandates/${mandateId}/process-close`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    reopen: (mandateId: string): Promise<MandateProcessStateDto> =>
      apiFetch<MandateProcessStateDto>(`/mandates/${mandateId}/process-reopen`, { method: 'POST' }),
  },

  customers: {
    /** GET /customers — Kunden-grid list (Story V10-J; keyset cursor in V10-J2). Returns { items, page }. */
    list: (params: CustomerListParams = {}): Promise<CustomerListResponseDto> => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.subject_type) qs.set('subject_type', params.subject_type);
      if (params.q) qs.set('q', params.q);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.unconfirmed_only !== undefined) {
        qs.set('unconfirmed_only', String(params.unconfirmed_only));
      }
      if (params.customers_only !== undefined) {
        qs.set('customers_only', String(params.customers_only));
      }
      if (params.include_total !== undefined) qs.set('include_total', String(params.include_total));
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<CustomerListResponseDto>(`/customers${query}`);
    },

    /** POST /customers — manual create-customer wizard. Returns { subjectType, subjectId }. */
    create: (body: CreateCustomerBody): Promise<CreateCustomerResponseDto> =>
      apiFetch<CreateCustomerResponseDto>('/customers', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** DELETE /customers/:id — soft-delete a firma customer. 204 No Content. */
    delete: (firmaId: string): Promise<void> =>
      apiFetch<void>(`/customers/${firmaId}`, { method: 'DELETE' }),

    /**
     * POST /customers/:id/confirm: confirms an unconfirmed record as a real client
     * (is_unconfirmed → false). 204 No Content; 409 when the record is not open unconfirmed.
     */
    confirmCustomer: (id: string): Promise<void> =>
      apiFetch<void>(`/customers/${id}/confirm`, { method: 'POST' }),

    /**
     * POST /customers/:id/decline: "Kein Kunde", cancels the open tasks of the record's incoming
     * mails and sets no status. 204 No Content; 409 when the record is not open unconfirmed.
     */
    declineCustomer: (id: string): Promise<void> =>
      apiFetch<void>(`/customers/${id}/decline`, { method: 'POST' }),

    /**
     * PATCH /customers/:type/:id/mail-team (MV): set the team whose members may
     * see this client's MAIL. `teamId` null lifts the restriction. Guarded by
     * `visibility.manage`; the api is the hard line (rule #2).
     */
    setMailTeam: (
      subjectType: 'person' | 'firma',
      subjectId: string,
      teamId: string | null,
    ): Promise<CustomerMailTeamDto> =>
      apiFetch<CustomerMailTeamDto>(`/customers/${subjectType}/${subjectId}/mail-team`, {
        method: 'PATCH',
        body: JSON.stringify({ team_id: teamId }),
      }),

    // ── P2 Placement Slice 1 — Kunde-Ebene «Risikoprofil» tab ──────────────────
    /** GET the generelles Risikoprofil (gated fields stripped server-side when role-hidden). */
    getRiskProfile: (subjectType: 'person' | 'firma', subjectId: string): Promise<RisikoprofilResponse> =>
      apiFetch<RisikoprofilResponse>(`/customers/${subjectType}/${subjectId}/risk-profile`),

    /** PATCH the Risikoprofil (merge-patch of attributes + gated columns; expected_updated_at → 409). */
    patchRiskProfile: (
      subjectType: 'person' | 'firma',
      subjectId: string,
      body: RisikoprofilPatchBody,
    ): Promise<RisikoprofilResponse> =>
      apiFetch<RisikoprofilResponse>(`/customers/${subjectType}/${subjectId}/risk-profile`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    /** POST .../risk-profile/review — mark the profile «geprueft». */
    reviewRiskProfile: (subjectType: 'person' | 'firma', subjectId: string): Promise<RisikoprofilResponse> =>
      apiFetch<RisikoprofilResponse>(`/customers/${subjectType}/${subjectId}/risk-profile/review`, {
        method: 'POST',
      }),

    /** PUT .../risk-profile/einwilligung — record/withdraw the besondere-Personendaten consent. */
    putRiskProfileEinwilligung: (
      subjectType: 'person' | 'firma',
      subjectId: string,
      erfasst: boolean,
    ): Promise<RisikoprofilResponse> =>
      apiFetch<RisikoprofilResponse>(`/customers/${subjectType}/${subjectId}/risk-profile/einwilligung`, {
        method: 'PUT',
        body: JSON.stringify({ erfasst }),
      }),

    /** GET the Basissparten-Checkliste (catalog Soll merged with the per-customer assessment + Ampel). */
    getCoverageBaseline: (subjectType: 'person' | 'firma', subjectId: string): Promise<BasisspartenResponse> =>
      apiFetch<BasisspartenResponse>(`/customers/${subjectType}/${subjectId}/coverage-baseline`),

    /** PUT one Basissparte assessment (applicable / status_override / notiz). */
    putCoverageBaseline: (
      subjectType: 'person' | 'firma',
      subjectId: string,
      sparteKey: string,
      body: BasisspartePutBody,
    ): Promise<BasisspartenItem> =>
      apiFetch<BasisspartenItem>(`/customers/${subjectType}/${subjectId}/coverage-baseline/${sparteKey}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

    // ── P2 hardening Q15 — Schadenverlauf je Sparte ──────────────────────────
    /** GET the per-Sparte Schadenverlauf (stored rows + read-only Schadenauskunft hints + legacy Altbestand). */
    getClaimsHistory: (subjectType: 'person' | 'firma', subjectId: string): Promise<KundeSchadenverlaufResponse> =>
      apiFetch<KundeSchadenverlaufResponse>(`/customers/${subjectType}/${subjectId}/claims-history`),

    /** PUT one per-Sparte row (true merge; expected_updated_at → 409). */
    putClaimsHistorySparte: (
      subjectType: 'person' | 'firma',
      subjectId: string,
      branchencode: string,
      body: KundeSchadenSpartePutBody,
    ): Promise<KundeSchadenSparteItem> =>
      apiFetch<KundeSchadenSparteItem>(`/customers/${subjectType}/${subjectId}/claims-history/${branchencode}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

    /** DELETE one per-Sparte row (soft remove, idempotent). 204 No Content. */
    removeClaimsHistorySparte: (subjectType: 'person' | 'firma', subjectId: string, branchencode: string): Promise<void> =>
      apiFetch<void>(`/customers/${subjectType}/${subjectId}/claims-history/${branchencode}`, { method: 'DELETE' }),

    /** DELETE the retired flat Schaden-Altbestand keys. 204 No Content. */
    clearClaimsHistoryLegacy: (subjectType: 'person' | 'firma', subjectId: string): Promise<void> =>
      apiFetch<void>(`/customers/${subjectType}/${subjectId}/claims-history/legacy`, { method: 'DELETE' }),
  },

  // ── P2 Placement Slice 4 — the Ausschreibungs-/Placement object ────────────────
  placements: {
    /** GET /placements — a customer's Placements + KPIs, optionally filtered by status / closure. */
    listForCustomer: (
      subjectType: 'person' | 'firma',
      subjectId: string,
      filters: { status?: PlacementStatus; closure_status?: PlacementClosureStatus } = {},
    ): Promise<PlacementListResponse> => {
      const qs = new URLSearchParams();
      qs.set('subject_type', subjectType);
      qs.set('subject_id', subjectId);
      if (filters.status) qs.set('status', filters.status);
      if (filters.closure_status) qs.set('closure_status', filters.closure_status);
      return apiFetch<PlacementListResponse>(`/placements?${qs.toString()}`);
    },

    /**
     * GET /placements with NO subject_type/subject_id — the TENANT-WIDE list (the sidebar
     * «Ausschreibungen» screen). Same PlacementListResponse shape; each item additionally
     * carries the customer's display name as `subject_name`. Optional status / closure filters.
     */
    listAll: (
      filters: {
        status?: PlacementStatus;
        closure_status?: PlacementClosureStatus;
        view?: PlacementListView;
        q?: string;
        limit?: number;
        cursor?: string;
      } = {},
    ): Promise<PlacementFirmListResponse> => {
      const qs = new URLSearchParams();
      if (filters.status) qs.set('status', filters.status);
      if (filters.closure_status) qs.set('closure_status', filters.closure_status);
      if (filters.view) qs.set('view', filters.view);
      if (filters.q) qs.set('q', filters.q);
      if (filters.limit !== undefined) qs.set('limit', String(filters.limit));
      if (filters.cursor) qs.set('cursor', filters.cursor);
      const q = qs.toString();
      return apiFetch<PlacementFirmListResponse>(q ? `/placements?${q}` : '/placements');
    },

    /** POST /placements — create a Placement (201 → the full detail). */
    create: (body: PlacementCreateBody): Promise<PlacementDetail> =>
      apiFetch<PlacementDetail>('/placements', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** GET /placements/:id — the full Placement detail. */
    get: (id: string): Promise<PlacementDetail> => apiFetch<PlacementDetail>(`/placements/${id}`),

    /** POST /placements/:id/abbrechen — cancel a Placement with a Grund. */
    abbrechen: (id: string, grund: string): Promise<PlacementDetail> =>
      apiFetch<PlacementDetail>(`/placements/${id}/abbrechen`, {
        method: 'POST',
        body: JSON.stringify({ grund }),
      }),

    // ── P2 Placement Slice 5 — Tab 1 Risikoprofil snapshot + Tab 2 Soll-Deckungskatalog ──

    /** GET /placements/:id/risikoprofil — the frozen Kunde general + editable branch Ist (materializes). */
    getRisikoprofil: (id: string): Promise<PlacementRisikoprofilResponse> =>
      apiFetch<PlacementRisikoprofilResponse>(`/placements/${id}/risikoprofil`),

    /** PATCH /placements/:id/risikoprofil/branch — merge-patch one branch object's working attributes. */
    patchBranchIst: (id: string, body: PlacementBranchIstPatchBody): Promise<PlacementRisikoprofilResponse> =>
      apiFetch<PlacementRisikoprofilResponse>(`/placements/${id}/risikoprofil/branch`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    /** POST /placements/:id/risikoprofil/refresh-general — re-materialize the general snapshot. */
    refreshGeneral: (id: string): Promise<PlacementRisikoprofilResponse> =>
      apiFetch<PlacementRisikoprofilResponse>(`/placements/${id}/risikoprofil/refresh-general`, { method: 'POST' }),

    /** POST /placements/:id/risikoprofil/abschliessen — advance to RisikoprofilErfasst (→ the detail). */
    abschliessenRisikoprofil: (id: string): Promise<PlacementDetail> =>
      apiFetch<PlacementDetail>(`/placements/${id}/risikoprofil/abschliessen`, { method: 'POST' }),

    /** GET /placements/:id/soll-deckung — the Soll-Deckung answers + progress + Pflichtdeckung (materializes). */
    getSollDeckung: (id: string): Promise<PlacementSollDeckungResponse> =>
      apiFetch<PlacementSollDeckungResponse>(`/placements/${id}/soll-deckung`),

    /** PUT /placements/:id/soll-deckung/:ruleKey — write one answer (true merge). */
    putAnswer: (id: string, ruleKey: string, body: Step2AnswerPutBody): Promise<PlacementSollDeckungResponse> =>
      apiFetch<PlacementSollDeckungResponse>(`/placements/${id}/soll-deckung/${ruleKey}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

    /** POST /placements/:id/soll-deckung/abschliessen — advance to VorgabenErfasst (→ the detail). */
    abschliessenSollDeckung: (id: string): Promise<PlacementDetail> =>
      apiFetch<PlacementDetail>(`/placements/${id}/soll-deckung/abschliessen`, { method: 'POST' }),

    /** POST /placements/:id/pflichtdeckung-kenntnisnahme — acknowledge the E12 warning. */
    pflichtdeckungKenntnisnahme: (id: string): Promise<PlacementSollDeckungResponse> =>
      apiFetch<PlacementSollDeckungResponse>(`/placements/${id}/pflichtdeckung-kenntnisnahme`, { method: 'POST' }),

    // ── P2 Placement Slice 7 — Tab 3a Versicherer-Ranking & Auswahl ──
    /** GET /placements/:id/versicherer/ranking — the ranked Versicherer universe (may recompute stats). */
    getVersichererRanking: (id: string): Promise<VersichererRankingResponse> =>
      apiFetch<VersichererRankingResponse>(`/placements/${id}/versicherer/ranking`),

    /** POST /placements/:id/versicherer/ranking/refresh — force a Kennzahlen recompute (audited). */
    refreshVersichererStats: (id: string): Promise<VersichererRankingResponse> =>
      apiFetch<VersichererRankingResponse>(`/placements/${id}/versicherer/ranking/refresh`, { method: 'POST' }),

    /** GET /placements/:id/versicherer/auswahl — the current selection + header chips. */
    getVersichererAuswahl: (id: string): Promise<VersichererAuswahlResponse> =>
      apiFetch<VersichererAuswahlResponse>(`/placements/${id}/versicherer/auswahl`),

    /** PUT /placements/:id/versicherer/auswahl — PUT-replace the selected carrier set. */
    putVersichererAuswahl: (id: string, body: VersichererAuswahlPutBody): Promise<VersichererAuswahlResponse> =>
      apiFetch<VersichererAuswahlResponse>(`/placements/${id}/versicherer/auswahl`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

    // ── P2 Placement Slice 8 — Tab 3b Submission + Versand + Erinnerung ──
    /** GET /placements/:id/submissions — the Offertanfragen list + counters + overlaid chips. */
    getSubmissions: (id: string): Promise<SubmissionListResponse> =>
      apiFetch<SubmissionListResponse>(`/placements/${id}/submissions`),

    /** POST /placements/:id/submissions — create an Offertanfrage per ausgewaehlt selection row. */
    erstellenSubmissions: (id: string): Promise<SubmissionErstellenResponse> =>
      apiFetch<SubmissionErstellenResponse>(`/placements/${id}/submissions`, { method: 'POST' }),

    /** POST /placements/:id/submissions/versenden — send the neu submissions from the broker's mailbox. */
    versendenSubmissions: (id: string, body: SubmissionVersendenBody): Promise<SubmissionVersendenResponse> =>
      apiFetch<SubmissionVersendenResponse>(`/placements/${id}/submissions/versenden`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** POST /placements/:id/submissions/:sid/erinnern — send a threaded reminder. */
    erinnernSubmission: (id: string, sid: string, body: SubmissionErinnernBody): Promise<SubmissionVersendenResponse> =>
      apiFetch<SubmissionVersendenResponse>(`/placements/${id}/submissions/${sid}/erinnern`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** POST /placements/:id/submissions/:sid/versand-aufloesen — resolve an interrupted send (#5). */
    versandAufloesenSubmission: (id: string, sid: string, aktion: 'nachtragen' | 'freigeben'): Promise<SubmissionListResponse> =>
      apiFetch<SubmissionListResponse>(`/placements/${id}/submissions/${sid}/versand-aufloesen`, {
        method: 'POST',
        body: JSON.stringify({ aktion }),
      }),

    /** POST /placements/:id/submissions/:sid/nicht-offeriert — mark the carrier declined (E1). */
    nichtOfferiertSubmission: (id: string, sid: string, body: SubmissionNichtOfferiertBody): Promise<SubmissionListResponse> =>
      apiFetch<SubmissionListResponse>(`/placements/${id}/submissions/${sid}/nicht-offeriert`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** POST /placements/:id/submissions/:sid/inaktiv — mark inaktiv (Ch 8.1). */
    inaktivSubmission: (id: string, sid: string, body: SubmissionInaktivBody): Promise<SubmissionListResponse> =>
      apiFetch<SubmissionListResponse>(`/placements/${id}/submissions/${sid}/inaktiv`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** PATCH /placements/:id/submissions/:sid/frist — edit the Antwortfrist. */
    patchSubmissionFrist: (id: string, sid: string, body: SubmissionFristBody): Promise<SubmissionListResponse> =>
      apiFetch<SubmissionListResponse>(`/placements/${id}/submissions/${sid}/frist`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    /** GET /placements/:id/submissions/:sid/unterlagen — the Unterlagen-Vorschau info list. */
    getSubmissionUnterlagen: (id: string, sid: string): Promise<SubmissionUnterlagenResponse> =>
      apiFetch<SubmissionUnterlagenResponse>(`/placements/${id}/submissions/${sid}/unterlagen`),

    /**
     * Absolute URL the browser opens in a new tab to view/download one Unterlage (bytes endpoint —
     * NOT a JSON api-client fn; mirrors the DMS content-link idiom + signatureUrl).
     */
    submissionUnterlageUrl: (id: string, sid: string, art: string): string =>
      `${API_BASE_URL}/placements/${id}/submissions/${sid}/unterlagen/${art}`,

    // ── P2 Placement Slice 9 — Rückfragen / Q&A (Tab 3c) ──────────────────────────
    /** GET /placements/:id/qna — the Q&A list (one thread per submission, lazily). */
    getQna: (id: string): Promise<QnaListResponse> => apiFetch<QnaListResponse>(`/placements/${id}/qna`),

    /** POST /placements/:id/submissions/:sid/qna/antworten — answer the carrier (threaded via MC5). */
    antwortenQna: (id: string, sid: string, body: QnaAntwortenBody): Promise<QnaListResponse> =>
      apiFetch<QnaListResponse>(`/placements/${id}/submissions/${sid}/qna/antworten`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** POST /placements/:id/submissions/:sid/qna/erledigt — close the thread by hand. */
    erledigtQna: (id: string, sid: string, body: QnaErledigtBody): Promise<QnaListResponse> =>
      apiFetch<QnaListResponse>(`/placements/${id}/submissions/${sid}/qna/erledigt`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** POST /placements/:id/submissions/:sid/qna/offen — re-open the thread. */
    offenQna: (id: string, sid: string): Promise<QnaListResponse> =>
      apiFetch<QnaListResponse>(`/placements/${id}/submissions/${sid}/qna/offen`, { method: 'POST' }),

    /** PATCH /placements/:id/submissions/:sid/qna/frist — edit the Antwortfrist. */
    patchQnaFrist: (id: string, sid: string, body: QnaFristBody): Promise<QnaListResponse> =>
      apiFetch<QnaListResponse>(`/placements/${id}/submissions/${sid}/qna/frist`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    /** POST /placements/:id/submissions/:sid/qna/gelesen — mark the thread's unread questions read. */
    gelesenQna: (id: string, sid: string): Promise<QnaGelesenResponse> =>
      apiFetch<QnaGelesenResponse>(`/placements/${id}/submissions/${sid}/qna/gelesen`, { method: 'POST' }),

    // ── P2 Placement Slice 10 — Offert-Posteingang (Tab 4a) ───────────────────────
    /** GET /placements/:id/offerten — the offer list (grouped per submission + nicht zugeordnet). */
    getOfferten: (id: string): Promise<OfferListResponse> => apiFetch<OfferListResponse>(`/placements/${id}/offerten`),

    /** The in-app datei stream URL (relative path for an <a href>/viewer; NOT a JSON api-client fn). */
    offertDateiUrl: (id: string, oid: string): string => `${API_BASE_URL}/placements/${id}/offerten/${oid}/datei`,

    /** POST /placements/:id/offerten/upload — manual offer upload (multipart; optional submission_id). */
    uploadOffert: async (id: string, file: File | Blob, submissionId?: string): Promise<OfferUploadResponse> => {
      const form = new FormData();
      form.append('file', file, file instanceof File ? file.name : 'offerte.pdf');
      if (submissionId) form.append('submission_id', submissionId);
      const res = await authedFetch(`${API_BASE_URL}/placements/${id}/offerten/upload`, { method: 'POST', body: form });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
        throw new Error(body.message ?? body.code ?? `HTTP ${res.status}`);
      }
      return res.json() as Promise<OfferUploadResponse>;
    },

    /** POST /placements/:id/offerten/:oid/zuordnen — assign the offer to a submission. */
    zuordnenOffert: (id: string, oid: string, body: OfferZuordnenBody): Promise<OfferListResponse> =>
      apiFetch<OfferListResponse>(`/placements/${id}/offerten/${oid}/zuordnen`, { method: 'POST', body: JSON.stringify(body) }),

    /** POST /placements/:id/offerten/:oid/aktuell — set this version current (E9). */
    aktuellOffert: (id: string, oid: string): Promise<OfferListResponse> =>
      apiFetch<OfferListResponse>(`/placements/${id}/offerten/${oid}/aktuell`, { method: 'POST' }),

    /** POST /placements/:id/offerten/:oid/re-extract — re-run the extractor. */
    reExtractOffert: (id: string, oid: string): Promise<OfferListResponse> =>
      apiFetch<OfferListResponse>(`/placements/${id}/offerten/${oid}/re-extract`, { method: 'POST' }),

    /** PATCH /placements/:id/offerten/:oid/normalisiert — the E11 broker completion. */
    normalisiertOffert: (id: string, oid: string, body: OfferNormalisiertBody): Promise<OfferListResponse> =>
      apiFetch<OfferListResponse>(`/placements/${id}/offerten/${oid}/normalisiert`, { method: 'PATCH', body: JSON.stringify(body) }),

    /** POST /placements/:id/offerten/:oid/verwerfen — discard the offer. */
    verwerfenOffert: (id: string, oid: string, body: OfferVerwerfenBody): Promise<OfferListResponse> =>
      apiFetch<OfferListResponse>(`/placements/${id}/offerten/${oid}/verwerfen`, { method: 'POST', body: JSON.stringify(body) }),

    // P2 Placement Slice 11 — Tab 4b KI-Offertvergleich
    /** GET /placements/:id/vergleich — the aktuell comparison report + run/edit gates. */
    getVergleich: (id: string): Promise<OfferComparisonResponse> =>
      apiFetch<OfferComparisonResponse>(`/placements/${id}/vergleich`),

    /** POST /placements/:id/vergleich — run the comparison (optional weights override). */
    runVergleich: (id: string, body: OfferComparisonRunBody = {}): Promise<OfferComparisonResponse> =>
      apiFetch<OfferComparisonResponse>(`/placements/${id}/vergleich`, { method: 'POST', body: JSON.stringify(body) }),

    /** PATCH /placements/:id/vergleich/:rid — broker review (narrative, weights, per-offer edits). */
    patchVergleich: (id: string, rid: string, body: OfferComparisonPatchBody): Promise<OfferComparisonResponse> =>
      apiFetch<OfferComparisonResponse>(`/placements/${id}/vergleich/${rid}`, { method: 'PATCH', body: JSON.stringify(body) }),

    /** POST /placements/:id/vergleich/:rid/ki-neu — re-run the KI narrative. */
    kiNeuVergleich: (id: string, rid: string): Promise<OfferComparisonResponse> =>
      apiFetch<OfferComparisonResponse>(`/placements/${id}/vergleich/${rid}/ki-neu`, { method: 'POST' }),

    /** POST /placements/:id/vergleich/:rid/freigeben — release the recommendation (FIDLEG gate). */
    freigebenVergleich: (id: string, rid: string): Promise<OfferComparisonResponse> =>
      apiFetch<OfferComparisonResponse>(`/placements/${id}/vergleich/${rid}/freigeben`, { method: 'POST' }),

    /** POST /placements/:id/vergleich/:rid/export — render + persist the Word export. */
    exportVergleich: (id: string, rid: string): Promise<OfferComparisonResponse> =>
      apiFetch<OfferComparisonResponse>(`/placements/${id}/vergleich/${rid}/export`, { method: 'POST' }),

    /** The in-app datei stream URL (relative path for an <a href download>; NOT a JSON api-client fn). */
    vergleichDateiUrl: (id: string, rid: string): string => `${API_BASE_URL}/placements/${id}/vergleich/${rid}/export/datei`,

    // P2 Placement Slice 12 — Tab 4c Kundenfreigabe + Kundenentscheid
    /** GET /placements/:id/kundenfreigabe — the dialog facts + the delivery + the decisions. */
    getKundenfreigabe: (id: string): Promise<KundenfreigabeResponse> =>
      apiFetch<KundenfreigabeResponse>(`/placements/${id}/kundenfreigabe`),

    /** POST /placements/:id/kundenfreigabe/zustellen — deliver the released comparison (channel pre-resolved). */
    zustellenKundenfreigabe: (id: string, body: KundenfreigabeZustellenBody = {}): Promise<KundenfreigabeResponse> =>
      apiFetch<KundenfreigabeResponse>(`/placements/${id}/kundenfreigabe/zustellen`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** POST /placements/:id/kundenfreigabe/kanal — E14: switch channel + re-deliver. */
    kanalKundenfreigabe: (id: string, body: KundenfreigabeKanalBody): Promise<KundenfreigabeResponse> =>
      apiFetch<KundenfreigabeResponse>(`/placements/${id}/kundenfreigabe/kanal`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** POST /placements/:id/kundenfreigabe/erneut-senden — re-deliver over the current channel. */
    erneutSendenKundenfreigabe: (id: string): Promise<KundenfreigabeResponse> =>
      apiFetch<KundenfreigabeResponse>(`/placements/${id}/kundenfreigabe/erneut-senden`, { method: 'POST' }),

    /** POST /placements/:id/kundenfreigabe/versand-aufloesen — resolve an interrupted send (#5). */
    versandAufloesenKundenfreigabe: (id: string, aktion: 'nachtragen' | 'freigeben'): Promise<KundenfreigabeResponse> =>
      apiFetch<KundenfreigabeResponse>(`/placements/${id}/kundenfreigabe/versand-aufloesen`, {
        method: 'POST',
        body: JSON.stringify({ aktion }),
      }),

    /** POST /placements/:id/kundenfreigabe/entscheid — record the Kundenentscheid on the customer's behalf. */
    entscheidKundenfreigabe: (id: string, body: KundenentscheidErfassenBody): Promise<KundenfreigabeResponse> =>
      apiFetch<KundenfreigabeResponse>(`/placements/${id}/kundenfreigabe/entscheid`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    // P2 Placement Slice 13 — Tab 5a Antrag + Signatur + Einreichung
    /** GET /placements/:id/antrag — the Tab-5a facts + the aktuell Antrag + its versions. */
    getAntrag: (id: string): Promise<AntragResponse> => apiFetch<AntragResponse>(`/placements/${id}/antrag`),

    /** POST /placements/:id/antrag — «Antrag vorbereiten» (prepare the first/next version). */
    vorbereitenAntrag: (id: string, body: AntragVorbereitenBody = {}): Promise<AntragResponse> =>
      apiFetch<AntragResponse>(`/placements/${id}/antrag`, { method: 'POST', body: JSON.stringify(body) }),

    /** PATCH /placements/:id/antrag — edit + re-render while vorbereitet. */
    aktualisierenAntrag: (id: string, body: AntragAktualisierenBody): Promise<AntragResponse> =>
      apiFetch<AntragResponse>(`/placements/${id}/antrag`, { method: 'PATCH', body: JSON.stringify(body) }),

    /** The Antrag document stream URL (for an <a href>/download; NOT a JSON api-client fn). */
    antragDateiUrl: (id: string): string => `${API_BASE_URL}/placements/${id}/antrag/dokument`,

    /** The signed-Antrag document stream URL. */
    antragSigniertDateiUrl: (id: string): string => `${API_BASE_URL}/placements/${id}/antrag/signiert/dokument`,

    /** POST /placements/:id/antrag/signatur — «Zur Unterschrift senden» (portal / email / esign). */
    signaturAntrag: (id: string, body: AntragSignaturBody): Promise<AntragResponse> =>
      apiFetch<AntragResponse>(`/placements/${id}/antrag/signatur`, { method: 'POST', body: JSON.stringify(body) }),

    /** POST /placements/:id/antrag/signatur/erneut — «Erneut zustellen». */
    erneutSignaturAntrag: (id: string): Promise<AntragResponse> =>
      apiFetch<AntragResponse>(`/placements/${id}/antrag/signatur/erneut`, { method: 'POST' }),

    /** PATCH /placements/:id/antrag/signatur/frist — change the signature Frist. */
    fristAntrag: (id: string, body: AntragFristBody): Promise<AntragResponse> =>
      apiFetch<AntragResponse>(`/placements/${id}/antrag/signatur/frist`, { method: 'PATCH', body: JSON.stringify(body) }),

    /** POST /placements/:id/antrag/signatur/abfragen — «Signaturstatus abfragen» (esign poll). */
    abfragenSignaturAntrag: (id: string): Promise<AntragResponse> =>
      apiFetch<AntragResponse>(`/placements/${id}/antrag/signatur/abfragen`, { method: 'POST' }),

    /** POST /placements/:id/antrag/signiert — upload the signed Antrag (broker; multipart). */
    signiertHochladenAntrag: async (id: string, file: File | Blob): Promise<AntragResponse> => {
      const form = new FormData();
      form.append('file', file, file instanceof File ? file.name : 'antrag-signiert.pdf');
      const res = await authedFetch(`${API_BASE_URL}/placements/${id}/antrag/signiert`, { method: 'POST', body: form });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
        throw new Error(body.message ?? body.code ?? `HTTP ${res.status}`);
      }
      return res.json() as Promise<AntragResponse>;
    },

    /** POST /placements/:id/antrag/einreichen — «Einreichen» (mail the signed Antrag to the carrier). */
    einreichenAntrag: (id: string): Promise<AntragResponse> =>
      apiFetch<AntragResponse>(`/placements/${id}/antrag/einreichen`, { method: 'POST' }),

    /** POST /placements/:id/antrag/verwerfen — «Verwerfen» (discard a wrong Antrag version). */
    verwerfenAntrag: (id: string, body: AntragVerwerfenBody): Promise<AntragResponse> =>
      apiFetch<AntragResponse>(`/placements/${id}/antrag/verwerfen`, { method: 'POST', body: JSON.stringify(body) }),

    /** POST /placements/:id/antrag/versand-aufloesen — resolve an interrupted send (#5). */
    versandAufloesenAntrag: (id: string, aktion: 'nachtragen' | 'freigeben'): Promise<AntragResponse> =>
      apiFetch<AntragResponse>(`/placements/${id}/antrag/versand-aufloesen`, { method: 'POST', body: JSON.stringify({ aktion }) }),

    // P2 Placement Slice 14 — Tab 5b Underwriting / Versicherer-Rueckmeldung
    /** GET /placements/:id/underwriting — the Tab-5b facts (carrier reply, entscheid, rounds, alternativen). */
    getUnderwriting: (id: string): Promise<UnderwritingResponse> =>
      apiFetch<UnderwritingResponse>(`/placements/${id}/underwriting`),

    /** POST /placements/:id/underwriting/annehmen — record the carrier acceptance. */
    annehmenUnderwriting: (id: string, body: UwEntscheidBody): Promise<UnderwritingResponse> =>
      apiFetch<UnderwritingResponse>(`/placements/${id}/underwriting/annehmen`, { method: 'POST', body: JSON.stringify(body) }),

    /** POST /placements/:id/underwriting/ablehnen — record the carrier rejection (E7). */
    ablehnenUnderwriting: (id: string, body: UwEntscheidBody): Promise<UnderwritingResponse> =>
      apiFetch<UnderwritingResponse>(`/placements/${id}/underwriting/ablehnen`, { method: 'POST', body: JSON.stringify(body) }),

    /** POST /placements/:id/underwriting/bedingungen — open an E6 condition round. */
    bedingungenUnderwriting: (id: string, body: UwBedingungenBody): Promise<UnderwritingResponse> =>
      apiFetch<UnderwritingResponse>(`/placements/${id}/underwriting/bedingungen`, { method: 'POST', body: JSON.stringify(body) }),

    /** POST /placements/:id/underwriting/runden/:rundeId/entscheid — record the customer decision on a round. */
    rundeEntscheidUnderwriting: (id: string, rundeId: string, body: UwRundeEntscheidBody): Promise<UnderwritingResponse> =>
      apiFetch<UnderwritingResponse>(`/placements/${id}/underwriting/runden/${rundeId}/entscheid`, { method: 'POST', body: JSON.stringify(body) }),

    /** POST /placements/:id/underwriting/alternative — propose an E7 alternative offer. */
    alternativeUnderwriting: (id: string, body: UwAlternativeBody): Promise<UnderwritingResponse> =>
      apiFetch<UnderwritingResponse>(`/placements/${id}/underwriting/alternative`, { method: 'POST', body: JSON.stringify(body) }),

    /** POST /placements/:id/underwriting/fallback — record a E7 fallback (gleicher Versicherer / Abbruch). */
    fallbackUnderwriting: (id: string, body: UwFallbackBody): Promise<UnderwritingResponse> =>
      apiFetch<UnderwritingResponse>(`/placements/${id}/underwriting/fallback`, { method: 'POST', body: JSON.stringify(body) }),

    /** POST /placements/:id/underwriting/deckung — record the Deckungsbestaetigung. */
    deckungUnderwriting: (id: string, body: UwDeckungBody): Promise<UnderwritingResponse> =>
      apiFetch<UnderwritingResponse>(`/placements/${id}/underwriting/deckung`, { method: 'POST', body: JSON.stringify(body) }),

    /** POST /placements/:id/underwriting/police-erhalten — record the received police. */
    policeErhaltenUnderwriting: (id: string, body: UwPoliceErhaltenBody): Promise<UnderwritingResponse> =>
      apiFetch<UnderwritingResponse>(`/placements/${id}/underwriting/police-erhalten`, { method: 'POST', body: JSON.stringify(body) }),

    // P2 Placement Slice 15 — Tab 6 (Historie + Policenkontrolle + Abschluss + Zustellung).
    /** GET /placements/:id/historie — the read-only end-to-end timeline. */
    getHistorie: (id: string, query?: { limit?: number; cursor?: string; quelle?: 'aktivitaet' | 'alle' }): Promise<PlacementHistorieResponse> => {
      const qs = new URLSearchParams();
      if (query?.limit != null) qs.set('limit', String(query.limit));
      if (query?.cursor) qs.set('cursor', query.cursor);
      if (query?.quelle) qs.set('quelle', query.quelle);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<PlacementHistorieResponse>(`/placements/${id}/historie${suffix}`);
    },
    /** GET /placements/:id/abschluss — the Abschluss section (Kontrolle, police, closure, Zustellung, aktionen). */
    getAbschluss: (id: string): Promise<PlacementAbschlussResponse> => apiFetch<PlacementAbschlussResponse>(`/placements/${id}/abschluss`),
    /** POST /placements/:id/abschluss/kontrolle — run the Policenkontrolle now. */
    kontrolleAbschluss: (id: string): Promise<PlacementAbschlussResponse> =>
      apiFetch<PlacementAbschlussResponse>(`/placements/${id}/abschluss/kontrolle`, { method: 'POST' }),
    /** POST /placements/:id/abschluss/police-verknuepfen — manually link a police. */
    policeVerknuepfenAbschluss: (id: string, body: PkPoliceVerknuepfenBody): Promise<PlacementAbschlussResponse> =>
      apiFetch<PlacementAbschlussResponse>(`/placements/${id}/abschluss/police-verknuepfen`, { method: 'POST', body: JSON.stringify(body) }),
    /** POST /placements/:id/abschluss/abweichung-geklaert — release the closure after a cleared Abweichung. */
    abweichungGeklaertAbschluss: (id: string, body: PkAbweichungGeklaertBody): Promise<PlacementAbschlussResponse> =>
      apiFetch<PlacementAbschlussResponse>(`/placements/${id}/abschluss/abweichung-geklaert`, { method: 'POST', body: JSON.stringify(body) }),
    /** POST /placements/:id/abschluss/police-zustellen — e-mail the police from the broker's mailbox. */
    policeZustellenAbschluss: (id: string): Promise<PlacementAbschlussResponse> =>
      apiFetch<PlacementAbschlussResponse>(`/placements/${id}/abschluss/police-zustellen`, { method: 'POST' }),
    /** POST /placements/:id/abschluss/zustellung-aufloesen — resolve an interrupted delivery (#6). */
    zustellungAufloesenAbschluss: (id: string, aktion: 'nachtragen' | 'freigeben'): Promise<PlacementAbschlussResponse> =>
      apiFetch<PlacementAbschlussResponse>(`/placements/${id}/abschluss/zustellung-aufloesen`, { method: 'POST', body: JSON.stringify({ aktion }) }),
    /** POST /placements/:id/abschluss/rueckmeldung-versicherer — reply to the insurer on an abweichung (P2 hardening 05). */
    rueckmeldungVersichererAbschluss: (id: string, body: PkRueckmeldungVersichererBody): Promise<PlacementAbschlussResponse> =>
      apiFetch<PlacementAbschlussResponse>(`/placements/${id}/abschluss/rueckmeldung-versicherer`, { method: 'POST', body: JSON.stringify(body) }),
  },

  // P2 Placement Slice 11 — the Ü7 Offer-Score weights (Regelwerk admin)
  offerScore: {
    /** GET /offer-score/gewichte — the tenant weights + the code default. */
    getGewichte: (): Promise<OfferScoreGewichteResponse> => apiFetch<OfferScoreGewichteResponse>(`/offer-score/gewichte`),

    /** PUT /offer-score/gewichte — set the tenant weights (admin). */
    putGewichte: (body: OfferScoreGewichtePutBody): Promise<OfferScoreGewichteResponse> =>
      apiFetch<OfferScoreGewichteResponse>(`/offer-score/gewichte`, { method: 'PUT', body: JSON.stringify(body) }),

    /** POST /offer-score/gewichte/reset — reset to the code default (admin). */
    resetGewichte: (): Promise<OfferScoreGewichteResponse> =>
      apiFetch<OfferScoreGewichteResponse>(`/offer-score/gewichte/reset`, { method: 'POST' }),
  },

  /**
   * P6-S7: the retention routes of a customer (tenant.manage). Each answers the retention read model.
   * POST/DELETE /customers/:t/:id/legal-hold, POST .../loeschung-freigeben | loeschung-ablehnen | loeschvorschlag-erneut.
   */
  retention: {
    setLegalHold: (subjectType: 'person' | 'firma', subjectId: string, reason: string): Promise<CustomerRetentionDto> =>
      apiFetch<CustomerRetentionDto>(`/customers/${subjectType}/${subjectId}/legal-hold`, { method: 'POST', body: JSON.stringify({ reason }) }),
    liftLegalHold: (subjectType: 'person' | 'firma', subjectId: string): Promise<CustomerRetentionDto> =>
      apiFetch<CustomerRetentionDto>(`/customers/${subjectType}/${subjectId}/legal-hold`, { method: 'DELETE' }),
    approve: (subjectType: 'person' | 'firma', subjectId: string, confirmation: string): Promise<CustomerRetentionDto> =>
      apiFetch<CustomerRetentionDto>(`/customers/${subjectType}/${subjectId}/loeschung-freigeben`, { method: 'POST', body: JSON.stringify({ confirmation }) }),
    decline: (subjectType: 'person' | 'firma', subjectId: string, reason: string): Promise<CustomerRetentionDto> =>
      apiFetch<CustomerRetentionDto>(`/customers/${subjectType}/${subjectId}/loeschung-ablehnen`, { method: 'POST', body: JSON.stringify({ reason }) }),
    repropose: (subjectType: 'person' | 'firma', subjectId: string): Promise<CustomerRetentionDto> =>
      apiFetch<CustomerRetentionDto>(`/customers/${subjectType}/${subjectId}/loeschvorschlag-erneut`, { method: 'POST' }),
  },

  customerStatus: {
    /**
     * GET /customer-status/:subjectType/:subjectId (Story 6.1).
     *
     * The current status PLUS the "(in Verarbeitung)" overlay: inVerarbeitung,
     * targetStatus and the API-computed displayStatus. Read by the Kunde detail so the
     * broker SEES "provisorisch (in Verarbeitung)" while an Analysemandat is out for
     * signature (#6). No mutation, no audit.
     */
    get: (
      subjectType: 'person' | 'firma',
      subjectId: string,
    ): Promise<CustomerStatusResponse> =>
      apiFetch<CustomerStatusResponse>(`/customer-status/${subjectType}/${subjectId}`),

    /**
     * PUT /customer-status/:subjectType/:subjectId/transition (E1).
     *
     * The everyday path, open to any user holding 'customer_status.transition':
     * the api's legal transition table applies, so an illegal target answers 400.
     */
    transition: (
      subjectType: 'person' | 'firma',
      subjectId: string,
      toStatus: CustomerStatusValue,
    ): Promise<CustomerStatusResponse> =>
      apiFetch<CustomerStatusResponse>(`/customer-status/${subjectType}/${subjectId}/transition`, {
        method: 'PUT',
        body: JSON.stringify({ toStatus }),
      }),

    /**
     * PUT /customer-status/:subjectType/:subjectId: set the status (E1).
     *
     * This is the api's manual-override route, the only status write endpoint
     * that exists: it needs the 'customer_status.override' permission and it
     * bypasses the legal transition table. It still refuses any change on an
     * inaktiv subject (422, schreibgeschützt).
     */
    override: (
      subjectType: 'person' | 'firma',
      subjectId: string,
      toStatus: CustomerStatusValue,
    ): Promise<CustomerStatusResponse> =>
      apiFetch<CustomerStatusResponse>(`/customer-status/${subjectType}/${subjectId}`, {
        method: 'PUT',
        body: JSON.stringify({ toStatus }),
      }),
  },

  vorgaenge: {
    /**
     * GET /vorgaenge/board (Story 6.4) — the Versand-/Eingangskontrolle board.
     *
     * Per-customer when subjectType + subjectId are set. Each row carries the
     * API-computed lifecycle: displayStatus ("versandt" / "erinnert" / "überfällig" /
     * "eingegangen" / …), mahnstandLabel, docKind and sentAt. Read by the Kunde detail
     * so the broker SEES the mandate Vorgang lifecycle on the Übersicht (#6).
     */
    board: (params: {
      subjectType: 'person' | 'firma';
      subjectId: string;
      limit?: number;
    }): Promise<VorgangBoardListResponse> => {
      const qs = new URLSearchParams();
      qs.set('subjectType', params.subjectType);
      qs.set('subjectId', params.subjectId);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      return apiFetch<VorgangBoardListResponse>(`/vorgaenge/board?${qs.toString()}`);
    },

    /** P4-S4: GET /vorgaenge/:id/pruefbericht — the consolidated Kontrollen-Prüfbericht (vorgang.read). */
    pruefbericht: (vorgangId: string): Promise<PruefberichtDto> =>
      apiFetch<PruefberichtDto>(`/vorgaenge/${encodeURIComponent(vorgangId)}/pruefbericht`),

    /** P4-S4: POST /vorgaenge/:id/pruefbericht/entscheid — the broker decision (mandate.eingangskontrolle). */
    entscheid: (vorgangId: string, body: PruefberichtEntscheidBody): Promise<PruefberichtDto> =>
      apiFetch<PruefberichtDto>(`/vorgaenge/${encodeURIComponent(vorgangId)}/pruefbericht/entscheid`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /**
     * The signature image itself, fetched with the session's bearer (an <img src> would carry no
     * credentials). The caller shows it through an object URL.
     */
    signatureImage: (vorgangId: string, personId: string, which: 'reference' | 'returned'): Promise<Blob> =>
      apiFetchBlob(`/vorgaenge/${encodeURIComponent(vorgangId)}/pruefbericht/signatures/${encodeURIComponent(personId)}/${which}`),
  },

  carriers: {
    /** GET /carriers — Versicherer list (P2 hardening 02). Optional filters. Returns { items }. */
    list: (params: { status?: string; q?: string; has_policies?: boolean } = {}): Promise<CarrierListResponseDto> => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.q) qs.set('q', params.q);
      if (params.has_policies !== undefined) qs.set('has_policies', String(params.has_policies));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<CarrierListResponseDto>(`/carriers${suffix}`);
    },

    /** GET /carriers/:carrierUid — the Versicherer detail (Stammdaten + KPIs + linked counts). */
    get: (carrierUid: string): Promise<VersichererDetailDto> =>
      apiFetch<VersichererDetailDto>(`/carriers/${carrierUid}`),

    /** POST /carriers — create a tenant Versicherer by UID (`crm.write`). 409 → ApiError. */
    create: (body: CreateVersichererBody): Promise<VersichererDetailDto> =>
      apiFetch<VersichererDetailDto>('/carriers', { method: 'POST', body: JSON.stringify(body) }),

    /** PATCH /carriers/:carrierUid — merge-patch the tenant Stammdaten (`crm.write`). */
    update: (carrierUid: string, body: UpdateVersichererBody): Promise<VersichererDetailDto> =>
      apiFetch<VersichererDetailDto>(`/carriers/${carrierUid}`, { method: 'PATCH', body: JSON.stringify(body) }),

    /** POST /carriers/:carrierUid/enrich — async Zefix re-fetch. 202; 429/422 surface as ApiError. */
    enrich: (carrierUid: string): Promise<EnrichVersichererResponse> =>
      apiFetch<EnrichVersichererResponse>(`/carriers/${carrierUid}/enrich`, { method: 'POST', body: JSON.stringify({}) }),

    /** GET /carriers/:carrierUid/claims — linked Schäden. */
    claims: (carrierUid: string): Promise<VersichererListResponse<VersichererClaimRowDto>> =>
      apiFetch(`/carriers/${carrierUid}/claims`),

    /** GET /carriers/:carrierUid/submissions — linked Ausschreibungen. */
    submissions: (carrierUid: string): Promise<VersichererListResponse<VersichererSubmissionRowDto>> =>
      apiFetch(`/carriers/${carrierUid}/submissions`),

    /** GET /carriers/:carrierUid/kommunikation — linked Kommunikation. */
    kommunikation: (carrierUid: string): Promise<VersichererListResponse<VersichererKommRowDto>> =>
      apiFetch(`/carriers/${carrierUid}/kommunikation`),

    /**
     * PATCH /carriers/:carrierUid/placement-settings — maintain THIS firm's OWN per-insurer
     * placement contacts (Ausschreibungs-E-Mail, Antragsformular template) on the tenant
     * `versicherer` overlay (`carrier.manage`; PR#43-B1). Merge-patch: absent keeps, null clears
     * (= back to the global default). logo_url is NOT accepted (global, platform admin only).
     */
    patchPlacementSettings: (
      carrierUid: string,
      body: CarrierPlacementSettingsBody,
    ): Promise<CarrierPlacementContactsDto> =>
      apiFetch(`/carriers/${carrierUid}/placement-settings`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    /**
     * GET /carriers/:carrierUid/notification-addresses — F1 (Foundation Slice B): this firm's live
     * «Benachrichtigungen» addresses per (Prozess x Kategorie) + the Hauptadresse fallback + the
     * read-only Brokercode VU (`policy.read`).
     */
    notificationAddresses: (carrierUid: string): Promise<InsurerNotificationAddressesDto> =>
      apiFetch(`/carriers/${carrierUid}/notification-addresses`),
    // The firm's flag «nur manuelle Meldung» (carrier.manage).
    registrationSettings: (carrierUid: string, body: CarrierRegistrationSettingsBody): Promise<CarrierRegistrationSettingsDto> =>
      apiFetch<CarrierRegistrationSettingsDto>(`/carriers/${carrierUid}/registration-settings`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    // P4-S7 (step 23): the documents filed at this carrier.
    documents: (carrierUid: string): Promise<CarrierDocumentsResponse> =>
      apiFetch<CarrierDocumentsResponse>(`/carriers/${carrierUid}/documents`),
    /** GET /carriers/:uid/mandate-registrations: the customers registered at this insurer (crm.read). */
    mandateRegistrations: (carrierUid: string): Promise<CarrierRegistrationsDto> =>
      apiFetch<CarrierRegistrationsDto>(`/carriers/${carrierUid}/mandate-registrations`),
  },

  invoices: {
    /** GET /invoices — Rechnungen list (Story V10-J2). Returns { items }. */
    list: (params: InvoiceListParams = {}): Promise<InvoiceListResponseDto> => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.subject_type) qs.set('subject_type', params.subject_type);
      if (params.subject_id) qs.set('subject_id', params.subject_id);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<InvoiceListResponseDto>(`/invoices${query}`);
    },

    /** GET /invoices/:id/check-detail — Rechnungskontrolle peek facts (Story UI-U4). */
    checkDetail: (id: string): Promise<InvoiceCheckDetailDto> =>
      apiFetch<InvoiceCheckDetailDto>(`/invoices/${id}/check-detail`),
  },

  /**
   * Rechnungskontrolle decision surface (Epic 10.6/10.7 — wired in Story UI-U4).
   * All human decisions go through the EXISTING E10 endpoints — no invented writes.
   */
  adminInvoiceCheck: {
    /** GET /admin/invoice-check/:id/history — Verlauf timeline (10.7). */
    history: (id: string): Promise<InvoiceHistoryResult> =>
      apiFetch<InvoiceHistoryResult>(`/admin/invoice-check/${id}/history`),

    /**
     * POST /admin/invoice-check/:id/override — Human Override (10.6).
     * reason ≥10 chars is MANDATORY (server 400 otherwise); append-only new row.
     */
    override: (id: string, body: InvoiceOverrideBody): Promise<InvoiceOverrideResult> =>
      apiFetch<InvoiceOverrideResult>(`/admin/invoice-check/${id}/override`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** POST /admin/invoice-check/:id/handle — run the Kap.-8 branch (K0/K1 auto-release etc.). */
    handle: (id: string): Promise<{ outcome?: string } & Record<string, unknown>> =>
      apiFetch<{ outcome?: string } & Record<string, unknown>>(
        `/admin/invoice-check/${id}/handle`,
        { method: 'POST' },
      ),

    /** POST /admin/invoice-check/:id/orchestrate — full idempotent chain = "Zuordnung bestätigen". */
    orchestrate: (id: string): Promise<Record<string, unknown>> =>
      apiFetch<Record<string, unknown>>(`/admin/invoice-check/${id}/orchestrate`, {
        method: 'POST',
      }),
  },

  policies: {
    /**
     * GET /policies: policy list w/ optional subject/carrier filters (Story V10-J2).
     *
     * Rows are `PolicyListItemDto` (the PolicyDto superset the api actually serves,
     * Story GAP-B4): `premium_gross` of the open version, `betreuer_name`,
     * `sparte_label`, `risikostufe`. Do not narrow to `PolicyDto[]`: the premium
     * column depends on the superset fields.
     */
    list: (params: PolicyListParams = {}): Promise<PolicyListItemDto[]> => {
      const qs = new URLSearchParams();
      if (params.subject_id) qs.set('subject_id', params.subject_id);
      if (params.line_of_business) qs.set('line_of_business', params.line_of_business);
      if (params.carrier_uid) qs.set('carrier_uid', params.carrier_uid);
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<PolicyListItemDto[]>(`/policies${query}`);
    },

    /** GET /policies/:id — current policy state. */
    get: (id: string): Promise<PolicyDto> => apiFetch<PolicyDto>(`/policies/${id}`),

    /**
     * POST /policies/:id/registration-ausnahme (policy.write): set a broker exception on the policy's
     * registration ({ reason }) or lift it ({ lift: true, liftReasonCode }, which also needs
     * mandate.eingangskontrolle).
     */
    registrationAusnahme: (policyId: string, body: PolicyRegistrationAusnahmeBody): Promise<PolicyRegistrationDto> =>
      apiFetch<PolicyRegistrationDto>(`/policies/${policyId}/registration-ausnahme`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /**
     * PATCH /policies/:id — manual policy Stammdaten edit (Story P2-3/P2-4, WS4 §4.6,
     * `policy.write`): `bezeichnung`, `praemienbefreiung` (+ `_seit`), `vermittler_person_id`,
     * `bemerkungen`. Send only the changed keys — all-optional body. Returns the refreshed
     * PolicyDto. 403 without `policy.write`; 400 if `praemienbefreiung_seit` is sent without a
     * resolved `praemienbefreiung=true`.
     */
    update: (id: string, patch: UpdatePolicyDto): Promise<PolicyDto> =>
      apiFetch<PolicyDto>(`/policies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    /**
     * POST /policies/:id/activate: promote a `draft` policy ("Sistiert") to `active`
     * (`policy.write`). No body. Returns the refreshed PolicyDto. 409 `policy_not_draft`
     * when the policy is in any other lifecycle state.
     */
    activate: (id: string): Promise<PolicyDto> =>
      apiFetch<PolicyDto>(`/policies/${id}/activate`, { method: 'POST' }),

    /** GET /policies/:id/registration-preview: whether activating reports the Brokermandat (P5-S7). */
    registrationPreview: (id: string): Promise<PolicyRegistrationPreviewDto> =>
      apiFetch<PolicyRegistrationPreviewDto>(`/policies/${id}/registration-preview`),

    /** POST /tasks/:taskId/vererbung/erneut-pruefen: the Vererbung again for a «Vererbung prüfen» task (P5-S7). */
    vererbungErneutPruefen: (taskId: string): Promise<VererbungRetryResultDto> =>
      apiFetch<VererbungRetryResultDto>(`/tasks/${taskId}/vererbung/erneut-pruefen`, { method: 'POST' }),

    /** GET /policies/:id/versions — full version history (version_nr ASC). */
    versions: (id: string): Promise<PolicyVersionDto[]> =>
      apiFetch<PolicyVersionDto[]>(`/policies/${id}/versions`),

    // ── P2 Placement Slice 2 — Police-Ebene «Risiko & Deckung» ─────────────────
    /** GET the branchenspezifische Ist-Attribute (per insured object) + the 91000 gated mirror. */
    getRiskAttributes: (id: string): Promise<PoliceRisikoResponse> =>
      apiFetch<PoliceRisikoResponse>(`/policies/${id}/risk-attributes`),

    /** PATCH one object's Ist-Attribute set (merge-patch; NULL ref = the policy-level set). */
    patchRiskAttributes: (id: string, body: PoliceRisikoPatchBody): Promise<PoliceRisikoResponse> =>
      apiFetch<PoliceRisikoResponse>(`/policies/${id}/risk-attributes`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    /** DELETE (soft-remove) one insured-object Ist-Attribute set by its row id. */
    removeRiskObject: (id: string, objectId: string): Promise<void> =>
      apiFetch<void>(`/policies/${id}/risk-attributes/objects/${objectId}`, { method: 'DELETE' }),

    /** GET the Soll-Deckung & Gap (catalog rules merged with the stored assessment + Ampel). */
    getTargetCoverage: (id: string): Promise<DeckungResponse> =>
      apiFetch<DeckungResponse>(`/policies/${id}/target-coverage`),

    /** PUT one Soll-Deckung rule (target/actual/status/notiz). */
    putTargetCoverage: (id: string, ruleKey: string, body: DeckungPutBody): Promise<DeckungItem> =>
      apiFetch<DeckungItem>(`/policies/${id}/target-coverage/${ruleKey}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

    // ── P2 Placement Slice 6 — Schadenverlauf / Schadenauskunft ──────────────────
    /** GET the Police Schadenverlauf (current + Verlauf + provider info). */
    getClaimsHistory: (id: string): Promise<PolicySchadenverlaufResponse> =>
      apiFetch<PolicySchadenverlaufResponse>(`/policies/${id}/claims-history`),

    /** POST the manual «Schadenverlauf neu einholen»; returns the new current entry. */
    retrieveClaimsHistory: (id: string): Promise<SchadenverlaufEintrag> =>
      apiFetch<SchadenverlaufEintrag>(`/policies/${id}/claims-history/retrieve`, { method: 'POST' }),

    /** GET one Schadenverlauf entry's raw provider payload (the Slice-8 Beilage). */
    getClaimsHistoryRohdaten: (id: string, entryId: string): Promise<SchadenverlaufRohdatenResponse> =>
      apiFetch<SchadenverlaufRohdatenResponse>(`/policies/${id}/claims-history/${entryId}/rohdaten`),

    /** GET /policies/:id/avb-links?active=true — the linked AVBs (Wave global-avb). */
    avbLinks: (id: string): Promise<AvbLinkItem[]> =>
      apiFetch<AvbLinkItem[]>(`/policies/${id}/avb-links?active=true`),

    /**
     * POST /policies/:id/avb-links/auto-match — run the AVB matching engine for the
     * policy's current version (carrier + Sparte + validity). `dryRun` previews the
     * matches without writing; otherwise the hits are linked (idempotent).
     */
    avbAutoMatch: (id: string, dryRun = false): Promise<AvbAutoMatchResult> =>
      apiFetch<AvbAutoMatchResult>(
        `/policies/${id}/avb-links/auto-match${dryRun ? '?dry_run=true' : ''}`,
        { method: 'POST' },
      ),

    /**
     * POST /policies/upload — manual policy PDF intake (Story 9.5, `document.upload`),
     * multipart. Runs Docpier extraction → classifies → creates/versions the Police and
     * links it to the holder (all async via the queue; the 202 response only carries the
     * `document_id`/`job_id`, not the resulting policy id). No Content-Type header — the
     * browser sets the multipart boundary.
     *
     * @param opts.holderId - the VN (firma or person id) the Police is held by (required).
     * @param opts.docKind  - 'police_neu' (new policy) | 'police_mutation' (mutation).
     */
    upload: async (
      file: File | Blob,
      opts: { holderId: string; docKind: 'police_neu' | 'police_mutation'; filename?: string },
    ): Promise<PolicyUploadResult> => {
      const form = new FormData();
      form.append('file', file, opts.filename ?? 'police.pdf');
      form.append('doc_kind', opts.docKind);
      form.append('holder_id', opts.holderId);
      const res = await authedFetch(`${API_BASE_URL}/policies/upload`, { method: 'POST', body: form });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `HTTP ${res.status}`);
      }
      return res.json() as Promise<PolicyUploadResult>;
    },
  },

  /**
   * Posteingang — the Eingangsliste over all four channels (epic WS-B, Detailkonzept 8.1).
   * The wire contract lives in `@brokly/shared/posteingang`. Both routes are live on dev
   * since 2026-09-08 (B1 step 3, api rev 0000088), JWT only, no permission key.
   * `list` is keyset-paged: page while `page.next_cursor` is non-null, never stop on a
   * short page - the visibility gate drops rows after the limit is applied.
   */
  posteingang: {
    /** GET /posteingang — paged Eingaenge with the derived status + counts. */
    list: (
      params: import('@brokly/shared').PosteingangListParams = {},
    ): Promise<import('@brokly/shared').PosteingangListResponse> => {
      const qs = new URLSearchParams();
      if (params.state) qs.set('state', params.state);
      if (params.kanal) qs.set('kanal', params.kanal);
      if (params.archived) qs.set('archived', 'true');
      if (params.private_senders) qs.set('private_senders', 'true');
      if (params.q) qs.set('q', params.q);
      if (params.attachment_hits) qs.set('attachment_hits', params.attachment_hits.join(','));
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<import('@brokly/shared').PosteingangListResponse>(`/posteingang${query}`);
    },
    /** GET /posteingang/counts — the open-count badge. */
    counts: (): Promise<import('@brokly/shared').PosteingangCounts> =>
      apiFetch<import('@brokly/shared').PosteingangCounts>('/posteingang/counts'),

    /**
     * P2 hardening 04: per-user "Absender fuer mich privat markieren". Distinct from the
     * per-mail Privat-Markierung — this hides a SENDER from only the caller's Posteingang.
     */
    senderPrivate: {
      /** GET /posteingang/sender-private — the caller's active rows. */
      list: (): Promise<import('@brokly/shared').SenderPrivateListResponse> =>
        apiFetch<import('@brokly/shared').SenderPrivateListResponse>('/posteingang/sender-private'),
      /** GET /posteingang/sender-private/preview?address= — the dialog state. */
      preview: (address: string): Promise<import('@brokly/shared').SenderPrivatePreviewResponse> =>
        apiFetch<import('@brokly/shared').SenderPrivatePreviewResponse>(
          `/posteingang/sender-private/preview?address=${encodeURIComponent(address)}`,
        ),
      /** POST /posteingang/sender-private — mark a sender private for the caller. */
      mark: (body: import('@brokly/shared').SenderPrivateMarkRequest): Promise<import('@brokly/shared').SenderPrivateDto> =>
        apiFetch<import('@brokly/shared').SenderPrivateDto>('/posteingang/sender-private', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      /** POST /posteingang/sender-private/:id/unmark — stop hiding a sender for the caller. */
      unmark: (
        id: string,
        body: import('@brokly/shared').SenderPrivateUnmarkRequest,
      ): Promise<import('@brokly/shared').SenderPrivateDto> =>
        apiFetch<import('@brokly/shared').SenderPrivateDto>(`/posteingang/sender-private/${id}/unmark`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
    },

    /**
     * GET /posteingang/:commId/attachments — the stored files of one Eingang.
     * Server-gated: hidden mail 404s, below tier full the list is empty (D3).
     */
    attachments: (commId: string): Promise<{ data: AttachmentView[] }> =>
      apiFetch<{ data: AttachmentView[] }>(`/posteingang/${commId}/attachments`),

    /** GET /posteingang/:commId/activity — the Aktivitätenhistorie (all tasks of the Eingang). */
    activity: (commId: string): Promise<import('@brokly/shared').PosteingangActivityResponse> =>
      apiFetch<import('@brokly/shared').PosteingangActivityResponse>(`/posteingang/${commId}/activity`),

    /** GET /posteingang/:commId/policenkontrolle — the Policenkontrolle-Befund(e) (P2 hardening 05). */
    policenkontrolle: (commId: string): Promise<import('@brokly/shared').PosteingangPolicenkontrolleResponse> =>
      apiFetch<import('@brokly/shared').PosteingangPolicenkontrolleResponse>(`/posteingang/${commId}/policenkontrolle`),

    /** GET /posteingang/:commId/sent/:sentId — one reply sent from this Eingang. */
    sentMail: (commId: string, sentId: string): Promise<import('@brokly/shared').PosteingangSentMail> =>
      apiFetch<import('@brokly/shared').PosteingangSentMail>(`/posteingang/${commId}/sent/${sentId}`),

    /** GET /posteingang/:commId/status — the stored status of one Eingang (offen | erledigt). */
    getStatus: (
      commId: string,
    ): Promise<{ id: string; status: import('@brokly/shared').PosteingangState; changed_at: string | null }> =>
      apiFetch(`/posteingang/${commId}/status`),

    /** PATCH /posteingang/:commId/status — set it (one click, audited server-side). */
    setStatus: (
      commId: string,
      status: import('@brokly/shared').PosteingangState,
    ): Promise<import('@brokly/shared').PosteingangStatusResponse> =>
      apiFetch<import('@brokly/shared').PosteingangStatusResponse>(`/posteingang/${commId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),

    /** POST /posteingang/:commId/read — mark one Eingang read. Idempotent. */
    read: (commId: string): Promise<{ id: string; read_at: string }> =>
      apiFetch<{ id: string; read_at: string }>(`/posteingang/${commId}/read`, { method: 'POST' }),

    /**
     * POST /posteingang/:commId/release — the Gesamtfreigabe.
     * Two 409s the UI must act on, not just report: `release_attachments_warning` (show
     * `data`, then repeat with attachments_warning_acknowledged) and
     * `release_channel_unsupported` (Portal/Briefpost have no adapter).
     */
    release: (
      commId: string,
      body: IntakeReleaseRequest = {},
    ): Promise<IntakeReleaseResponse> =>
      apiFetch<IntakeReleaseResponse>(`/posteingang/${commId}/release`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  communications: {
    /** GET /communications — three-bucket overview (Story 7.2; wired in V10-J2). */
    list: (params: CommOverviewParams): Promise<CommOverviewListResponse> => {
      const qs = new URLSearchParams();
      qs.set('bucket', params.bucket);
      if (params.subject_type) qs.set('subject_type', params.subject_type);
      if (params.subject_id) qs.set('subject_id', params.subject_id);
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      return apiFetch<CommOverviewListResponse>(`/communications?${qs.toString()}`);
    },
    /**
     * GET /communications/:commId/content — the viewer's tier-projected body of ANY
     * communication, task-linked or not (Fix C). At 'full' both `body` (plain) and
     * `body_html` are populated; reduced tiers return nulls (never leak across tiers).
     */
    getContent: (commId: string): Promise<CommunicationContentDto> =>
      apiFetch<CommunicationContentDto>(`/communications/${commId}/content`),
    /** GET one ON-DEMAND comm attachment as a Blob (U5, issue 31; save via saveBlob).
     *  The provider attachment id is not a UUID, hence encoded. Below tier full: 404. */
    downloadAttachment: (commId: string, attachmentId: string): Promise<Blob> =>
      apiFetchBlob(`/communications/${commId}/attachments/${encodeURIComponent(attachmentId)}/download`),
    /** POST /communications/vn-messages — send a broker→VN free-text message (Wave W5). */
    sendVnMessage: (body: SendVnMessageBody): Promise<SendVnMessageResult> =>
      apiFetch<SendVnMessageResult>('/communications/vn-messages', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /**
     * POST /communications/:commId/dismiss — persist the Eingang "Ablehnen"/"Archivieren"
     * dismissal server-side (migration 0102). Fixes the dismiss-only-in-a-React-state-Set
     * gap: without this call the message reappeared on reload/re-login.
     */
    dismiss: (commId: string, body: DismissCommunicationBody = {}): Promise<CommunicationDismissResult> =>
      apiFetch<CommunicationDismissResult>(`/communications/${commId}/dismiss`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** POST /communications/:commId/undismiss — undo a dismiss (migration 0102). */
    undismiss: (commId: string): Promise<CommunicationDismissResult> =>
      apiFetch<CommunicationDismissResult>(`/communications/${commId}/undismiss`, {
        method: 'POST',
      }),

    // ── Bereich 1: nachträgliche Privat-Markierung (Berechtigungskonzept V3.0 §2.2) ──

    /**
     * POST /communications/:commId/mark-private — only the sender or an internal To/CC
     * recipient may call this (OR-principle, one suffices, no admin override). Once
     * marked, the comm disappears (incl. metadata) for every other internal user.
     */
    markPrivate: (commId: string): Promise<CommunicationPrivateResult> =>
      apiFetch<CommunicationPrivateResult>(`/communications/${commId}/mark-private`, {
        method: 'POST',
      }),

    /** POST /communications/:commId/unmark-private — reverse mark-private (same gate). */
    unmarkPrivate: (commId: string): Promise<CommunicationPrivateResult> =>
      apiFetch<CommunicationPrivateResult>(`/communications/${commId}/unmark-private`, {
        method: 'POST',
      }),

    /**
     * POST /communications/:commId/mark-thread-private — P4: mark the WHOLE e-mail thread
     * private (past + current members + future mail). Same OR-principle gate as single-mail
     * (sender or internal To/CC of the thread; one suffices, no admin override).
     */
    markThreadPrivate: (commId: string): Promise<CommThreadPrivateResult> =>
      apiFetch<CommThreadPrivateResult>(`/communications/${commId}/mark-thread-private`, {
        method: 'POST',
      }),

    /** POST /communications/:commId/unmark-thread-private — reverse mark-thread-private (same gate). */
    unmarkThreadPrivate: (commId: string): Promise<CommThreadPrivateResult> =>
      apiFetch<CommThreadPrivateResult>(`/communications/${commId}/unmark-thread-private`, {
        method: 'POST',
      }),

    // ── Freigabe / access-workflow (Story 7.3 + PART C1/C2) ──────────────────

    /** POST /communications/:commId/access-requests — request Vollzugriff (W1). */
    issueAccessRequest: (
      commId: string,
      body: IssueAccessRequestBody = {},
    ): Promise<AccessRequestItem> =>
      apiFetch<AccessRequestItem>(`/communications/${commId}/access-requests`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** GET /communications/:commId/access-requests — requests on one comm (own, or all if participant). */
    listRequestsForComm: (commId: string): Promise<AccessRequestItem[]> =>
      apiFetch<AccessRequestItem[]>(`/communications/${commId}/access-requests`),

    /** GET /communications/access-requests?role=decider — the decider inbox (W2). */
    listAccessRequests: (params: ListAccessRequestsParams = {}): Promise<AccessRequestItem[]> => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.role) qs.set('role', params.role);
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<AccessRequestItem[]>(`/communications/access-requests${query}`);
    },

    /** POST /communications/access-requests/:requestId/decision — grant or deny (W2). */
    decideAccessRequest: (
      requestId: string,
      body: DecideAccessRequestBody,
    ): Promise<AccessRequestItem> =>
      apiFetch<AccessRequestItem>(`/communications/access-requests/${requestId}/decision`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** GET /communications/access-grants — standing grants for a subject + own (W3). */
    listAccessGrants: (params: ListAccessGrantsParams = {}): Promise<ManagedAccessGrantItem[]> => {
      const qs = new URLSearchParams();
      if (params.subject_type) qs.set('subject_type', params.subject_type);
      if (params.subject_id) qs.set('subject_id', params.subject_id);
      if (params.user_id) qs.set('user_id', params.user_id);
      if (params.domain) qs.set('domain', params.domain);
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<ManagedAccessGrantItem[]>(`/communications/access-grants${query}`);
    },

    /** POST /communications/access-grants — create a Managed-Access standing grant (W3). */
    grantManagedAccess: (body: GrantManagedAccessBody): Promise<ManagedAccessGrantItem> =>
      apiFetch<ManagedAccessGrantItem>('/communications/access-grants', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** DELETE /communications/access-grants/:grantId — soft-revoke a standing grant (W3). */
    revokeAccessGrant: (grantId: string): Promise<ManagedAccessGrantItem> =>
      apiFetch<ManagedAccessGrantItem>(`/communications/access-grants/${grantId}`, {
        method: 'DELETE',
      }),
  },

  relationships: {
    /** GET /persons|firms/:id/relationships — flat relationship feed (Story V10-J2 wiring). */
    listForSubject: (
      subjectType: 'person' | 'firma',
      id: string,
    ): Promise<RelationshipFeedItem[]> =>
      apiFetch<RelationshipFeedItem[]>(
        `/${subjectType === 'person' ? 'persons' : 'firms'}/${id}/relationships`,
      ),
  },

  beziehungsnetz: {
    /** GET /beziehungsnetz/:subjectType/:id — the Kunde-Struktur ego graph (Story UI-U8). */
    get: (subjectType: 'person' | 'firma', id: string): Promise<BeziehungsnetzDto> =>
      apiFetch<BeziehungsnetzDto>(`/beziehungsnetz/${subjectType}/${id}`),
  },

  commReferences: {
    /** GET /communications/:commId/references — manual + auto references (Story UI-U8). */
    list: (commId: string): Promise<CommReferenceDto[]> =>
      apiFetch<CommReferenceDto[]>(`/communications/${commId}/references`),
    /** POST /communications/:commId/references — hand-file a manual reference. */
    create: (commId: string, body: CreateCommReferenceInput): Promise<CommReferenceDto> =>
      apiFetch<CommReferenceDto>(`/communications/${commId}/references`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    /** DELETE /communications/:commId/references/:refId — remove a MANUAL reference. */
    remove: (commId: string, refId: string): Promise<void> =>
      apiFetch<void>(`/communications/${commId}/references/${refId}`, { method: 'DELETE' }),
  },

  claims: {
    /** GET /claims?subject_type=&subject_id= — the Schäden of one customer (Referenzieren picker). */
    pick: (subjectType: 'person' | 'firma', subjectId: string): Promise<ClaimPickerItemDto[]> => {
      const qs = new URLSearchParams({ subject_type: subjectType, subject_id: subjectId });
      return apiFetch<ClaimPickerItemDto[]>(`/claims?${qs.toString()}`);
    },
  },

  renewals: {
    /** GET /renewals/board — Renewals board (Story V10-J wires Story R2). */
    board: (bucket?: RenewalBucket): Promise<RenewalBoardResponseDto> =>
      apiFetch<RenewalBoardResponseDto>(`/renewals/board${bucket ? `?bucket=${bucket}` : ''}`),

    /** GET /renewals/heute — Heute/Briefing aggregation (Story V10-J wires Story R2). */
    heute: (): Promise<RenewalHeuteDto> => apiFetch<RenewalHeuteDto>('/renewals/heute'),
  },

  search: {
    /**
     * GET /search — standalone tenant-wide hybrid (keyword + semantic) search
     * (Story V10-K). Returns { q, items, counts }. NOT the Brok agent.
     *
     * DMS M10/M12: `scope: 'policy'` restricts the response to the Policies-page
     * "Semantisch" mode (only 'police'-typed hits). ea/fix-search: `scope: 'document'`
     * does the same for the Ablage-page Volltext/Semantisch toggle (only 'dokument'-typed
     * hits, routed at `/ablage/:id`). `includeInactive` opts the document-content/semantic
     * legs into matching historized documents too. `opts` also accepts a bare number for
     * the legacy positional-`limit` callers.
     */
    query: (
      q: string,
      opts: number | { limit?: number; scope?: 'policy' | 'document' | 'posteingang'; includeInactive?: boolean } = {},
    ): Promise<SearchResponseDto> => {
      const { limit, scope, includeInactive } = typeof opts === 'number' ? { limit: opts, scope: undefined, includeInactive: undefined } : opts;
      const qs = new URLSearchParams({ q });
      if (limit !== undefined) qs.set('limit', String(limit));
      if (scope) qs.set('scope', scope);
      if (includeInactive) qs.set('include_inactive', 'true');
      return apiFetch<SearchResponseDto>(`/search?${qs.toString()}`);
    },
  },

  agent: {
    /** GET /agent/conversation — replay the single persisted thread. */
    getConversation: (): Promise<{ messages: AgentMessageDto[] }> =>
      apiFetch<{ messages: AgentMessageDto[] }>('/agent/conversation'),
    /**
     * POST /agent/conversation/new, start a fresh chat ("Neuer Chat", FIX 2).
     * Advances the context-reset watermark + clears the active attachment server-side;
     * the caller then invalidates qk.agentConversation() and clears the local transcript.
     */
    resetConversation: (): Promise<{ ok: true }> =>
      apiFetch<{ ok: true }>('/agent/conversation/new', { method: 'POST' }),
  },

  tenant: {
    /** GET /tenant/self — the caller's firm name + pricing-tier label (Story UI-U2). */
    self: (): Promise<TenantSelfResponse> => apiFetch<TenantSelfResponse>('/tenant/self'),
  },

  statistics: {
    /** GET /statistics/portfolio — the Portfolio analytics aggregate (Story UI-U7). */
    portfolio: (): Promise<PortfolioStatisticsDto> =>
      apiFetch<PortfolioStatisticsDto>('/statistics/portfolio'),
  },

  tasks: {
    /** GET /tasks/:id/uebergabe - the live checklist of the task «Übergabe offener Vorgänge» (404 for another task). */
    uebergabe: (taskId: string): Promise<UebergabeChecklistDto> =>
      apiFetch<UebergabeChecklistDto>(`/tasks/${encodeURIComponent(taskId)}/uebergabe`),
    /**
     * GET /tasks — worklist (keyset paginated).
     * Returns { data: TaskSummary[], page: { next_cursor, limit } }.
     */
    list: (params: TaskListParams = {}): Promise<TaskListResponse> => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.routing) qs.set('routing', params.routing);
      if (params.process) qs.set('process', params.process);
      if (params.assigned_to) qs.set('assigned_to', params.assigned_to);
      if (params.mine) qs.set('mine', 'true');
      if (params.comm_id) qs.set('comm_id', params.comm_id);
      if (params.q) qs.set('q', params.q);
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      if (params.include_total) qs.set('include_total', 'true');
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<TaskListResponse>(`/tasks${query}`);
    },

    /**
     * GET /tasks/queue — the caller's Funktionsgruppen group queue (Story V10-F
     * endpoint; thin client wrapper added in Story UI-U2 for the nav-count probe).
     * Same TaskListResponse envelope as GET /tasks.
     */
    queue: (params: Pick<TaskListParams, 'cursor' | 'limit'> = {}): Promise<TaskListResponse> => {
      const qs = new URLSearchParams();
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<TaskListResponse>(`/tasks/queue${query}`);
    },

    /**
     * POST /tasks — create a task manually ("Neue Aufgabe").
     * Returns the created row in the GET /tasks TaskSummary shape.
     */
    create: (body: CreateTaskInput): Promise<TaskSummary> =>
      apiFetch<TaskSummary>('/tasks', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /**
     * GET /tasks/:id — split-view read (task + comm + attachments + activity).
     */
    getSplitView: (id: string): Promise<SplitViewResponse> =>
      apiFetch<SplitViewResponse>(`/tasks/${id}`),

    /**
     * POST /tasks/:id/complete — mark task as done.
     */
    complete: (id: string, note?: string): Promise<TaskSummary> =>
      apiFetch<TaskSummary>(`/tasks/${id}/complete`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      }),

    /**
     * POST /tasks/:id/cancel — cancel a task.
     */
    cancel: (id: string, note?: string): Promise<TaskSummary> =>
      apiFetch<TaskSummary>(`/tasks/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      }),

    /**
     * POST /tasks/:id/reopen — reopen a completed or cancelled task.
     */
    reopen: (id: string, note?: string): Promise<TaskSummary> =>
      apiFetch<TaskSummary>(`/tasks/${id}/reopen`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      }),

    /**
     * POST /tasks/:id/in-progress — move task to in-progress.
     */
    inProgress: (id: string): Promise<TaskSummary> =>
      apiFetch<TaskSummary>(`/tasks/${id}/in-progress`, { method: 'POST' }),

    /**
     * POST /tasks/:id/assign — assign task to a user.
     */
    assign: (id: string, assigneeUserId: string): Promise<TaskSummary> =>
      apiFetch<TaskSummary>(`/tasks/${id}/assign`, {
        method: 'POST',
        body: JSON.stringify({ assignee_user_id: assigneeUserId }),
      }),

    /**
     * POST /tasks/:id/approve — the Einzelfreigabe. Sets meta.approval.staged and leaves
     * status untouched, so count `approval.staged`. Not `complete`: a staged task refuses
     * that with 409 task_release_required, the batch closes at the Gesamtfreigabe.
     */
    approve: (id: string, note?: string): Promise<TaskSummary> =>
      apiFetch<TaskSummary>(`/tasks/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      }),

    /**
     * GET /tasks/:id/attachments — the Versandanhaenge of one task, seeded on first read.
     * Use `deactivated_by_user`, not `deactivated_at`: the seed deactivates inline pictures.
     */
    attachments: (id: string): Promise<{ data: TaskAttachmentView[] }> =>
      apiFetch<{ data: TaskAttachmentView[] }>(`/tasks/${id}/attachments`),

    /**
     * PATCH /tasks/:id/attachments/:documentId — Versandkanal and/or is_active.
     * At least one field; 409 task_attachment_locked once the task is done or cancelled.
     */
    patchAttachment: (
      id: string,
      documentId: string,
      patch: TaskAttachmentPatch,
    ): Promise<TaskAttachmentView> =>
      apiFetch<TaskAttachmentView>(`/tasks/${id}/attachments/${documentId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    /**
     * GET /tasks/:id/draft — the per-Aufgabe Antwortentwurf (S3), generated on first read.
     * 404 = nothing to show (no comm, or the assist refused): say so, do not render blank.
     */
    draft: (id: string): Promise<TaskDraftView> =>
      apiFetch<TaskDraftView>(`/tasks/${id}/draft`),

    /**
     * PUT /tasks/:id/draft — save an edit (original AI text kept, edit audited).
     * Empty draft_text means "send nothing"; read first or it 404s task_draft_not_found.
     */
    putDraft: (id: string, body: TaskDraftPut): Promise<TaskDraftView> =>
      apiFetch<TaskDraftView>(`/tasks/${id}/draft`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

    /**
     * POST /tasks/bulk — apply one action to multiple tasks (AC3).
     * Per-item failures do not abort the batch.
     */
    bulk: (
      ids: string[],
      action: string,
      params?: { assignee_user_id?: string; note?: string },
    ): Promise<TaskBulkResult> =>
      apiFetch<TaskBulkResult>('/tasks/bulk', {
        method: 'POST',
        body: JSON.stringify({ ids, action, params }),
      }),
  },

  /**
   * Administration area (Story UI-U6) — the broker-tenant settings surface.
   * Every method wires an EXISTING admin endpoint (bearer + 401-refresh come free)
   * except the two NEW thin reads (effective-permissions, members).
   * Consumers render DEFENSIVELY: a 404 / absent field hides the element (AC10).
   */
  admin: {
    // ── Guard-awareness + members (NEW reads) ─────────────────────────────
    effectivePermissions: (): Promise<EffectivePermissionsResponse> =>
      apiFetch<EffectivePermissionsResponse>('/me/effective-permissions'),
    /** GET /me/effective-visibility — the caller's denied visibility keys (Bereich 3). */
    effectiveVisibility: (): Promise<EffectiveVisibilityResponse> =>
      apiFetch<EffectiveVisibilityResponse>('/me/effective-visibility'),
    members: (): Promise<AdminMembersResponse> => apiFetch<AdminMembersResponse>('/admin/members'),
    /** POST /admin/members/invite — invite a NEW broker employee (Fix 5). */
    inviteMember: (body: {
      email: string;
      displayName: string;
      roleId?: string;
    }): Promise<AdminInvitationDto> =>
      apiFetch<AdminInvitationDto>('/admin/members/invite', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    /** POST /admin/members/invitations/:id/resend — re-send a pending invite (Fix 5). */
    resendInvitation: (invitationId: string): Promise<AdminInvitationDto> =>
      apiFetch<AdminInvitationDto>(`/admin/members/invitations/${invitationId}/resend`, {
        method: 'POST',
      }),

    // ── Rollen & Rechte / Berechtigungsgruppen (Epic 12.6) ────────────────
    roles: (): Promise<{ roles: RoleDto[] }> => apiFetch<{ roles: RoleDto[] }>('/admin/roles'),
    rolesCatalog: (): Promise<CatalogResponseDto> =>
      apiFetch<CatalogResponseDto>('/admin/roles/catalog'),
    createRole: (body: {
      name: string;
      description?: string | null;
      kind?: string;
      permission_keys: string[];
      // WS1 §5.8: mark this role an Administrationsrolle (pure parametrization).
      ist_administrationsrolle?: boolean;
    }): Promise<RoleDto> =>
      apiFetch<RoleDto>('/admin/roles', { method: 'POST', body: JSON.stringify(body) }),
    updateRole: (
      id: string,
      body: { name?: string; description?: string | null; ist_administrationsrolle?: boolean },
    ): Promise<RoleDto> =>
      apiFetch<RoleDto>(`/admin/roles/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    setRolePermissions: (id: string, permissionKeys: string[]): Promise<RoleDto> =>
      apiFetch<RoleDto>(`/admin/roles/${id}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permission_keys: permissionKeys }),
      }),
    deleteRole: (id: string): Promise<void> =>
      apiFetch<void>(`/admin/roles/${id}`, { method: 'DELETE' }),

    // ── Sichtbarkeit (V10-E deny-set inside the role wizard) ──────────────
    visibilityCatalog: (): Promise<{ catalog: VisibilityKeyDefinition[] }> =>
      apiFetch<{ catalog: VisibilityKeyDefinition[] }>('/admin/visibility/catalog'),
    roleVisibilityDenies: (roleId: string): Promise<RoleVisibilityDeniesResponse> =>
      apiFetch<RoleVisibilityDeniesResponse>(`/admin/roles/${roleId}/visibility-denies`),
    setRoleVisibilityDenies: (
      roleId: string,
      deniedKeys: string[],
    ): Promise<RoleVisibilityDeniesResponse> =>
      apiFetch<RoleVisibilityDeniesResponse>(`/admin/roles/${roleId}/visibility-denies`, {
        method: 'PUT',
        body: JSON.stringify({ denied_keys: deniedKeys }),
      }),

    // ── Feldrechte (WS1 §5.2 — the finer per-field visible/editable layer) ────
    fieldCatalog: (): Promise<{ catalog: FieldPermissionKeyDefinition[] }> =>
      apiFetch<{ catalog: FieldPermissionKeyDefinition[] }>('/admin/fields/catalog'),
    roleFieldPermissions: (roleId: string): Promise<RoleFieldPermissionsResponse> =>
      apiFetch<RoleFieldPermissionsResponse>(`/admin/roles/${roleId}/field-permissions`),
    setRoleFieldPermissions: (
      roleId: string,
      fieldPermissions: FieldPermissionDto[],
    ): Promise<RoleFieldPermissionsResponse> =>
      apiFetch<RoleFieldPermissionsResponse>(`/admin/roles/${roleId}/field-permissions`, {
        method: 'PUT',
        body: JSON.stringify({ field_permissions: fieldPermissions }),
      }),
    effectiveFieldPermissions: (): Promise<EffectiveFieldPermissionsResponse> =>
      apiFetch<EffectiveFieldPermissionsResponse>('/me/effective-field-permissions'),

    // ── User-role assignment (12.6) ──────────────────────────────────────
    userRoles: (userId: string): Promise<UserRolesDto> =>
      apiFetch<UserRolesDto>(`/admin/users/${userId}/roles`),
    assignRole: (userId: string, roleId: string): Promise<{ assigned: boolean }> =>
      apiFetch<{ assigned: boolean }>(`/admin/users/${userId}/roles`, {
        method: 'POST',
        body: JSON.stringify({ role_id: roleId }),
      }),
    unassignRole: (userId: string, roleId: string): Promise<void> =>
      apiFetch<void>(`/admin/users/${userId}/roles/${roleId}`, { method: 'DELETE' }),

    // ── Teams / Funktionsgruppen (V10-F) ─────────────────────────────────
    funktionsgruppen: (): Promise<FunktionsgruppenListResponse> =>
      apiFetch<FunktionsgruppenListResponse>('/admin/funktionsgruppen'),
    funktionsgruppe: (id: string): Promise<FunktionsgruppeDetailDto> =>
      apiFetch<FunktionsgruppeDetailDto>(`/admin/funktionsgruppen/${id}`),
    createFunktionsgruppe: (body: {
      name: string;
      description?: string | null;
    }): Promise<FunktionsgruppeDto> =>
      apiFetch<FunktionsgruppeDto>('/admin/funktionsgruppen', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    updateFunktionsgruppe: (
      id: string,
      body: { name?: string; description?: string | null },
    ): Promise<FunktionsgruppeDto> =>
      apiFetch<FunktionsgruppeDto>(`/admin/funktionsgruppen/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    deleteFunktionsgruppe: (id: string): Promise<void> =>
      apiFetch<void>(`/admin/funktionsgruppen/${id}`, { method: 'DELETE' }),
    /**
     * WS4 (Bereich 4 / UC8): `window` is an OPTIONAL befristete Stellvertretung
     * (ISO 8601 strings) — omitted entirely = permanent membership (unchanged).
     */
    addFunktionsgruppeMember: (
      id: string,
      userId: string,
      window?: { gueltig_von?: string; gueltig_bis?: string },
    ): Promise<{ added: boolean }> =>
      apiFetch<{ added: boolean }>(`/admin/funktionsgruppen/${id}/members`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, ...window }),
      }),
    /** WS4 — edit an EXISTING member's Stellvertretung window in place (either bound may be null to clear). */
    updateFunktionsgruppeMemberWindow: (
      id: string,
      userId: string,
      window: { gueltig_von?: string | null; gueltig_bis?: string | null },
    ): Promise<{ gueltig_von: string | null; gueltig_bis: string | null }> =>
      apiFetch<{ gueltig_von: string | null; gueltig_bis: string | null }>(
        `/admin/funktionsgruppen/${id}/members/${userId}`,
        { method: 'PATCH', body: JSON.stringify(window) },
      ),
    removeFunktionsgruppeMember: (id: string, userId: string): Promise<{ removed: boolean }> =>
      apiFetch<{ removed: boolean }>(`/admin/funktionsgruppen/${id}/members/${userId}`, {
        method: 'DELETE',
      }),

    // ── Kundenteams / customer-visibility teams (Berechtigungskonzept V3.0 Bereich 2, WS2) ──
    kundenteams: (): Promise<KundenteamListResponse> =>
      apiFetch<KundenteamListResponse>('/admin/teams'),
    kundenteam: (id: string): Promise<KundenteamDetailDto> =>
      apiFetch<KundenteamDetailDto>(`/admin/teams/${id}`),
    searchKundenteamCustomers: (q: string): Promise<{ items: KundenteamCustomerDto[] }> =>
      apiFetch<{ items: KundenteamCustomerDto[] }>(
        `/admin/teams/customers/search?q=${encodeURIComponent(q)}`,
      ),
    createKundenteam: (body: { name: string; description?: string | null }): Promise<KundenteamDto> =>
      apiFetch<KundenteamDto>('/admin/teams', { method: 'POST', body: JSON.stringify(body) }),
    updateKundenteam: (
      id: string,
      body: { name?: string; description?: string | null },
    ): Promise<KundenteamDto> =>
      apiFetch<KundenteamDto>(`/admin/teams/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    deleteKundenteam: (id: string): Promise<void> =>
      apiFetch<void>(`/admin/teams/${id}`, { method: 'DELETE' }),
    addKundenteamMember: (id: string, userId: string): Promise<{ added: boolean }> =>
      apiFetch<{ added: boolean }>(`/admin/teams/${id}/members`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      }),
    removeKundenteamMember: (id: string, userId: string): Promise<{ removed: boolean }> =>
      apiFetch<{ removed: boolean }>(`/admin/teams/${id}/members/${userId}`, { method: 'DELETE' }),
    assignKundenteamCustomer: (
      id: string,
      subjectType: 'person' | 'firma',
      subjectId: string,
    ): Promise<{ assigned: boolean }> =>
      apiFetch<{ assigned: boolean }>(`/admin/teams/${id}/customers`, {
        method: 'POST',
        body: JSON.stringify({ subject_type: subjectType, subject_id: subjectId }),
      }),
    unassignKundenteamCustomer: (
      id: string,
      subjectType: 'person' | 'firma',
      subjectId: string,
    ): Promise<{ unassigned: boolean }> =>
      apiFetch<{ unassigned: boolean }>(
        `/admin/teams/${id}/customers/${subjectType}/${subjectId}`,
        { method: 'DELETE' },
      ),

    // ── Kontrollfunktion / control backlog (Berechtigungskonzept V3.0 Bereich 5, WS5) ──
    // Read-only cross-customer Rückstand list + the assign-to-person/role action. Both gated by
    // administration.kontrolle.rueckstand.einsehen (the api is the hard line).
    controlBacklog: (): Promise<ControlBacklogListResponse> =>
      apiFetch<ControlBacklogListResponse>('/admin/kontrolle/rueckstand'),
    assignControlBacklog: (
      id: string,
      body: AssignControlBacklogItemInput,
    ): Promise<ControlBacklogItemDto> =>
      apiFetch<ControlBacklogItemDto>(`/admin/kontrolle/rueckstand/${id}/assign`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    // ── Kontrollbereich Cockpit (Berechtigungskonzept V4.0 · Bereich 5, Kapitel 8) ──
    // The evolution of the WS5 skeleton: 4 uniform age-stages + a signal engine. All reads
    // gated by administration.kontrolle.rueckstand.einsehen; zuweisen adds the dedicated
    // administration.kontrolle.rueckstand.zuweisen permission (the api is the hard line).
    /** GET /admin/kontrolle/uebersicht — the dashboard-panel aggregate. */
    kontrolleUebersicht: (): Promise<KontrolleUebersichtDto> =>
      apiFetch<KontrolleUebersichtDto>('/admin/kontrolle/uebersicht'),
    /** GET /admin/kontrolle/rueckstand?stufe=N — the per-stage Kontrollliste (+ optional filters). */
    kontrolleRueckstand: (
      stufe: number,
      filters: { kanal?: string; kunde?: string; team?: string } = {},
    ): Promise<KontrolleRueckstandItemDto[]> => {
      const params = new URLSearchParams({ stufe: String(stufe) });
      if (filters.kanal) params.set('kanal', filters.kanal);
      if (filters.kunde) params.set('kunde', filters.kunde);
      if (filters.team) params.set('team', filters.team);
      return apiFetch<KontrolleRueckstandItemDto[]>(`/admin/kontrolle/rueckstand?${params.toString()}`);
    },
    /** GET /admin/kontrolle/rueckstand/:id — one element's detail. */
    kontrolleRueckstandDetail: (id: string): Promise<KontrolleRueckstandDetailDto> =>
      apiFetch<KontrolleRueckstandDetailDto>(`/admin/kontrolle/rueckstand/${id}`),
    /** POST /admin/kontrolle/rueckstand/:id/zuweisen — route to a person/group (+ optional Frist). */
    kontrolleZuweisen: (id: string, body: ZuweisenInput): Promise<KontrolleRueckstandDetailDto> =>
      apiFetch<KontrolleRueckstandDetailDto>(`/admin/kontrolle/rueckstand/${id}/zuweisen`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    /** GET /admin/einstellungen/kontrolle-fristen — the per-broker stage thresholds. */
    kontrolleFristen: (): Promise<KontrolleFristenDto> =>
      apiFetch<KontrolleFristenDto>('/admin/einstellungen/kontrolle-fristen'),
    /** PUT /admin/einstellungen/kontrolle-fristen — set the 4 thresholds (monotonic, admin-only). */
    setKontrolleFristen: (body: SetKontrolleFristenInput): Promise<KontrolleFristenDto> =>
      apiFetch<KontrolleFristenDto>('/admin/einstellungen/kontrolle-fristen', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

    // ── Prozess-Erinnerungen (Berechtigungskonzept V3.0 / Einfuehrungskonzept · P7) ──
    // P7 (per-process rework): all process-type configs + the dynamic template. GET gated by
    // tenant.read; the two PUTs by tenant.manage (the api is the hard line).
    processReminderConfig: (): Promise<ProcessReminderConfigResponseDto> =>
      apiFetch<ProcessReminderConfigResponseDto>('/admin/process-reminders'),
    setProcessReminderType: (
      body: SetProcessReminderTypeConfigInput,
    ): Promise<ProcessReminderTypeConfigDto> =>
      apiFetch<ProcessReminderTypeConfigDto>('/admin/process-reminders/type', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    setProcessReminderTemplate: (
      body: SetProcessReminderTemplateInput,
    ): Promise<ProcessReminderTemplateDto> =>
      apiFetch<ProcessReminderTemplateDto>('/admin/process-reminders/template', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

    // ── Mahnwesen P-04 (P4-S2) ──────────────────────────────────────────────
    // GET gated by tenant.read; the PUTs by tenant.manage (the api is the hard line).
    mahnwesenConfig: (processKey = 'P-04'): Promise<MahnwesenProcessConfigDto> =>
      apiFetch<MahnwesenProcessConfigDto>(`/admin/mahnwesen/${processKey}`),
    setMahnwesenConfig: (
      body: PutMahnwesenProcessConfigBody,
      processKey = 'P-04',
    ): Promise<MahnwesenProcessConfigDto> =>
      apiFetch<MahnwesenProcessConfigDto>(`/admin/mahnwesen/${processKey}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    // The Brokermandat Meldung settings (GET tenant.read, PUT tenant.manage).
    brokerRegistrationSettings: (): Promise<BrokerRegistrationSettingsDto> =>
      apiFetch<BrokerRegistrationSettingsDto>(`/admin/einstellungen/brokermandat-meldung`),
    setBrokerRegistrationSettings: (body: SetBrokerRegistrationSettingsBody): Promise<BrokerRegistrationSettingsDto> =>
      apiFetch<BrokerRegistrationSettingsDto>(`/admin/einstellungen/brokermandat-meldung`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    // P4-S7: the per-tenant Versicherer-Benachrichtigung Fristen (GET and PUT mahnwesen_config.manage).
    versichererFristen: (): Promise<InsurerNotificationFristConfigDto[]> =>
      apiFetch<InsurerNotificationFristConfigDto[]>(`/admin/einstellungen/versicherer-fristen`),
    setVersichererFristen: (
      body: SetInsurerNotificationFristConfigBody,
    ): Promise<InsurerNotificationFristConfigDto> =>
      apiFetch<InsurerNotificationFristConfigDto>(`/admin/einstellungen/versicherer-fristen`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    textTemplates: (processKey = 'P-04'): Promise<TextTemplateDto[]> =>
      apiFetch<TextTemplateDto[]>(`/admin/text-templates?process_key=${processKey}`),
    setTextTemplate: (body: UpsertTextTemplateBody): Promise<TextTemplateDto> =>
      apiFetch<TextTemplateDto>('/admin/text-templates', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

    // ── Regelwerk (P2 Placement Slice 3) ─────────────────────────────────
    // GET gated by regelwerk.read; the create/patch/reset writes by regelwerk.manage (api = hard line).
    regelwerkSollDeckung: (
      filters: { ebene?: string; branchencode?: string; kundentyp?: string; include_inactive?: boolean; sprache?: string } = {},
    ): Promise<RegelwerkListResponse<RegelwerkSollDeckungItem>> => {
      const qs = new URLSearchParams();
      if (filters.ebene) qs.set('ebene', filters.ebene);
      if (filters.branchencode) qs.set('branchencode', filters.branchencode);
      if (filters.kundentyp) qs.set('kundentyp', filters.kundentyp);
      if (filters.include_inactive) qs.set('include_inactive', 'true');
      if (filters.sprache) qs.set('sprache', filters.sprache);
      const q = qs.toString();
      return apiFetch<RegelwerkListResponse<RegelwerkSollDeckungItem>>(`/admin/regelwerk/soll-deckung${q ? `?${q}` : ''}`);
    },
    createRegelwerkSollRule: (body: SollDeckungRuleCreateBody): Promise<RegelwerkSollDeckungItem> =>
      apiFetch<RegelwerkSollDeckungItem>('/admin/regelwerk/soll-deckung', { method: 'POST', body: JSON.stringify(body) }),
    patchRegelwerkSollRule: (id: string, body: SollDeckungRulePatchBody): Promise<RegelwerkSollDeckungItem> =>
      apiFetch<RegelwerkSollDeckungItem>(`/admin/regelwerk/soll-deckung/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    resetRegelwerkSollRule: (id: string): Promise<RegelwerkSollDeckungItem> =>
      apiFetch<RegelwerkSollDeckungItem>(`/admin/regelwerk/soll-deckung/${id}/reset`, { method: 'POST' }),
    regelwerkAttributSchema: (
      filters: { ebene?: string; branchencode?: string; profil_typ?: string; include_inactive?: boolean; sprache?: string } = {},
    ): Promise<RegelwerkListResponse<RegelwerkAttributSchemaItem>> => {
      const qs = new URLSearchParams();
      if (filters.ebene) qs.set('ebene', filters.ebene);
      if (filters.branchencode) qs.set('branchencode', filters.branchencode);
      if (filters.profil_typ) qs.set('profil_typ', filters.profil_typ);
      if (filters.include_inactive) qs.set('include_inactive', 'true');
      if (filters.sprache) qs.set('sprache', filters.sprache);
      const q = qs.toString();
      return apiFetch<RegelwerkListResponse<RegelwerkAttributSchemaItem>>(`/admin/regelwerk/attribut-schema${q ? `?${q}` : ''}`);
    },
    patchRegelwerkSchemaField: (id: string, body: AttributSchemaPatchBody): Promise<RegelwerkAttributSchemaItem> =>
      apiFetch<RegelwerkAttributSchemaItem>(`/admin/regelwerk/attribut-schema/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    resetRegelwerkSchemaField: (id: string): Promise<RegelwerkAttributSchemaItem> =>
      apiFetch<RegelwerkAttributSchemaItem>(`/admin/regelwerk/attribut-schema/${id}/reset`, { method: 'POST' }),

    // ── Ausschlussliste / customer-suppression (Fix 6, decision B) ────────
    suppression: (): Promise<SuppressionEntryDto[]> =>
      apiFetch<SuppressionEntryDto[]>('/admin/suppression'),
    createSuppression: (body: {
      domain?: string;
      uid?: string;
      company_name?: string;
      firma_id?: string;
      reason?: string;
      status?: 'active' | 'inactive';
    }): Promise<SuppressionEntryDto> =>
      apiFetch<SuppressionEntryDto>('/admin/suppression', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    updateSuppression: (
      id: string,
      body: {
        domain?: string | null;
        uid?: string | null;
        company_name?: string | null;
        firma_id?: string | null;
        reason?: string | null;
        status?: 'active' | 'inactive';
      },
    ): Promise<SuppressionEntryDto> =>
      apiFetch<SuppressionEntryDto>(`/admin/suppression/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    deleteSuppression: (id: string): Promise<void> =>
      apiFetch<void>(`/admin/suppression/${id}`, { method: 'DELETE' }),

    // ── Rechnungskontrolle (Epic 10.5) ───────────────────────────────────
    toleranceConfig: (): Promise<ToleranceConfigEntryDto[]> =>
      apiFetch<ToleranceConfigEntryDto[]>('/admin/tolerance-config'),
    setToleranceConfig: (
      sparte: string,
      body: SetToleranceConfigBody,
    ): Promise<ToleranceConfigEntryDto> =>
      apiFetch<ToleranceConfigEntryDto>(`/admin/tolerance-config/${sparte}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    toleranceTestmodus: (body: TestmodusBody): Promise<TestmodusResult> =>
      apiFetch<TestmodusResult>('/admin/tolerance-config/testmodus', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    // ── Workflows: Kanäle + Matrix + Routing (V10-A / 3.4 / V10-F) ────────
    channelConfig: (): Promise<ChannelConfigDto[]> =>
      apiFetch<ChannelConfigDto[]>('/admin/channel-config'),
    setChannelConfig: (
      channelKey: string,
      body: { active: boolean; priority?: number | null; fallbackChannel?: ChannelKey | null },
    ): Promise<ChannelConfigDto> =>
      apiFetch<ChannelConfigDto>(`/admin/channel-config/channels/${channelKey}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    channelMatrix: (): Promise<ChannelMatrixEntryDto[]> =>
      apiFetch<ChannelMatrixEntryDto[]>('/admin/channel-config/matrix'),
    createChannelMatrix: (body: {
      docType: DeliveryDocType;
      product?: string | null;
      channelKey: ChannelKey;
    }): Promise<ChannelMatrixEntryDto> =>
      apiFetch<ChannelMatrixEntryDto>('/admin/channel-config/matrix', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    deleteChannelMatrix: (id: string): Promise<void> =>
      apiFetch<void>(`/admin/channel-config/matrix/${id}`, { method: 'DELETE' }),
    confidencePolicy: (): Promise<ConfidencePolicyEntryDto[]> =>
      apiFetch<ConfidencePolicyEntryDto[]>('/admin/confidence-policy'),
    setConfidencePolicy: (
      process: string,
      body: { auto_threshold: number; freigabe_threshold: number },
    ): Promise<ConfidencePolicyEntryDto> =>
      apiFetch<ConfidencePolicyEntryDto>(`/admin/confidence-policy/${process}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    groupRouting: (): Promise<GroupRoutingEntryDto[]> =>
      apiFetch<GroupRoutingEntryDto[]>('/admin/group-routing'),
    setGroupRouting: (
      process: string,
      funktionsgruppeId: string | null,
    ): Promise<GroupRoutingEntryDto> =>
      apiFetch<GroupRoutingEntryDto>(`/admin/group-routing/${process}`, {
        method: 'PUT',
        body: JSON.stringify({ funktionsgruppe_id: funktionsgruppeId }),
      }),

    // ── Mandant & Branding (3.1 / Kap. 20 / V10-D / V10-S) ────────────────
    tenant: (): Promise<TenantConfigDto> => apiFetch<TenantConfigDto>('/admin/tenant'),
    setTenantDomains: (ownDomains: string[]): Promise<TenantConfigDto> =>
      apiFetch<TenantConfigDto>('/admin/tenant', {
        method: 'PATCH',
        body: JSON.stringify({ own_domains: ownDomains }),
      }),
    /**
     * PATCH /admin/tenant/mail-default-team (MV): the team that sees mail which
     * could not be matched to a client. `teamId` null lifts the restriction.
     * Guarded by `visibility.manage`; returns the FULL tenant config.
     */
    setMailDefaultTeam: (teamId: string | null): Promise<TenantConfigDto> =>
      apiFetch<TenantConfigDto>('/admin/tenant/mail-default-team', {
        method: 'PATCH',
        body: JSON.stringify({ team_id: teamId }),
      }),
    // Berechtigungskonzept V3.0 · Bereich 1 — the per-Mandant cap on user sync levels.
    emailSyncCap: (): Promise<EmailSyncCapResponse> =>
      apiFetch<EmailSyncCapResponse>('/admin/email-sync-cap'),
    setEmailSyncCap: (maxLevel: EmailSyncLevel): Promise<EmailSyncCapResponse> =>
      apiFetch<EmailSyncCapResponse>('/admin/email-sync-cap', {
        method: 'PUT',
        body: JSON.stringify({ max_email_sync_level: maxLevel }),
      }),
    portalConfig: (): Promise<PortalConfigDto> =>
      apiFetch<PortalConfigDto>('/admin/customer-portal'),
    setPortalConfig: (body: SetPortalConfigBody): Promise<PortalConfigDto> =>
      apiFetch<PortalConfigDto>('/admin/customer-portal', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    // Follow-up ①: the broker's OWN live branding self-edit — merges logoRef + the 3 W4
    // colours into portal_config.branding with IMMEDIATE effect (email + portal read it
    // live). Distinct from the platform-admin DRAFT branding PATCH.
    setBrokerBranding: (patch: Partial<BrokerBrandingDto>): Promise<PortalConfigDto> =>
      apiFetch<PortalConfigDto>('/admin/customer-portal/branding', {
        method: 'PUT',
        body: JSON.stringify(patch),
      }),
    /**
     * POST /admin/customer-portal/branding/logo — the broker uploads their OWN logo
     * (multipart; the browser sets the boundary). Returns the stored logoRef, which the
     * caller then persists via setBrokerBranding({ logoRef }).
     */
    uploadBrokerLogo: async (
      file: File | Blob,
      filename?: string,
    ): Promise<{ logoRef: string }> => {
      const form = new FormData();
      form.append('file', file, filename ?? 'logo.png');
      // NOTE: no Content-Type header — the browser sets the multipart boundary.
      const res = await authedFetch(`${API_BASE_URL}/admin/customer-portal/branding/logo`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `HTTP ${res.status}`);
      }
      return res.json() as Promise<{ logoRef: string }>;
    },
    portalGrants: (subjectType: string, subjectId: string): Promise<PortalGrantAdminItem[]> =>
      apiFetch<PortalGrantAdminItem[]>(`/admin/customer-portal/grants/${subjectType}/${subjectId}`),
    /** GET the customer's primary portal holder row (null when never touched by the portal). */
    portalAccess: (subjectType: string, subjectId: string): Promise<PortalAccessResponse> =>
      apiFetch<PortalAccessResponse>(`/admin/customer-portal/access/${subjectType}/${subjectId}`),
    revokePortalGrant: (grantId: string): Promise<unknown> =>
      apiFetch<unknown>(`/admin/customer-portal/grants/${grantId}/revoke`, { method: 'POST' }),
    /** PUT the ONE invitation address. A pure store: the server sends nothing. */
    setPortalInviteEmail: (
      subjectType: string,
      subjectId: string,
      email: string,
    ): Promise<unknown> =>
      apiFetch<unknown>(`/admin/customer-portal/invite-email/${subjectType}/${subjectId}`, {
        method: 'PUT',
        body: JSON.stringify({ email }),
      }),
    /**
     * POST the 1-click portal invite. Bereich 6: in `gruppenbasiert` mode the
     * customer must carry >=1 Portal-Berechtigungsgruppe for the new portal user
     * (no groupless activation) and the broker must pick at least one; the selected
     * ids ride along as `groupIds` (the ReinviteBodyDto field name; `gruppe_ids` would
     * be rejected by forbidNonWhitelisted). In `einfach` mode the array is omitted.
     */
    sendPortalInvite: (
      subjectType: string,
      subjectId: string,
      groupIds?: string[],
    ): Promise<unknown> =>
      apiFetch<unknown>(`/admin/customer-portal/invite/${subjectType}/${subjectId}`, {
        method: 'POST',
        ...(groupIds && groupIds.length > 0
          ? { body: JSON.stringify({ groupIds }) }
          : {}),
      }),
    avbList: (
      params: { carrier_uid?: string; q?: string; limit?: number } = {},
    ): Promise<AvbDocumentResponse[]> => {
      const qs = new URLSearchParams();
      if (params.carrier_uid) qs.set('carrier_uid', params.carrier_uid);
      if (params.q) qs.set('q', params.q);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<AvbDocumentResponse[]>(`/avb${query}`);
    },
    /** POST /avb — register one AVB version (multipart; the browser sets the boundary). */
    avbRegister: async (
      fields: AvbRegisterFields,
      file: File | Blob,
      filename?: string,
    ): Promise<AvbDocumentResponse> => {
      const form = new FormData();
      form.append('file', file, filename ?? 'avb.pdf');
      for (const [k, v] of Object.entries(fields)) {
        if (v !== undefined && v !== null) form.append(k, String(v));
      }
      // NOTE: no Content-Type header — the browser sets the multipart boundary.
      const res = await authedFetch(`${API_BASE_URL}/avb`, { method: 'POST', body: form });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `HTTP ${res.status}`);
      }
      return res.json() as Promise<AvbDocumentResponse>;
    },

    // ── Audit-Log (1.4) ──────────────────────────────────────────────────
    audit: (
      params: {
        entity_type?: string;
        entity_id?: string;
        action?: string;
        actor_kind?: string;
        user_id?: string;
        /** 'normal' or 'high'; validated server-side against AUDIT_SEVERITIES. */
        severity?: string;
        from?: string;
        to?: string;
        cursor?: string;
        limit?: number;
      } = {},
    ): Promise<AuditListResponse> => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
      }
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return apiFetch<AuditListResponse>(`/audit${query}`);
    },
  },

  /**
   * Delegated mailbox (Story MC6) — the broker's OWN connected mailbox(es).
   * Owned by the sibling mailbox-connect API stories (mc1 foundation + the
   * OAuth-callback/connect controller); consumed here + feature-detected (a
   * 404 / error ⇒ the `'absent'` availability, AC8). NO `send` method — that is
   * the sibling send story's surface; MC6 is connect/list/disconnect only.
   */
  mailbox: {
    /** GET /mailbox/connections — the single feature-detect source (connect_enabled + rows). */
    connections: (): Promise<MailboxConnectionsResponse> =>
      apiFetch<MailboxConnectionsResponse>('/mailbox/connections'),
    /** POST /mailbox/connect/:provider — returns the provider authorize URL (api owns state/PKCE). */
    connect: (provider: MailboxProvider): Promise<MailboxConnectResponse> =>
      apiFetch<MailboxConnectResponse>(`/mailbox/connect/${provider}`, { method: 'POST' }),
    /** DELETE /mailbox/connections/:id — revoke + remove one connection (204). */
    disconnect: (id: string): Promise<void> =>
      apiFetch<void>(`/mailbox/connections/${id}`, { method: 'DELETE' }),
    /**
     * POST /mailbox/connections/:id/sync (P2 hardening 06) — run ONE bounded delta round
     * NOW for the caller's own mailbox. 200 ManualSyncResult; 404 not owned; 409
     * sync_in_progress; 429 sync_cooldown; 403 connect disabled (all surfaced via ApiError).
     */
    syncNow: (id: string): Promise<ManualSyncResult> =>
      apiFetch<ManualSyncResult>(`/mailbox/connections/${id}/sync`, { method: 'POST' }),
    /**
     * POST /mailbox/send (Story MC5) — send a reply/new email AS the caller's own
     * connected mailbox (201 MailboxSendResult). `in_reply_to_communication_id`
     * threads the reply onto its parent; tenant + actor are resolved server-side
     * from the JWT (rule #1) — never on the wire.
     */
    send: (body: MailboxSendMessageBody): Promise<MailboxSendResult> =>
      apiFetch<MailboxSendResult>('/mailbox/send', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  /**
   * feat/manual-esign-loop — the manual step-by-step e-signature loop (broker side).
   * "Dokument zur Unterschrift senden": opens a sign-required Vorgang + a needs_signature
   * portal_delivery so the document lands in the VN's portal with a signature call-to-action.
   */
  signRequests: {
    /**
     * POST /mandates/sign-requests — send a document to the VN for signing (multipart).
     * Provide EXACTLY ONE source: an existing `documentId` OR an uploaded `file`.
     */
    create: async (input: {
      subjectType: 'person' | 'firma';
      subjectId: string;
      documentId?: string;
      mandateId?: string;
      file?: File | Blob;
      filename?: string;
    }): Promise<SendToSignResult> => {
      const form = new FormData();
      form.append('subjectType', input.subjectType);
      form.append('subjectId', input.subjectId);
      if (input.documentId) form.append('documentId', input.documentId);
      if (input.mandateId) form.append('mandateId', input.mandateId);
      if (input.file) form.append('file', input.file, input.filename ?? 'dokument.pdf');
      // NOTE: no Content-Type header — the browser sets the multipart boundary.
      const res = await authedFetch(`${API_BASE_URL}/mandates/sign-requests`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `HTTP ${res.status}`);
      }
      return res.json() as Promise<SendToSignResult>;
    },
  },

  /**
   * DMS (M11) — the Ablage grid + the reusable inline-PDF-viewer's data source.
   * All routes require `document.read`/`document.download`/`document.set_state`/
   * `document.delete`/`document.upload` server-side (dms/spec.md §6) — this client
   * layer does NOT re-derive permission rules (coding-standards #2); callers gate
   * the UI defensively via `useEffectivePermissions`.
   *
   * [Source: apps/api/src/modules/documents/documents-read.controller.ts]
   * [Source: apps/api/src/modules/documents/documents-actions.controller.ts]
   */
  documents: {
    /**
     * GET /documents — the Ablage grid list + filter + fulltext-search endpoint.
     * Always requested in PAGINATED mode (any of cursor/limit/include_total present)
     * so the response is the `{ items, page }` envelope — the grid needs
     * `page.total_count` for the "N von M Einträgen" toolbar count.
     */
    list: (params: DocumentListQuery = {}): Promise<DocumentListResponse> => {
      const qs = new URLSearchParams();
      if (params.q) qs.set('q', params.q);
      if (params.state) qs.set('state', params.state);
      if (params.category) qs.set('category', params.category);
      if (params.source) qs.set('source', params.source);
      if (params.client_id) qs.set('client_id', params.client_id);
      if (params.include_inactive) qs.set('include_inactive', 'true');
      // DMS Part 2 (P2-12) — the admin-only "Duplikate" view opt-in. Server-gated
      // (admin-only, silent no-op otherwise, dms/spec-part2 §2.4) — sending it is safe
      // for any caller; ONLY useDuplicateRows (the admin Duplikate view) sets it.
      if (params.include_duplicates) qs.set('include_duplicates', 'true');
      qs.set('include_total', 'true');
      if (params.cursor) qs.set('cursor', params.cursor);
      qs.set('limit', String(params.limit ?? 500));
      return apiFetch<DocumentListResponse>(`/documents?${qs.toString()}`);
    },

    /** GET /documents/:id — the detail/inline-viewer page data (incl. connected_objects). */
    get: (id: string): Promise<DocumentDetailDto> => apiFetch<DocumentDetailDto>(`/documents/${id}`),

    /** GET /documents/:id/versions — the "See history" version chain. */
    versions: (id: string): Promise<DocumentVersionDto[]> =>
      apiFetch<DocumentVersionDto[]>(`/documents/${id}/versions`),

    /**
     * GET /documents/duplicates — the admin-only "Duplikate" audit list (P2-12): every
     * detected duplicate for the tenant (original link + method + score + detected_at).
     * Admin-gated server-side (`@Roles('admin')`, 403 for non-admins) — only called from
     * the admin Duplikate view, itself gated by `useEffectivePermissions().isWildcard`.
     */
    duplicates: (): Promise<DuplicateInfoDto[]> => apiFetch<DuplicateInfoDto[]>('/documents/duplicates'),

    /**
     * GET /documents/:id/content — the inline PDF-viewer bytes. Returns a Blob the
     * caller wraps in an object URL (same "blob detour" as `mandateWizard.documentBlob`:
     * the route is auth-gated, so a plain <embed src> pointed at the API URL would never
     * send the bearer token). `document.read`-gated — the VIEWER surface, not a genuine
     * download (see `.download` below for that).
     */
    content: (id: string): Promise<Blob> => apiFetchBlob(`/documents/${id}/content`),

    /**
     * GET /documents/:id/download — the genuine single-document download (DL-2 fix,
     * wave dms-fixC). `document.download`-gated (NOT `document.read`) — same bytes as
     * `.content`, but the server sets `Content-Disposition: attachment`. Every
     * single-row "Herunterladen" action MUST call this, not `.content`, so
     * `document.download` is actually enforceable (a `document.read`-only caller can
     * still VIEW inline via `.content` but gets a 403 here).
     */
    download: (id: string): Promise<Blob> => apiFetchBlob(`/documents/${id}/download`),

    /**
     * POST /documents/upload — generic basic-user upload (`document.upload`), multipart.
     * NOTE: no Content-Type header — the browser sets the multipart boundary.
     */
    upload: async (
      file: File | Blob,
      opts: {
        filename?: string;
        docKind?: DocExtractionKind;
        linkedType?: string;
        linkedId?: string;
        title?: string;
      } = {},
    ): Promise<DocumentUploadResult> => {
      const form = new FormData();
      form.append('file', file, opts.filename ?? 'dokument.pdf');
      if (opts.docKind) form.append('doc_kind', opts.docKind);
      if (opts.linkedType) form.append('linked_type', opts.linkedType);
      if (opts.linkedId) form.append('linked_id', opts.linkedId);
      if (opts.title) form.append('title', opts.title);
      const res = await authedFetch(`${API_BASE_URL}/documents/upload`, { method: 'POST', body: form });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `HTTP ${res.status}`);
      }
      return res.json() as Promise<DocumentUploadResult>;
    },

    /** PATCH /documents/:id/state — toggle active <-> inactive (`document.set_state`). */
    setState: (id: string, state: 'active' | 'inactive'): Promise<DocumentStateResult> =>
      apiFetch<DocumentStateResult>(`/documents/${id}/state`, {
        method: 'PATCH',
        body: JSON.stringify({ state }),
      }),

    /**
     * PATCH /documents/:id — manual reclassify (`document.update`): change the document's
     * `kind` and/or its primary linked object (assign to a Kunde/Police). Send only the
     * changed keys; an explicit `null` clears the field.
     */
    update: (id: string, patch: DocumentUpdateBody): Promise<DocumentUpdateResult> =>
      apiFetch<DocumentUpdateResult>(`/documents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    /**
     * DELETE /documents/:id — hard-delete an INACTIVE document (admin-only,
     * `document.delete`). 409 `document_active` when the document is still active.
     */
    delete: (id: string): Promise<{ deleted: true }> =>
      apiFetch<{ deleted: true }>(`/documents/${id}`, { method: 'DELETE' }),

    /**
     * POST /documents/batch/download — a ZIP of the requested documents' bytes
     * (`document.download`). Used for MULTI-select; a single-row download goes
     * through `.download()` + `saveBlob` instead (no zip-of-one).
     */
    batchDownload: (documentIds: string[]): Promise<Blob> =>
      apiFetchBlob('/documents/batch/download', {
        method: 'POST',
        body: JSON.stringify({ document_ids: documentIds }),
      }),

    /** POST /documents/batch/delete — admin-only; skips ids still active. */
    batchDelete: (documentIds: string[]): Promise<DocumentBatchDeleteResult> =>
      apiFetch<DocumentBatchDeleteResult>('/documents/batch/delete', {
        method: 'POST',
        body: JSON.stringify({ document_ids: documentIds }),
      }),

    /** POST /documents/batch/state — basic; toggles active <-> inactive for a set of ids. */
    batchSetState: (
      documentIds: string[],
      state: 'active' | 'inactive',
    ): Promise<DocumentBatchStateResult> =>
      apiFetch<DocumentBatchStateResult>('/documents/batch/state', {
        method: 'POST',
        body: JSON.stringify({ document_ids: documentIds, state }),
      }),
  },
};

/** feat/manual-esign-loop: 201 response of POST /mandates/sign-requests. */
export interface SendToSignResult {
  vorgangId: string;
  deliveryId: string;
  documentId: string;
  barcodeToken: string;
  status: 'sent';
}

/**
 * POST /agent/chat — returns the raw streaming Response (SSE body).
 * Cannot use EventSource (POST) so we use fetch + ReadableStream directly.
 */
export function agentChatStream(
  message: string,
  signal?: AbortSignal,
  attachmentId?: string,
  attachmentName?: string,
): Promise<Response> {
  return authedFetch(`${API_BASE_URL}/agent/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({
      message,
      ...(attachmentId ? { attachmentId } : {}),
      // Display filename only (persisted so the transcript chip survives a reload).
      ...(attachmentId && attachmentName ? { attachmentName } : {}),
    }),
    signal,
  });
}

/**
 * POST /agent/attachment — multipart document upload → { attachmentId } (Feature B).
 *
 * Extract-to-text MVP: the browser uploads a PDF/image; the server stores the bytes
 * and returns an opaque id. The id is then passed on the next /agent/chat turn via
 * `attachmentId`; the server extracts the text layer and injects it as context.
 * NOTE: do NOT set Content-Type — the browser must set the multipart boundary.
 */
export async function agentUploadAttachment(
  file: Blob,
  opts?: { filename?: string; signal?: AbortSignal },
): Promise<AgentAttachmentResponseDto> {
  const form = new FormData();
  form.append('file', file, opts?.filename ?? 'attachment');
  const res = await authedFetch(`${API_BASE_URL}/agent/attachment`, {
    method: 'POST',
    body: form,
    signal: opts?.signal,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<AgentAttachmentResponseDto>;
}

/**
 * POST /agent/transcribe — multipart audio → { text } (Story P3b → P3a endpoint).
 * INPUT ONLY: caller puts text into the composer; nothing is auto-sent.
 * NOTE: do NOT set Content-Type — the browser must set the multipart boundary.
 *
 * [Source: Story P3b spec §3]
 */
export async function agentTranscribe(
  audio: Blob,
  opts?: { filename?: string; language?: string; signal?: AbortSignal },
): Promise<AgentTranscribeResponseDto> {
  const form = new FormData();
  form.append('file', audio, opts?.filename ?? 'recording.webm');
  if (opts?.language) form.append('language', opts.language);
  // NOTE: no Content-Type header — the browser sets the multipart boundary;
  // authedFetch only adds Authorization.
  const res = await authedFetch(`${API_BASE_URL}/agent/transcribe`, {
    method: 'POST',
    body: form,
    signal: opts?.signal,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<AgentTranscribeResponseDto>;
}

/**
 * POST /agent/confirm — structured confirm for the button path (P2b).
 *
 * Deterministic path: button → structured endpoint → resolvePendingDecision (server).
 * Returns a streaming SSE Response identical in shape to /agent/chat.
 *
 * [Source: Story P2b spec §3.1]
 */
export function agentConfirmStream(
  toolCallId: string,
  decision: import('@brokly/shared').AgentDecision,
  note?: string,
  signal?: AbortSignal,
): Promise<Response> {
  return authedFetch(`${API_BASE_URL}/agent/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ toolCallId, decision, ...(note !== undefined ? { note } : {}) }),
    signal,
  });
}
